/**
 * L'écran des ordres de transport — la liste, la fiche, la création.
 *
 * Conforme à la maquette validée `docs/maquettes/ot.html`, aux écarts près qui
 * sont écrits plus bas et qui tiennent tous à la même cause : la maquette
 * montre des choses que la base ne porte pas encore.
 *
 * Même découpe que les autres écrans, et pour la même raison :
 *
 *   - `VueOrdres` ne fait que DESSINER. On lui donne un état, elle rend du
 *     HTML — ce qui permet de la rendre et de la REGARDER hors navigateur,
 *     avec n'importe quel état : liste vide, fiche en panne, étape en litige ;
 *   - `EcranOrdres` tient l'état, appelle l'API, et lui passe le résultat.
 *
 * Le calcul est dans `ot.ts`, en fonctions pures : mise en forme, totaux,
 * filtrage, contrôle de saisie, composition des instants.
 *
 * TROIS ÉCARTS AVEC LA MAQUETTE, TOUS ASSUMÉS ET SIGNALÉS À L'UTILISATEUR :
 *
 *   - **l'onglet Documents A OUVERT le 04/09/2026** (migration
 *     `20260904190000`). Le fichier ne passe pas par l'API : le navigateur
 *     l'envoie au rangement Supabase avec sa propre session, et l'API n'écrit
 *     que la ligne. La ligne D'ABORD, le fichier ensuite — un fichier sans
 *     ligne serait invisible et payant ;
 *   - **« Ressource affectée » n'est pas sur les étapes.** Elle viendra du
 *     planning, qui n'existe pas : aucune colonne ne relie aujourd'hui une
 *     étape à une affectation. L'afficher demanderait d'inventer le lien ;
 *   - **« Dupliquer » n'est pas là.** Il n'existe aucune route qui duplique un
 *     ordre, et l'imiter par cinq appels laisserait un ordre à moitié copié si
 *     le troisième échouait.
 *
 * Un quatrième écart, dans l'autre sens : **la fiche porte « Annuler » et
 * « Rouvrir »**, que la maquette ne montre pas. Ce sont les deux seules
 * décisions humaines qui restent sur le statut — les quatre autres valeurs se
 * déduisent des étapes depuis le 04/09/2026. Le sélecteur de statut qui vivait
 * dans le formulaire de modification a été retiré le même jour : il posait une
 * valeur que la première étape validée réécrivait sans prévenir.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ANNUAIRE_TRANSPORTEURS } from './transporteurs.js';
import { EcranConfirmation } from './EcranConfirmation.js';
import { EcranOrdreAffretement } from './EcranOrdreAffretement.js';
import { OngletExecution } from './OngletExecution.js';
import { FormulaireFiche } from './FormulaireFiche.js';
import { ChoixCherchable } from './ChoixCherchable.js';
import {
  codePropose,
  pourCreation,
  saisieVide,
  validerFiche,
  type Annuaire,
  type Saisie,
} from './annuaire.js';
import {
  enQuatreDecimales,
  manquesDIndexationDits,
  manquesDits,
  moisDit,
  margeReelle,
  margeReelleDite,
  minutesDites,
  minutesSaisies,
} from './finance.js';
import {
  AFFRETEMENT_VIDE,
  CHOIX_NOUVEAU,
  corpsAffretement,
  grouperTransporteurs,
  marge,
  margeDite,
  type SaisieAffretement,
} from './affretement.js';
import {
  ajouterEtape,
  annulerOrdre,
  creerFiche,
  envoyerEnFacturation,
  retirerDeLaFacturation,
  lireListe,
  lireLeCout,
  apercuIndexation,
  remplacerLesMarchandises,
  type IndexationLue,
  ouvrirUnLitige,
  poserLExecution,
  recalculerLeCout,
  resoudreUnLitige,
  lireListeReglee,
  lireLesPrestations,
  ajouterUnePrestation,
  modifierUnePrestation,
  retirerUnePrestation,
  lireMotifsLitige,
  lireOrdre,
  affreter,
  annulerLAffretement,
  chercherTransporteurs,
  poserLEchange,
  type TransporteurTrouve,
  lireOrdres,
  lireParc,
  lireVehicules,
  creerMotifLitige,
  modifierEtape,
  modifierOrdre,
  creerOrdre,
  reordonnerEtapes,
  rouvrirOrdre,
  supprimerEtape,
  supprimerOrdre,
  lireFiche,
  type EchecApi,
  type CoutOrdre,
  type LigneListe,
  type Resultat,
  type Vehicule,
  type Document as DocumentJoint,
  type LigneReglee,
  type PrestationAnnexe,
  type PrestationsDeLOrdre,
  confirmerLeDocument,
  declarerLeDocument,
  lireDocuments,
  supprimerLeDocument,
  lireConfirmation,
} from './api.js';
import {
  adresseDuFichier,
  effacerLeFichier,
  envoyerLeFichier,
  poidsDit,
  POIDS_MAXIMUM,
} from './rangement.js';
import { ANNUAIRE_SITES } from './sites.js';
import { PARC_CHAUFFEURS, PARC_REMORQUES, PARC_VEHICULES } from './parcs.js';
import { texte as texteParc, type LigneParc } from './parc.js';
import { ANNUAIRE_CLIENTS } from './clients.js';
import { valeur } from './annuaire.js';
import { PAYS } from './pays.js';
import { mlLisible } from '@stafa/core';
import {
  adresseDite,
  lienItineraire,
  aujourdhui,
  classeStatut,
  corpsEtape,
  creneau,
  ceQueLaListeCache,
  chargementDit,
  chercherPlusLoinSert,
  corpsEchange,
  ecartEchange,
  echangeDepuis,
  decompte,
  differenceOrdre,
  enEuros,
  enFrancais,
  etapeDepuis,
  etapeVide,
  appliquerFiltres,
  AUCUN_FILTRE,
  basculer,
  choixDeduits,
  cochesDe,
  compteFiltres,
  CRITERES_FILTRE,
  etiquettesDeFiltre,
  filtrer,
  oterFiltre,
  trier,
  triSuivant,
  TRI_DEFAUT,
  instantDepuis,
  instantDit,
  jourCourt,
  lisible,
  litigeDit,
  ordreDepuis,
  ordreDuplique,
  etapesDupliquees,
  NATURES_MARCHANDISE,
  ORDRE_VIDE,
  pourCreationOrdre,
  basculerCarrosserie,
  CARROSSERIES,
  carrosserieChoisie,
  codesAcceptes,
  pourCreationSite,
  maintenant,
  ranger,
  rapport,
  refusEchange,
  refusLitige,
  ressourceDite,
  refusValidation,
  reporterSurLesLivraisons,
  SITE_VIDE,
  affreteDit,
  estAffrete,
  statutDit,
  metreLineaireDit,
  supportsAvec,
  totalHtDeLaSaisie,
  type PrestationNeuve,
  type LigneMarchandise,
  AUTRE_DIMENSION,
  choisirLeSupport,
  corpsMarchandises,
  ligneMarchandiseVide,
  marchandisesDepuisEtapes,
  mlUnitaireDeLaLigne,
  totauxDesMarchandises,
  validerLigneMarchandise,
  lignesDepuisLues,
  totaux,
  livraisonDit,
  validerEtape,
  validerOrdre,
  validerSite,
  type Etape,
  type FicheOrdre,
  type MotifLitige,
  type OrdreTransport,
  type SaisieEchange,
  type SaisieEtape,
  type SaisieLitige,
  type SaisieOrdre,
  type SaisieSite,
  type SaisieValidation,
  type TypeEtape,
} from './ot.js';
import {
  enBase64,
  refusDuDocument,
  suiteDeLaLecture,
  type EtatLecture,
  type PointAVerifier,
} from './confirmation_lue.js';
import {
  deposerUneConfirmation,
  ecarterLaConfirmation,
  etatDit,
  jourDe,
  lancerLaLecture,
  lectureDe,
  listerLesConfirmations,
  marquerCreee,
  nombreAVerifier,
  relire,
  sAbonnerALaLecture,
  suivanteAVerifier,
  trajetDe,
  type ConfirmationACreer,
} from './a_creer.js';
import type { ColonneTri, Filtres, Tri } from './ot.js';

// ---------------------------------------------------------------------------
// Les états
// ---------------------------------------------------------------------------

type EtatListe =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'prete'; readonly ordres: readonly OrdreTransport[] }
  | { readonly nom: 'panne' };

type EtatFiche =
  | { readonly nom: 'aucune' }
  | { readonly nom: 'chargement'; readonly id: string }
  | { readonly nom: 'prete'; readonly id: string; readonly fiche: FicheOrdre }
  | { readonly nom: 'panne'; readonly id: string; readonly cause: EchecApi };

type Onglet = 'commande' | 'etapes' | 'execution' | 'documents';

/** L'édition de la commande, dans la fiche comme à la création. */
interface EditionOrdre {
  readonly initiale: SaisieOrdre;
  readonly saisie: SaisieOrdre;
  readonly fautes: Readonly<Record<string, string>>;
  readonly refus: string | null;
  readonly envoi: boolean;
}

/**
 * L'étape ouverte en saisie.
 *
 * `id` vaut `null` pour une étape qu'on ajoute : elle n'existe pas encore en
 * base, et le formulaire est le même. `site` porte la création d'un site
 * repliée dans la carte — `null` tant qu'on ne l'a pas dépliée.
 */
interface EditionEtape {
  readonly id: string | null;
  readonly saisie: SaisieEtape;
  /**
   * L'étape telle qu'elle était à l'ouverture. Sert à n'envoyer QUE ce qui a
   * changé sur l'équipage : poser un camion sans toucher au chauffeur laisse
   * l'API recopier l'attelage du jour, ce qu'un « chauffeur : rien » explicite
   * empêcherait.
   */
  readonly initiale: SaisieEtape;
  readonly fautes: Readonly<Record<string, string>>;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly site: SaisieSite | null;
  readonly fautesSite: Readonly<Record<string, string>>;
  readonly confirme: boolean;
}

/**
 * LA SAISIE D'UN ORDRE NEUF — l'ordre et ses étapes, avant tout enregistrement.
 *
 * `relais` — F3, refonte de la saisie d'OT (31/10/2026). Décision de Mohamed :
 * « le relais doit rester possible ». Sans relais, le véhicule choisi en haut
 * est porté sur TOUTES les étapes ; avec, chaque étape a le sien. Facultatif :
 * absent vaut « pas de relais », et les créations existantes n'ont rien à
 * poser.
 */
interface EtatCreation {
  readonly ordre: EditionOrdre;
  readonly etapes: readonly EditionEtape[];
  readonly relais?: boolean;
  /**
   * LES PRESTATIONS ANNEXES SAISIES AVANT L'ENREGISTREMENT — F3. Elles partent
   * après la création de l'ordre, par la route des prestations.
   */
  readonly prestations?: readonly PrestationNeuve[];
  /**
   * LES DOCUMENTS DÉPOSÉS AVANT L'ENREGISTREMENT — F3. Cadrage §8 : le volet
   * Documents est gardé « pour pouvoir mettre les documents avant
   * d'enregistrer l'OT ». Ils sont joints juste après la création, par les
   * mêmes trois appels que l'onglet Documents.
   */
  readonly documents?: readonly AjoutDocument[];
  /**
   * « AFFRÉTER À UN TRANSPORTEUR… » choisi dans le véhicule — F3, cadrage §5.
   * L'affrètement ne se pose que sur un ordre qui existe : la boîte s'ouvre
   * dès la fiche enregistrée.
   */
  readonly affreter?: boolean;
  /**
   * LES LIGNES DU PACKAGING — F4, 01/11/2026. `undefined` tant qu'on n'y a
   * pas touché : elles se déduisent alors de ce que portent les chargements
   * (`marchandisesDepuisEtapes`), après la lecture d'une confirmation ou une
   * duplication.
   */
  readonly marchandises?: readonly LigneMarchandise[];
}

/** Les lignes du Packaging d'une saisie, déduites tant qu'on n'y a pas touché. */
function lignesDe(
  creation: EtatCreation,
  supports: readonly LigneReglee[],
): readonly LigneMarchandise[] {
  return (
    creation.marchandises ??
    marchandisesDepuisEtapes(
      creation.etapes.map((e) => e.saisie),
      supports,
    )
  );
}

/**
 * LES RANGS DES LIGNES SUIVENT LES ÉTAPES. Une étape retirée ou déplacée
 * décale celles d'après ; une ligne qui pointait sur l'étape retirée revient
 * à l'affectation d'office.
 */
function lignesApres(
  lignes: readonly LigneMarchandise[] | undefined,
  avant: readonly number[],
  types: readonly string[],
): readonly LigneMarchandise[] | undefined {
  if (lignes === undefined) return undefined;
  const defaut = ligneMarchandiseVide(types);
  return lignes.map((l) => {
    const c = avant.indexOf(l.chargement);
    const v = avant.indexOf(l.livraison);
    return {
      ...l,
      chargement: c >= 0 ? c : defaut.chargement,
      livraison: v >= 0 ? v : defaut.livraison,
    };
  });
}

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable:
    'Cet ordre de transport n’existe plus. Il a pu être supprimé entre-temps.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce numéro est déjà pris. Réessayez.',
  invalide: 'Une valeur saisie n’est pas acceptée. Vérifiez les champs.',
  // Un ordre de transport ne pose aucune période : ce refus ne vient jamais
  // d'ici. La clé existe parce que `EchecApi` la porte.
  conflit: 'Cette période en recouvre une autre.',
  encore_actif: 'Annulez cet ordre avant de le supprimer.',
  encore_rattache: 'Impossible de supprimer : quelque chose s’y rattache encore.',
  // Les quatre refus du planning. Ils n'arrivent pas sur cet écran — la carte
  // doit rester exhaustive, c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus, et son camion ne se retire plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // Ne vient jamais d'ici : seul le planning déplace des étapes. La carte doit
  // rester exhaustive — c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

// ---------------------------------------------------------------------------
// Petits morceaux de dessin
// ---------------------------------------------------------------------------

function Statut({ statut }: { readonly statut: string }) {
  return <span className={`statut ${classeStatut(statut)}`}>{statutDit(statut)}</span>;
}

/** Une paire libellé/valeur dans l'onglet Commande. Jamais de valeur inventée. */
function Champ({
  nom,
  children,
  calcule,
}: {
  readonly nom: string;
  readonly children: React.ReactNode;
  readonly calcule?: string | undefined;
}) {
  return (
    <div className="champ-ot">
      <dt>{nom}</dt>
      <dd className={calcule === undefined ? undefined : 'fige'}>
        {children}
        {calcule !== undefined && <span className="cadenas"> · {calcule}</span>}
      </dd>
    </div>
  );
}

function Absent() {
  return <span className="sourdine">non renseigné</span>;
}

/**
 * Le site tel qu'il s'écrit dans un déroulant : son nom, et sa ville s'il ne la
 * porte pas déjà.
 *
 * Constaté en REGARDANT l'écran le 04/09/2026 : « LE ROY LOGISTIQUE — Amiens »
 * s'affichait « LE ROY LOGISTIQUE — Amiens — Amiens ». Les sites réels de TNS
 * portent presque tous leur ville dans leur nom ; ajouter la colonne `ville`
 * sans regarder la répétait sur la moitié de la liste.
 */
export function nommerLeSite(site: {
  readonly nom: string;
  readonly ville: string | null;
}): string {
  if (site.ville === null || site.ville.trim() === '') return site.nom;
  const sansAccent = (t: string) =>
    t
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  return sansAccent(site.nom).includes(sansAccent(site.ville))
    ? site.nom
    : `${site.nom} — ${site.ville}`;
}

// ---------------------------------------------------------------------------
// Le formulaire d'une étape
// ---------------------------------------------------------------------------

export interface ProprietesFormulaireEtape {
  readonly edition: EditionEtape;
  readonly sites: readonly LigneListe[];
  readonly motifs: readonly MotifLitige[];
  /**
   * Les supports que la société a réglés, AVEC LEURS DIMENSIONS depuis le
   * 05/09/2026 : le formulaire n'en tire plus seulement des noms, il en tire
   * le mètre linéaire à afficher pendant la saisie. Ils ne sont plus écrits
   * dans le code
   * depuis le 04/09/2026 : ils descendent de l'API jusqu'ici.
   */
  readonly supports: readonly LigneReglee[];
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  /** Vrai à la création de l'ordre : ni « fait », ni litige sur une étape à naître. */
  readonly neuve: boolean;
  readonly quandChamp: (cle: keyof SaisieEtape, valeur: string | boolean) => void;
  readonly quandSite: (id: string) => void;
  readonly quandOuvrirSite: () => void;
  readonly quandChampSite: (cle: keyof SaisieSite, valeur: string) => void;
  readonly quandCreerSite: () => void;
  readonly quandFermerSite: () => void;
}

export function FormulaireEtape({
  edition,
  sites,
  supports,
  vehicules,
  chauffeurs,
  remorques,
  neuve,
  quandChamp,
  quandSite,
  quandOuvrirSite,
  quandChampSite,
  quandCreerSite,
  quandFermerSite,
}: ProprietesFormulaireEtape) {
  const { saisie, fautes, site, fautesSite } = edition;

  const faute = (cle: string) =>
    fautes[cle] === undefined ? null : (
      <span className="erreur-champ">{fautes[cle]}</span>
    );

  return (
    <div className="grille-saisie">
      <div className="saisie c3">
        <label htmlFor="etape-site">Site *</label>
        {/* CHERCHABLE DEPUIS LE 08/09/2026, même raison que le client. Le
            libellé reste `nommerLeSite` — le nom, la ville, le code postal —
            et c'est sur lui que porte la recherche : taper « chartres » trouve
            donc aussi les sites dont seule la VILLE le porte. */}
        <ChoixCherchable
          id="etape-site"
          choix={sites.map((s) => ({ id: s.id, libelle: nommerLeSite(s) }))}
          valeur={saisie.site_id}
          invite="Chercher un site…"
          fautif={fautes['site_id'] !== undefined}
          quandChoisir={quandSite}
        />
        {faute('site_id')}
      </div>
      <div className="sous-champ c3">
        {site === null && (
          <button
            className="bouton bouton-discret"
            type="button"
            onClick={quandOuvrirSite}
          >
            + Créer un site
          </button>
        )}
      </div>

      {/* Le site absent de l'annuaire se crée SANS QUITTER l'ordre : on
          n'abandonne pas une saisie à moitié faite pour aller ailleurs. Seul le
          nom est obligatoire — vérifié dans `sites.ts`, c'est le seul champ
          marqué `obligatoire` du descripteur. */}
      {site !== null && (
        <div className="creation-site">
          <p className="titre-creation-site">Créer le site</p>
          <div className="grille-saisie">
            <div className="saisie c3">
              <label htmlFor="site-nom">Nom *</label>
              <input
                id="site-nom"
                className="boite"
                type="text"
                value={site.nom}
                onChange={(e) => quandChampSite('nom', e.target.value)}
              />
              {fautesSite['nom'] !== undefined && (
                <span className="erreur-champ">{fautesSite['nom']}</span>
              )}
            </div>
            <div className="saisie c3">
              <label htmlFor="site-adresse">Adresse</label>
              <input
                id="site-adresse"
                className="boite"
                type="text"
                value={site.adresse}
                onChange={(e) => quandChampSite('adresse', e.target.value)}
              />
            </div>
            <div className="saisie c2">
              <label htmlFor="site-cp">Code postal</label>
              <input
                id="site-cp"
                className="boite"
                type="text"
                value={site.code_postal}
                onChange={(e) => quandChampSite('code_postal', e.target.value)}
              />
            </div>
            <div className="saisie c2">
              <label htmlFor="site-ville">Ville</label>
              <input
                id="site-ville"
                className="boite"
                type="text"
                value={site.ville}
                onChange={(e) => quandChampSite('ville', e.target.value)}
              />
            </div>
            {/* LE PAYS À LA PLACE DU CODE — F2, 31/10/2026. Le code n'est
                plus saisi : il est engendré à la création (`creerLeSite`).
                Même liste fermée que la fiche d'un site dans l'annuaire. */}
            <div className="saisie c2">
              <label htmlFor="site-pays">Pays</label>
              <select
                id="site-pays"
                className="boite"
                value={site.pays}
                onChange={(e) => quandChampSite('pays', e.target.value)}
              >
                {(PAYS.includes(site.pays) ? PAYS : [site.pays, ...PAYS]).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* TEXTE EXPLICATIF RETIRÉ — refonte graphique, maquettes validées le 31/10/2026 : « un logiciel ne se commente pas lui-même ». */}
          <div className="pied-creation-site">
            <button className="bouton" type="button" onClick={quandCreerSite}>
              Créer et rattacher
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              onClick={quandFermerSite}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* L'adresse du jour. Recopiée de la fiche du site, puis FIGÉE — mais
          modifiable, parce que le camion a pu aller ailleurs. Les quatre noms
          sont ceux de l'annuaire des sites. */}
      <div className="saisie c2">
        <label htmlFor="etape-nom">Nom du lieu</label>
        <input
          id="etape-nom"
          className="boite"
          type="text"
          value={saisie.site_nom}
          onChange={(e) => quandChamp('site_nom', e.target.value)}
        />
        {faute('site_nom')}
      </div>
      <div className="saisie c2">
        <label htmlFor="etape-adresse">Adresse</label>
        <input
          id="etape-adresse"
          className="boite"
          type="text"
          value={saisie.site_adresse}
          onChange={(e) => quandChamp('site_adresse', e.target.value)}
        />
      </div>
      <div className="saisie">
        <label htmlFor="etape-cp">Code postal</label>
        <input
          id="etape-cp"
          className="boite"
          type="text"
          value={saisie.site_code_postal}
          onChange={(e) => quandChamp('site_code_postal', e.target.value)}
        />
      </div>
      <div className="saisie">
        <label htmlFor="etape-ville">Ville</label>
        <input
          id="etape-ville"
          className="boite"
          type="text"
          value={saisie.site_ville}
          onChange={(e) => quandChamp('site_ville', e.target.value)}
        />
      </div>

      {/* Une date se saisit dans un champ DATE, une heure dans un champ HEURE —
          demandé par l'utilisateur le 03/09/2026. Le calendrier du système
          s'ouvre, les flèches incrémentent, et « 32/13/2026 » ne peut pas être
          tapé. Le format suit la langue du NAVIGATEUR, pas le code. */}
      <div className="saisie">
        <label htmlFor="etape-jour">Date *</label>
        <input
          id="etape-jour"
          className="boite"
          type="date"
          value={saisie.jour}
          onChange={(e) => quandChamp('jour', e.target.value)}
        />
        {faute('jour')}
      </div>
      <div className="saisie">
        <label htmlFor="etape-de">De</label>
        <input
          id="etape-de"
          className="boite"
          type="time"
          value={saisie.heure_debut}
          onChange={(e) => quandChamp('heure_debut', e.target.value)}
        />
        {faute('heure_debut')}
      </div>
      <div className="saisie">
        <label htmlFor="etape-a">À</label>
        <input
          id="etape-a"
          className="boite"
          type="time"
          value={saisie.heure_fin}
          onChange={(e) => quandChamp('heure_fin', e.target.value)}
        />
        {faute('heure_fin')}
      </div>
      <div className="saisie">
        <label htmlFor="etape-quantite">Quantité</label>
        <input
          id="etape-quantite"
          className="boite"
          type="text"
          inputMode="decimal"
          value={saisie.quantite}
          onChange={(e) => quandChamp('quantite', e.target.value)}
        />
        {faute('quantite')}
      </div>
      {/* Liste FERMÉE à l'écran, base ouverte — demandé le 04/09/2026, et
          contraire au commentaire de la migration : voir `SUPPORTS` dans
          `ot.ts`, où la contradiction est écrite. */}
      <div className="saisie">
        <label htmlFor="etape-support">Support</label>
        <select
          id="etape-support"
          className="boite"
          value={saisie.support}
          onChange={(e) => quandChamp('support', e.target.value)}
        >
          <option value="">Sans support</option>
          {supportsAvec(
            saisie.support,
            supports.map((s) => s.nom),
          ).map((s) => (
            <option value={s} key={s}>
              {s}
            </option>
          ))}
        </select>
        {/* LE MÈTRE LINÉAIRE, PENDANT QU'ON SAISIT — demandé par l'utilisateur
            le 05/09/2026. Il ne se saisit jamais et n'apparaît dans aucun
            champ : c'est une lecture, calculée par le même code que celui qui
            le figera sur l'étape. Absent quand il n'y a rien à dire, plutôt
            qu'un « 0,00 ML » qui se lirait « n'occupe rien ». */}
        {metreLineaireDit(saisie.support, saisie.quantite, supports) !== null && (
          <p className="calcule">
            {metreLineaireDit(saisie.support, saisie.quantite, supports)}
          </p>
        )}
      </div>
      <div className="saisie">
        <label htmlFor="etape-poids">Poids (kg)</label>
        <input
          id="etape-poids"
          className="boite"
          type="text"
          inputMode="numeric"
          value={saisie.poids}
          onChange={(e) => quandChamp('poids', e.target.value)}
        />
        {faute('poids')}
      </div>

      {/* LE CAMION EST SUR L'ÉTAPE, et non sur l'ordre — décidé le 04/09/2026 :
          « le relais doit être possible ». Le chauffeur et la remorque ne se
          choisissent pas ici : ils viennent de l'attelage du jour, réglé sur
          l'écran Affectation. Les poser à deux endroits finirait par les faire
          diverger. */}
      {/* L'ÉQUIPAGE DE CETTE ÉTAPE, figé une fois posé. Poser le camion sans
          toucher aux deux autres fait recopier l'attelage du jour par l'API —
          comme choisir un site recopie son adresse. Les corriger ici ne change
          RIEN à l'attelage du parc : ce sont deux choses différentes, et c'est
          ce qui fait qu'un attelage corrigé ne réécrit pas les transports
          passés. */}
      <div className="saisie c2">
        <label htmlFor="etape-vehicule">Camion</label>
        <select
          id="etape-vehicule"
          className="boite"
          value={saisie.vehicule_id}
          onChange={(e) => quandChamp('vehicule_id', e.target.value)}
        >
          <option value="">Pas encore planifiée</option>
          {vehicules
            .filter((v) => v.actif === true || v.id === saisie.vehicule_id)
            .map((v) => (
              <option value={v.id} key={v.id}>
                {texteParc(v, 'immatriculation') ?? v.id}
              </option>
            ))}
        </select>
      </div>
      <div className="saisie c2">
        <label htmlFor="etape-chauffeur">Chauffeur</label>
        <select
          id="etape-chauffeur"
          className="boite"
          value={saisie.chauffeur_id}
          onChange={(e) => quandChamp('chauffeur_id', e.target.value)}
        >
          <option value="">D’après l’attelage</option>
          {chauffeurs
            .filter((c) => c.actif === true || c.id === saisie.chauffeur_id)
            .map((c) => (
              <option value={c.id} key={c.id}>
                {texteParc(c, 'nom') ?? c.id}
              </option>
            ))}
        </select>
      </div>
      <div className="saisie c2">
        <label htmlFor="etape-remorque">Remorque</label>
        <select
          id="etape-remorque"
          className="boite"
          value={saisie.remorque_id}
          onChange={(e) => quandChamp('remorque_id', e.target.value)}
        >
          <option value="">D’après l’attelage</option>
          {remorques
            .filter((r) => r.actif === true || r.id === saisie.remorque_id)
            .map((r) => (
              <option value={r.id} key={r.id}>
                {texteParc(r, 'immatriculation') ?? r.id}
              </option>
            ))}
        </select>
      </div>

      {/* RÉFÉRENCE ET INSTRUCTIONS — F2, 31/10/2026. La référence est
          nouvelle (`etapes.reference`) ; les instructions sont la note de
          toujours (`etapes.note`), renommée comme sur la maquette validée. */}
      <div className="saisie c2">
        <label htmlFor="etape-reference">Référence</label>
        <input
          id="etape-reference"
          className="boite"
          type="text"
          value={saisie.reference}
          onChange={(e) => quandChamp('reference', e.target.value)}
        />
        {faute('reference')}
      </div>
      <div className="saisie c4">
        <label htmlFor="etape-note">Instructions</label>
        <input
          id="etape-note"
          className="boite"
          type="text"
          value={saisie.note}
          onChange={(e) => quandChamp('note', e.target.value)}
        />
      </div>

      {/* Le suivi n'existe pas sur une étape qui n'est pas encore enregistrée :
          on ne coche pas « fait » sur un transport qu'on est en train de saisir,
          et on n'ouvre pas un litige sur une livraison qui n'a pas eu lieu. */}
      {!neuve && (
        <>
          {/* `pleine-en-petit` : sous 640 px la grille n'a que deux colonnes,
              et sans cette règle « Passé le » et « À » — les deux moitiés d'un
              même instant — se retrouvaient sur deux lignes différentes.
              Constaté en REGARDANT l'écran en 420 px le 04/09/2026. */}
          <div className="saisie c2 pleine-en-petit">
            <label htmlFor="etape-fait">Réalisation</label>
            <label className="case-fait" htmlFor="etape-fait">
              <input
                id="etape-fait"
                type="checkbox"
                checked={saisie.fait}
                onChange={(e) => quandChamp('fait', e.target.checked)}
              />
              Validée
            </label>
          </div>
          {/* LES TROIS CHAMPS D'UN MÊME INSTANT TIENNENT DANS UNE SEULE CASE.

              09/09/2026. Une première rédaction leur donnait trois cases de
              grille : à 1440 px la ligne tenait, mais à 900 px « Arrivée » et
              « Départ » tombaient sur deux lignes différentes, et à 420 px les
              trois étaient séparés. MESURÉ sur l'aperçu, pas supposé.

              C'est exactement le défaut que le commentaire du 04/09 ci-dessus
              dit avoir déjà corrigé une fois pour « Passé le » et « À ». Les
              mettre dans UNE case le rend impossible à n'importe quelle
              largeur : la grille ne peut plus les séparer, seule la rangée
              interne se replie.

              Le jour du départ n'existe pas à l'écran : il vaut celui de
              l'arrivée. Une étape à cheval sur minuit se règle en avançant la
              date. Faire retaper la même date pour un cas sur cent serait une
              saisie de plus tous les jours. */}
          <div className="saisie c4 pleine-en-petit">
            <label htmlFor="etape-arrive-jour">Réalisée le</label>
            <div className="trio-heures">
              <input
                id="etape-arrive-jour"
                className="boite"
                type="date"
                disabled={!saisie.fait}
                value={saisie.arrive_jour}
                onChange={(e) => quandChamp('arrive_jour', e.target.value)}
              />
              <label className="etiquette-serree" htmlFor="etape-arrive-heure">
                Arrivée
              </label>
              <input
                id="etape-arrive-heure"
                className="boite"
                type="time"
                disabled={!saisie.fait}
                value={saisie.arrive_heure}
                onChange={(e) => quandChamp('arrive_heure', e.target.value)}
              />
              <label className="etiquette-serree" htmlFor="etape-reparti-heure">
                Départ
              </label>
              <input
                id="etape-reparti-heure"
                className="boite"
                type="time"
                disabled={!saisie.fait}
                value={saisie.reparti_heure}
                onChange={(e) => quandChamp('reparti_heure', e.target.value)}
              />
            </div>
            {faute('arrive_jour')}
            {faute('arrive_heure')}
            {faute('reparti_heure')}
          </div>
          <p className="aide-saisie c6">
            L’arrivée et le départ donnent le temps réellement passé sur place. Le
            bouton « Valider » de l’en-tête les demande aussi, sans passer par ce
            formulaire.
          </p>

          {/* LE LITIGE A QUITTÉ CE FORMULAIRE — 07/09/2026.
              Il vit dans sa table et se saisit par le bouton « Litige » de
              l'en-tête de l'étape, qui appelle sa propre route. Le laisser ici
              en ferait un champ parmi des heures et des quantités, qu'on pose
              par mégarde en corrigeant autre chose ; et surtout, un formulaire
              d'étape ne peut pas montrer PLUSIEURS litiges successifs, ce que
              la table permet désormais. */}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Les deux boîtes : valider une étape, ouvrir un litige
// ---------------------------------------------------------------------------

/**
 * VALIDER UNE ÉTAPE, et dire QUAND elle a été réalisée.
 *
 * Même forme que la boîte de décrochage de l'écran Affectation, et pour la même
 * raison, apprise le 04/09/2026 sur cet écran-là : poser une date en silence
 * est une faute, et obliger à ouvrir un formulaire pour la saisir en est une
 * autre. On la demande, au moment du geste, avec l'instant courant proposé.
 */
export function BoiteValidation({
  saisie,
  derniere,
  tempsPasse,
  envoi,
  refus,
  quandChamp,
  quandTemps,
  quandFermer,
  quandValider,
}: {
  readonly saisie: SaisieValidation;
  /**
   * LA DERNIÈRE ÉTAPE À FAIRE ? Le temps passé ne se demande qu'alors : c'est
   * le moment où le transport est fini, et le seul où l'on connaît sa durée.
   */
  readonly derniere: boolean;
  readonly tempsPasse: string;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (
    cle: 'jour' | 'heureArrivee' | 'heureDepart',
    valeur: string,
  ) => void;
  readonly quandTemps: (valeur: string) => void;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  const faute = refusValidation(saisie);
  const minutes = minutesSaisies(tempsPasse);
  /** « 1 h 25 sur place », ou `null` tant que les deux heures ne tiennent pas. */
  const attenteDite = (() => {
    if (saisie.heureArrivee === '' || saisie.heureDepart === '') return null;
    const a = instantDepuis(saisie.jour, saisie.heureArrivee);
    const d = instantDepuis(saisie.jour, saisie.heureDepart);
    if (a === null || d === null) return null;
    const ecart = Math.round((Date.parse(d) - Date.parse(a)) / 60000);
    if (ecart < 0) return null;
    return minutesDites(ecart);
  })();
  const tempsFautif = derniere && tempsPasse.trim() !== '' && minutes === null;

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Valider l’étape</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandValider();
          }}
        >
          {/* TROIS CHAMPS DEPUIS LE 09/09/2026, et non deux.

              Défaut signalé par l'utilisateur : « une étape ne me demande
              toujours pas l'heure d'arrivée et de départ en appuyant sur
              validé (uniquement en appuyant sur modifier) ». Le formulaire
              d'étape avait été repris, pas cette boîte — et c'est ELLE que
              l'exploitation ouvre tous les jours.

              L'ARRIVÉE EST FACULTATIVE : on valide souvent le lendemain matin
              les livraisons de la veille. L'exiger bloquerait la validation,
              donc le statut de l'ordre, donc la facturation, pour une donnée
              de confort. */}
          <div className="boites-validation">
            <div>
              <label className="etiquette-boite" htmlFor="validation-jour">
                Réalisée le
              </label>
              <input
                id="validation-jour"
                className="champ-boite"
                type="date"
                value={saisie.jour}
                disabled={envoi}
                onChange={(e) => quandChamp('jour', e.target.value)}
              />
            </div>
            <div>
              <label className="etiquette-boite" htmlFor="validation-arrivee">
                Arrivée
              </label>
              <input
                id="validation-arrivee"
                className="champ-boite"
                type="time"
                value={saisie.heureArrivee}
                disabled={envoi}
                onChange={(e) => quandChamp('heureArrivee', e.target.value)}
              />
            </div>
            <div>
              <label className="etiquette-boite" htmlFor="validation-depart">
                Départ
              </label>
              <input
                id="validation-depart"
                className="champ-boite"
                type="time"
                value={saisie.heureDepart}
                disabled={envoi}
                onChange={(e) => quandChamp('heureDepart', e.target.value)}
              />
            </div>
          </div>
          {/* LE TEMPS SUR PLACE, DIT TOUT DE SUITE. C'est la raison d'être des
              deux heures : sans ce retour, on saisit deux nombres sans voir ce
              qu'ils produisent. */}
          {attenteDite !== null && (
            <p className="aide-boite">{attenteDite} passées sur place.</p>
          )}
          {/* LE TEMPS PASSÉ — la seule saisie nouvelle du projet, et elle vit
              ICI plutôt que dans un champ à part. Le geste existe déjà et
              demande déjà l'heure ; en ajouter un second serait celui qu'on
              oublie. Décidé le 07/09/2026.

              POURQUOI IL FAUT LE SAISIR : un Orléans → Madrid chargé le 09 à
              7 h et livré le 11 à 8 h donne 49 heures d'écart, avec deux repos
              journaliers dedans. Prendre l'écart tel quel multiplierait le
              coût par trois. */}
          {derniere && (
            <>
              <label className="etiquette-boite" htmlFor="validation-temps">
                Temps passé sur le transport
              </label>
              <input
                id="validation-temps"
                className="champ-boite"
                type="text"
                placeholder="7 h 45"
                value={tempsPasse}
                disabled={envoi}
                onChange={(e) => quandTemps(e.target.value)}
              />
              {tempsFautif ? (
                <p className="bandeau-refus">
                  Écrivez « 7 h 45 », « 7:45 » ou un nombre de minutes.
                </p>
              ) : (
                <p className="aide-boite">
                  {minutes === null
                    ? 'Sans lui, le coût de revient de ce transport ne se calculera pas — l’écart entre le chargement et la livraison porte les repos, et le prendre tel quel triplerait le coût.'
                    : `${minutesDites(minutes) ?? ''} de conduite et de manœuvres. Le coût du transport se calcule dans la foulée.`}
                </p>
              )}
            </>
          )}

          {faute === null ? (
            <p className="aide-boite">
              L’instant courant est proposé. Corrigez-le si l’étape a été réalisée plus
              tôt — le lendemain matin, on valide souvent la veille.
            </p>
          ) : (
            <p className="faute-boite">{faute}</p>
          )}
          {refus !== null && <p className="faute-boite">{refus}</p>}
          <div className="pied-boite">
            <button className="bouton" type="submit" disabled={envoi || faute !== null}>
              {envoi ? 'Enregistrement…' : 'Valider'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * OUVRIR UN LITIGE.
 *
 * **Le motif se CRÉE ici**, et c'est ce qui manquait : la table `motifs_litige`
 * naît vide — la migration n'y insère aucune ligne — et aucun écran n'en
 * créait. Le déroulant ne proposait donc que « Aucun », et le bouton « Litige »
 * ne servait à rien. Constaté par l'utilisateur le 04/09/2026 : « Litige ne
 * marche pas ». C'est le défaut évité sur les absences le 03/09, et refait ici.
 *
 * Les motifs restent PARAMÉTRABLES par société : Stafa est vendu à d'autres
 * transporteurs, qui n'ont pas les mêmes mots.
 */
export function BoiteLitige({
  saisie,
  motifs,
  envoi,
  refus,
  quandChamp,
  quandNouveauMotif,
  quandFermer,
  quandOuvrir,
}: {
  readonly saisie: SaisieLitige;
  readonly motifs: readonly MotifLitige[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (
    cle: 'motifId' | 'note' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  /** `null` referme la création et revient au choix dans la liste. */
  readonly quandNouveauMotif: (nom: string | null) => void;
  readonly quandFermer: () => void;
  readonly quandOuvrir: () => void;
}) {
  const faute = refusLitige(saisie);
  const actifs = motifs.filter((m) => m.actif);

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Ouvrir un litige</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandOuvrir();
          }}
        >
          {saisie.motifNouveau === null ? (
            <>
              <label className="etiquette-boite" htmlFor="litige-motif">
                Motif
              </label>
              <select
                id="litige-motif"
                className="champ-boite"
                value={saisie.motifId}
                disabled={envoi}
                onChange={(e) => quandChamp('motifId', e.target.value)}
              >
                <option value="">Choisir un motif…</option>
                {actifs.map((m) => (
                  <option value={m.id} key={m.id}>
                    {m.nom}
                  </option>
                ))}
              </select>
              <div className="pied-creation-site">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={() => quandNouveauMotif('')}
                >
                  + Nouveau motif
                </button>
              </div>
              {actifs.length === 0 && (
                <p className="aide-boite">
                  Aucun motif n’est encore enregistré. Créez le premier : ils sont
                  propres à votre société, et vous les retrouverez ensuite.
                </p>
              )}
            </>
          ) : (
            <>
              <label className="etiquette-boite" htmlFor="litige-nouveau">
                Nouveau motif
              </label>
              <input
                id="litige-nouveau"
                className="champ-boite"
                type="text"
                placeholder="Attente sur site, Marchandise abîmée…"
                value={saisie.motifNouveau}
                disabled={envoi}
                onChange={(e) => quandNouveauMotif(e.target.value)}
              />
              <div className="pied-creation-site">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={() => quandNouveauMotif(null)}
                >
                  Choisir dans la liste
                </button>
              </div>
            </>
          )}

          <label className="etiquette-boite" htmlFor="litige-quoi">
            Ce qui s’est passé
          </label>
          <input
            id="litige-quoi"
            className="champ-boite"
            type="text"
            value={saisie.note}
            disabled={envoi}
            onChange={(e) => quandChamp('note', e.target.value)}
          />

          <div className="deux-boites">
            <div>
              <label className="etiquette-boite" htmlFor="litige-jour">
                Constaté le
              </label>
              <input
                id="litige-jour"
                className="champ-boite"
                type="date"
                value={saisie.jour}
                disabled={envoi}
                onChange={(e) => quandChamp('jour', e.target.value)}
              />
            </div>
            <div>
              <label className="etiquette-boite" htmlFor="litige-heure">
                à
              </label>
              <input
                id="litige-heure"
                className="champ-boite"
                type="time"
                value={saisie.heure}
                disabled={envoi}
                onChange={(e) => quandChamp('heure', e.target.value)}
              />
            </div>
          </div>

          {faute === null ? (
            <p className="aide-boite">
              Le litige restera ouvert tant que personne n’aura écrit comment il a été
              réglé. Cela se fait depuis « Modifier », sur l’étape.
            </p>
          ) : (
            <p className="faute-boite">{faute}</p>
          )}
          {refus !== null && <p className="faute-boite">{refus}</p>}

          <div className="pied-boite">
            <button className="bouton" type="submit" disabled={envoi || faute !== null}>
              {envoi ? 'Enregistrement…' : 'Ouvrir le litige'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Une carte d'étape
// ---------------------------------------------------------------------------

/**
 * LA BOÎTE « ÉCHANGE » — la seule saisie d'emballage de tout le logiciel.
 *
 * Le cadrage §6 : « Échange choisi à la saisie de l'étape, pas sur le site. »
 * L'écran Emballage montre les soldes et tient les bons ; c'est ICI qu'on dit
 * ce qui s'est passé au quai.
 *
 * UNE BOÎTE ET NON DES CHAMPS DANS LE FORMULAIRE DE L'ÉTAPE. Deux raisons :
 * l'échange s'écrit par une AUTRE route (`PUT /emballage/etapes/:id`), et il
 * n'existe qu'une fois l'étape enregistrée — le mêler à la saisie obligerait à
 * deux écritures dans un même geste, dont la seconde pourrait échouer seule.
 * Même choix que la boîte de litige, juste au-dessus.
 *
 * DEUX CHIFFRES SAISIS, ET DEUX SEULEMENT. « Restées » est livrées − reprises,
 * et l'écart aussi : les saisir serait un troisième nombre à tenir d'accord —
 * ce que faisait la v1, avec `restees` ET `ecart` en plus de la quantité.
 */
export function BoiteEchange({
  saisie,
  supports,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
  quandEffacer,
}: {
  readonly saisie: SaisieEchange;
  readonly supports: readonly LigneReglee[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: keyof SaisieEchange, valeur: string) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
  /** `null` quand il n'y a rien à effacer : l'étape n'a pas encore d'échange. */
  readonly quandEffacer: (() => void) | null;
}) {
  const faute = refusEchange(saisie);
  const ecart = ecartEchange(saisie);
  const echangeables = supports.filter((s) => s.echangeable === true);

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Échange de palettes</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="echange-support">
            Support échangé
          </label>
          <select
            id="echange-support"
            className="champ-boite"
            value={saisie.support}
            disabled={envoi}
            onChange={(e) => quandChamp('support', e.target.value)}
          >
            <option value="">Choisir un support…</option>
            {/* La liste ne propose QUE ce qui se rend. Un support qu'on ne
                reprend pas n'a pas d'échange, et le proposer inviterait à
                saisir un mouvement qui ne veut rien dire. Le support déjà
                saisi reste proposé même s'il a été décoché depuis : on ne
                perd pas une valeur qu'on retrouve dans l'historique. */}
            {supportsAvec(
              saisie.support,
              echangeables.map((s) => s.nom),
            ).map((nom) => (
              <option value={nom} key={nom}>
                {nom}
              </option>
            ))}
          </select>
          {echangeables.length === 0 && (
            <p className="aide-boite">
              Aucun support n’est marqué comme échangeable. Cochez-le dans Paramètres →
              Supports.
            </p>
          )}

          <label className="etiquette-boite" htmlFor="echange-livrees">
            Palettes livrées
          </label>
          <input
            id="echange-livrees"
            className="champ-boite"
            type="text"
            inputMode="numeric"
            value={saisie.livrees}
            disabled={envoi}
            onChange={(e) => quandChamp('livrees', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="echange-reprises">
            Reprises sur place
          </label>
          <input
            id="echange-reprises"
            className="champ-boite"
            type="text"
            inputMode="numeric"
            value={saisie.reprises}
            disabled={envoi}
            onChange={(e) => quandChamp('reprises', e.target.value)}
          />

          {/* L'ÉCART SE LIT PENDANT QU'ON SAISIT, il ne se saisit jamais.
              C'est lui que le bon reconnaîtra, et c'est lui qui bougera le
              solde du client. */}
          {ecart !== null && ecart !== 0 && (
            <p className="calcule">
              {ecart > 0
                ? `Le client nous devra ${String(ecart)} palettes.`
                : `Nous lui devrons ${String(Math.abs(ecart))} palettes.`}
            </p>
          )}

          <label className="etiquette-boite" htmlFor="echange-bon">
            N° du bon remis par le client
          </label>
          <input
            id="echange-bon"
            className="champ-boite"
            type="text"
            value={saisie.bonNumero}
            disabled={envoi}
            onChange={(e) => quandChamp('bonNumero', e.target.value)}
          />

          {saisie.bonNumero.trim() !== '' && (
            <>
              <label className="etiquette-boite" htmlFor="echange-bon-expire">
                Valable jusqu’au
              </label>
              <input
                id="echange-bon-expire"
                className="champ-boite"
                type="date"
                value={saisie.bonExpire}
                disabled={envoi}
                onChange={(e) => quandChamp('bonExpire', e.target.value)}
              />
              {/* TEXTE EXPLICATIF RETIRÉ — refonte graphique, maquettes validées le 31/10/2026 : « un logiciel ne se commente pas lui-même ». */}
            </>
          )}

          {faute !== null && <p className="erreur-champ">{faute}</p>}
          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi || faute !== null}>
              {envoi ? 'Enregistrement…' : 'Enregistrer l’échange'}
            </button>
            {quandEffacer !== null && (
              <button
                className="bouton bouton-risque"
                type="button"
                disabled={envoi}
                onClick={quandEffacer}
              >
                Effacer
              </button>
            )}
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * LE PANNEAU D'AFFRÈTEMENT — qui appeler, à quel prix, et ce qu'il reste.
 *
 * Cadrage §6 : « Depuis le champ Véhicule : panneau avec transporteur, contact,
 * prix d'achat obligatoire, véhicule demandé, instructions, marge affichée en
 * direct. »
 *
 * L'AXE NE SE SAISIT PAS. Il se déduit du premier chargement et de la dernière
 * livraison — l'API le calcule et le fige à l'enregistrement. Ici, il ne sert
 * qu'à CHERCHER : la liste montre d'abord ceux qui font cet axe.
 *
 * LA MARGE S'AFFICHE EN DIRECT, et elle ne bloque rien. Le cadrage bloque sur
 * le prix de VENTE sous grille, pas sur l'achat : l'exploitation sait des
 * choses que le logiciel ignore. On montre, on n'interdit pas.
 */
export function BoiteAffretement({
  saisie,
  prixVente,
  transporteurs,
  tous,
  chargement,
  envoi,
  refus,
  quandChamp,
  quandNouveau,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: SaisieAffretement;
  readonly prixVente: string | null;
  readonly transporteurs: readonly TransporteurTrouve[];
  /** L'annuaire ENTIER — voir `grouperTransporteurs` pour le pourquoi. */
  readonly tous: readonly LigneListe[];
  readonly chargement: boolean;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: keyof SaisieAffretement, valeur: string) => void;
  readonly quandNouveau: () => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  const m = marge(prixVente, saisie.prixAchat === '' ? null : saisie.prixAchat);
  const groupes = grouperTransporteurs(transporteurs, tous);
  // Le téléphone peut venir de l'un OU de l'autre : un transporteur hors axe en
  // a un aussi, et c'est justement lui qu'on va appeler.
  const choisi =
    transporteurs.find((t) => t.id === saisie.transporteurId) ??
    tous.find((t) => t.id === saisie.transporteurId) ??
    null;
  const pret = saisie.transporteurId !== '' && saisie.prixAchat.trim() !== '';

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Affréter cet ordre</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="affretement-qui">
            Transporteur
          </label>
          <select
            id="affretement-qui"
            className="champ-boite"
            value={saisie.transporteurId}
            disabled={envoi || chargement}
            onChange={(e) => {
              if (e.target.value === CHOIX_NOUVEAU) {
                quandNouveau();
                return;
              }
              quandChamp('transporteurId', e.target.value);
            }}
          >
            <option value="">
              {chargement ? 'Recherche des transporteurs…' : 'Choisir…'}
            </option>
            {/* CEUX QUI FONT L'AXE D'ABORD — c'est le moteur qui les a classés :
                nombre de transports, puis dernier affrètement, puis nom. Le
                nombre est écrit à côté, pour qu'on sache POURQUOI celui-là est
                en tête. PUIS TOUS LES AUTRES : la couverture n'est pas toujours
                saisie, et on appelle parfois hors zone (décision du 07/09). */}
            {groupes.axe.length > 0 && (
              <optgroup label="Font cet axe">
                {groupes.axe.map((o) => (
                  <option value={o.id} key={o.id}>
                    {o.texte}
                  </option>
                ))}
              </optgroup>
            )}
            {groupes.autres.length > 0 && (
              <optgroup
                label={
                  groupes.axe.length > 0 ? 'Tous les autres' : 'Tous les transporteurs'
                }
              >
                {groupes.autres.map((o) => (
                  <option value={o.id} key={o.id}>
                    {o.texte}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Il n’est pas dans la liste">
              <option value={CHOIX_NOUVEAU}>+ Nouveau transporteur…</option>
            </optgroup>
          </select>
          {!chargement && groupes.axe.length === 0 && groupes.autres.length > 0 && (
            <p className="aide-boite">
              Aucun transporteur ne fait cet axe avec ce matériel d’après sa couverture.
              Les autres restent proposés ci-dessus.
            </p>
          )}
          {!chargement && groupes.axe.length === 0 && groupes.autres.length === 0 && (
            <p className="aide-boite">
              Votre annuaire ne contient aucun transporteur actif. Créez-en un
              ci-dessus.
            </p>
          )}
          {choisi?.telephone != null && <p className="calcule">{choisi.telephone}</p>}

          <label className="etiquette-boite" htmlFor="affretement-prix">
            Prix d’achat HT
          </label>
          <input
            id="affretement-prix"
            className="champ-boite"
            type="text"
            inputMode="decimal"
            value={saisie.prixAchat}
            disabled={envoi}
            onChange={(e) => quandChamp('prixAchat', e.target.value)}
          />

          {/* LA MARGE EN DIRECT, dans ses trois états. Le signe et le taux
              portent le sens ; la classe ne fait que le répéter. */}
          {m !== null && <p className={`calcule marge-${m.couleur}`}>{margeDite(m)}</p>}
          {m === null && prixVente === null && (
            <p className="aide-boite">
              Cet ordre n’a pas de prix de vente : la marge ne se calcule pas.
            </p>
          )}

          <label className="etiquette-boite" htmlFor="affretement-contact">
            Contact chez lui
          </label>
          <input
            id="affretement-contact"
            className="champ-boite"
            type="text"
            value={saisie.contactNom}
            disabled={envoi}
            onChange={(e) => quandChamp('contactNom', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="affretement-instructions">
            Instructions
          </label>
          <input
            id="affretement-instructions"
            className="champ-boite"
            type="text"
            value={saisie.instructions}
            disabled={envoi}
            onChange={(e) => quandChamp('instructions', e.target.value)}
          />

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi || !pret}>
              {envoi ? 'Enregistrement…' : 'Affréter'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * RÉSOUDRE UN LITIGE — dire COMMENT, et quand.
 *
 * La résolution est OBLIGATOIRE : c'est le cadrage, et c'est la base
 * (`litiges_resolution_complete`). Un litige clos sans dire comment ne vaut pas
 * mieux qu'un litige oublié — on le rouvrirait dans six mois sans savoir ce qui
 * avait été convenu.
 *
 * La date se saisit, comme celle d'ouverture : un avoir passé la semaine
 * dernière ne doit pas porter la date d'aujourd'hui.
 */
export function BoiteResolution({
  saisie,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: {
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  };
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: 'texte' | 'jour' | 'heure', valeur: string) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Résoudre le litige</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="resolution-texte">
            Comment il a été réglé
          </label>
          <input
            id="resolution-texte"
            className="champ-boite"
            type="text"
            value={saisie.texte}
            disabled={envoi}
            onChange={(e) => quandChamp('texte', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="resolution-jour">
            Réglé le
          </label>
          <div className="paire-boite">
            <input
              id="resolution-jour"
              className="champ-boite"
              type="date"
              value={saisie.jour}
              disabled={envoi}
              onChange={(e) => quandChamp('jour', e.target.value)}
            />
            <input
              className="champ-boite"
              type="time"
              aria-label="Heure du règlement"
              value={saisie.heure}
              disabled={envoi}
              onChange={(e) => quandChamp('heure', e.target.value)}
            />
          </div>

          <p className="aide-boite">
            Le litige reste dans l’historique de l’étape avec ce texte. Si l’incident se
            répète, on en ouvre un NOUVEAU — celui-ci garde ce qui a été convenu.
          </p>

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || saisie.texte.trim() === ''}
            >
              {envoi ? 'Enregistrement…' : 'Résoudre'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Ce qui se saisit dans la boîte d'une prestation annexe. */
export interface SaisiePrestation {
  /** `null` = on en ajoute une ; sinon on modifie celle-ci. */
  readonly prestationId: string | null;
  readonly natureId: string;
  readonly detail: string;
  readonly quantite: string;
  readonly prixUnitaire: string;
}

/**
 * AJOUTER OU MODIFIER UNE PRESTATION ANNEXE — 15/09/2026.
 *
 * LA NATURE NE SE CHANGE PLUS EN MODIFICATION, et le menu est alors éteint.
 * Elle décide de l'unité, donc du sens de la quantité : « 2 » passerait de
 * deux heures à deux palettes sans que rien ne le signale. Se tromper de
 * nature se corrige en retirant la prestation et en la reposant — même règle
 * que la catégorie d'une ligne de facture.
 *
 * LE PRIX EST PRÉ-REMPLI PAR LA NATURE et reste modifiable : 45 €/h d'attente
 * est ton tarif, et le retaper chaque fois finit par produire 40, 45 et 50 sur
 * trois ordres du même mois ; mais un client négocié n'est pas une exception.
 */
export function BoitePrestation({
  saisie,
  natures,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: SaisiePrestation;
  readonly natures: readonly LigneReglee[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (
    cle: 'natureId' | 'detail' | 'quantite' | 'prixUnitaire',
    valeur: string,
  ) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  const modification = saisie.prestationId !== null;
  // LES ÉTEINTES NE SE PROPOSENT PAS, mais celle DÉJÀ CHOISIE reste dans la
  // liste même éteinte : sans elle, modifier une vieille prestation ferait
  // sauter le menu sur une autre nature sans prévenir.
  const proposables = natures.filter(
    (n) => n.actif || n.id === saisie.natureId,
  );
  const choisie = natures.find((n) => n.id === saisie.natureId);
  const total = (() => {
    const q = Number(saisie.quantite.replace(',', '.'));
    const pu = Number(saisie.prixUnitaire.replace(',', '.'));
    if (!Number.isFinite(q) || !Number.isFinite(pu)) return null;
    return enEuros((Math.round(q * pu * 100) / 100).toFixed(2));
  })();

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>{modification ? 'Modifier la prestation' : 'Prestation annexe'}</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="prestation-nature">
            Nature
          </label>
          <select
            id="prestation-nature"
            className="champ-boite"
            value={saisie.natureId}
            disabled={envoi || modification}
            onChange={(e) => quandChamp('natureId', e.target.value)}
          >
            <option value="">—</option>
            {proposables.map((n) => (
              <option key={n.id} value={n.id}>
                {n.nom}
                {n.prix_defaut == null
                  ? ''
                  : ` — ${enEuros(n.prix_defaut) ?? ''}/${uniteDite(n.unite ?? '') || 'forfait'}`}
              </option>
            ))}
          </select>

          <label className="etiquette-boite" htmlFor="prestation-detail">
            Précision (facultative)
          </label>
          <input
            id="prestation-detail"
            className="champ-boite"
            type="text"
            value={saisie.detail}
            disabled={envoi}
            onChange={(e) => quandChamp('detail', e.target.value)}
          />

          <div className="paire-boite">
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="prestation-quantite">
                Quantité
                {choisie === undefined
                  ? ''
                  : ` (${uniteEnToutesLettres(choisie.unite ?? '')})`}
              </label>
              <input
                id="prestation-quantite"
                className="champ-boite"
                type="text"
                inputMode="decimal"
                value={saisie.quantite}
                disabled={envoi}
                onChange={(e) => quandChamp('quantite', e.target.value)}
              />
            </div>
            <div className="champ-boite-colonne">
              <label className="etiquette-boite" htmlFor="prestation-prix">
                Prix unitaire HT
              </label>
              <input
                id="prestation-prix"
                className="champ-boite"
                type="text"
                inputMode="decimal"
                value={saisie.prixUnitaire}
                disabled={envoi}
                onChange={(e) => quandChamp('prixUnitaire', e.target.value)}
              />
            </div>
          </div>

          <p className="aide-boite">
            Total HT de la prestation : <b>{total ?? '—'}</b>. Elle entre dans le
            chiffre d’affaires de l’ordre, donc dans sa marge réelle, et partira
            sur la facture sous le même numéro d’OT — dans le groupe
            « Prestations annexes », jamais mélangée au transport.
          </p>

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={
                envoi ||
                saisie.natureId === '' ||
                saisie.quantite.trim() === '' ||
                saisie.prixUnitaire.trim() === ''
              }
            >
              {envoi ? 'Enregistrement…' : modification ? 'Enregistrer' : 'Ajouter'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * LE FORMULAIRE DE L'ANNUAIRE, DANS UNE FENÊTRE, PAR-DESSUS L'ORDRE.
 *
 * Demandé le 07/09/2026 : « une fenêtre de l'annuaire mais sans quitter
 * l'ordre ». Ce n'est donc PAS un second formulaire écrit ici — c'est le même
 * `FormulaireFiche` que l'écran Annuaire, avec les mêmes champs, la même
 * validation et le même code proposé. En écrire un second aurait garanti que
 * les deux divergent au premier champ ajouté.
 *
 * Le voile est celui des autres boîtes ; seule la largeur change, parce qu'une
 * fiche de transporteur porte quatorze champs là où une boîte en porte trois.
 */
/**
 * CRÉER UNE FICHE D'ANNUAIRE SANS QUITTER L'ORDRE.
 *
 * PARAMÉTRÉE PAR L'ANNUAIRE, et non recopiée pour chacun. Elle sert au
 * TRANSPORTEUR depuis le 06/09/2026 et au CLIENT depuis le 08/09 ; deux boîtes
 * jumelles auraient divergé au premier changement porté d'un seul côté — c'est
 * la raison qui a déjà fait paramétrer `EcranAnnuaire` et `annuaire.ts`.
 *
 * Le formulaire est celui de l'Annuaire, AU CHAMP PRÈS : le descripteur est la
 * seule source de vérité sur ce qu'une fiche porte. Un champ ajouté à la fiche
 * client apparaît ici sans qu'on y touche.
 *
 * PAR-DESSUS ce qui est ouvert dessous, qui reste en place : la commande à
 * moitié saisie, ou le prix déjà tapé dans le panneau d'affrètement, ne sont
 * pas perdus.
 */
function BoiteNouvelleFiche({
  annuaire,
  saisie,
  fautes,
  refus,
  envoi,
  quandChamp,
  quandNomQuitte,
  quandEnregistrer,
  quandAnnuler,
}: {
  readonly annuaire: Annuaire;
  readonly saisie: Saisie;
  readonly fautes: Readonly<Record<string, string>>;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly quandChamp: (cle: string, valeur: string) => void;
  readonly quandNomQuitte: () => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
}) {
  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-annuaire" role="dialog" aria-modal="true">
        <FormulaireFiche
          champs={annuaire.champs}
          titre={`Nouveau ${annuaire.singulier}`}
          saisie={saisie}
          fautes={fautes}
          refus={refus}
          envoi={envoi}
          quandChamp={quandChamp}
          quandNomQuitte={quandNomQuitte}
          quandEnregistrer={quandEnregistrer}
          quandAnnuler={quandAnnuler}
        />
      </div>
    </div>
  );
}

function CarteEtape({
  etape,
  edition,
  sites,
  motifs,
  supports,
  vehicules,
  chauffeurs,
  remorques,
  quandModifier,
  quandBasculerFait,
  quandLitige,
  quandResoudre,
  quandEchange,
  quandMonter,
  quandDescendre,
  occupee,
  quandChamp,
  quandSite,
  quandOuvrirSite,
  quandChampSite,
  quandCreerSite,
  quandFermerSite,
  quandEnregistrer,
  quandAnnuler,
  quandDemanderSuppression,
  quandSupprimer,
  gelee,
}: {
  readonly etape: Etape;
  readonly edition: EditionEtape | null;
  readonly sites: readonly LigneListe[];
  readonly motifs: readonly MotifLitige[];
  readonly supports: readonly LigneReglee[];
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly quandModifier: () => void;
  readonly quandBasculerFait: () => void;
  readonly quandLitige: () => void;
  readonly quandResoudre: (litigeId: string) => void;
  readonly quandEchange: () => void;
  readonly quandMonter: (() => void) | null;
  readonly quandDescendre: (() => void) | null;
  /**
   * L'ORDRE EST PARTI EN FACTURATION : l'étape ne se touche plus.
   *
   * Règle du 09/09/2026. Ce qui disparaît : monter, descendre, valider,
   * dévalider, modifier, supprimer — tout ce qui change ce qui sera facturé.
   *
   * CE QUI RESTE : le litige et l'échange de palettes. Ni l'un ni l'autre ne
   * change la facture, et le litige est justement ce qui vous apprend qu'il
   * faut faire revenir l'ordre. Le geler empêcherait d'écrire le motif même du
   * retour.
   */
  readonly gelee: boolean;
  readonly occupee: boolean;
  readonly quandChamp: (cle: keyof SaisieEtape, valeur: string | boolean) => void;
  readonly quandSite: (id: string) => void;
  readonly quandOuvrirSite: () => void;
  readonly quandChampSite: (cle: keyof SaisieSite, valeur: string) => void;
  readonly quandCreerSite: () => void;
  readonly quandFermerSite: () => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
  readonly quandDemanderSuppression: () => void;
  readonly quandSupprimer: () => void;
}) {
  const ouverte = edition !== null;
  const classes = ['carte-etape'];
  if (etape.fait) classes.push('faite');
  if (ouverte) classes.push('ouverte');

  const quand = creneau(etape);
  const jour = jourCourt(etape.jour);
  const litige = litigeDit(etape);

  return (
    <div className={classes.join(' ')}>
      <div className="entete-etape">
        <span className="rang">{etape.rang}</span>
        <span className="type-etape">
          {etape.type === 'chargement' ? 'Chargement' : 'Livraison'}
        </span>
        <span className="site-etape">{etape.site_nom}</span>
        {/* « Validée », et non « faite » — mot choisi par l'utilisateur le
            04/09/2026. */}
        {etape.fait && (
          <span className="statut livre">
            {instantDit(etape.reparti_le) === null
              ? 'Validée'
              : `Validée le ${instantDit(etape.reparti_le) as string}`}
          </span>
        )}
        {/* LES DEUX ACTIONS DU QUOTIDIEN SONT ICI, en un clic.
            Défaut signalé par l'utilisateur le 04/09/2026 : « aucune
            possibilité de valider une étape ou de mettre un litige ». Les deux
            existaient — derrière « Modifier ». C'est exactement le diagnostic
            de l'écran Affectation le 03/09 : l'action la plus fréquente doit
            être la plus visible, pas la mieux cachée. */}
        {!ouverte && (
          <div className="actions-etape">
            {!gelee && quandMonter !== null && (
              <button
                className="bouton bouton-discret bouton-petit"
                type="button"
                disabled={occupee}
                onClick={quandMonter}
                aria-label={`Monter l’étape ${String(etape.rang)}`}
                title="Monter cette étape"
              >
                ↑
              </button>
            )}
            {!gelee && quandDescendre !== null && (
              <button
                className="bouton bouton-discret bouton-petit"
                type="button"
                disabled={occupee}
                onClick={quandDescendre}
                aria-label={`Descendre l’étape ${String(etape.rang)}`}
                title="Descendre cette étape"
              >
                ↓
              </button>
            )}
            {!gelee && (
              <button
                className={
                  etape.fait
                    ? 'bouton bouton-discret bouton-petit'
                    : 'bouton bouton-petit'
                }
                type="button"
                disabled={occupee}
                onClick={quandBasculerFait}
              >
                {etape.fait ? 'Dévalider' : 'Valider'}
              </button>
            )}
            {etape.motif_litige_id === null && (
              <button
                className="bouton bouton-risque bouton-petit"
                type="button"
                disabled={occupee}
                onClick={quandLitige}
              >
                Litige
              </button>
            )}
            {/* L'ÉCHANGE EST UN GESTE DE L'ÉTAPE, comme valider et comme
                ouvrir un litige — et non un champ du formulaire : il s'écrit
                par une autre route, et il n'existe qu'une fois l'étape
                enregistrée. Le libellé dit s'il y en a déjà un, pour qu'on
                sache qu'on va corriger et non créer. */}
            <button
              className="bouton bouton-discret bouton-petit"
              type="button"
              disabled={occupee}
              onClick={quandEchange}
            >
              {etape.echange_support === null ? 'Échange' : 'Échange ✓'}
            </button>
            {!gelee && (
              <button
                className="bouton bouton-discret bouton-petit"
                type="button"
                disabled={occupee}
                onClick={quandModifier}
              >
                Modifier
              </button>
            )}
          </div>
        )}
      </div>

      <div className="corps-etape">
        {edition === null ? (
          <dl className="grille-lecture">
            <div className="case-lecture">
              <dt>Adresse du jour</dt>
              <dd>
                {adresseDite(etape).map((ligne, rang) => (
                  <span className="ligne-adresse" key={`${ligne}-${String(rang)}`}>
                    {ligne}
                  </span>
                ))}
                {/* L'ITINÉRAIRE GPS — lot 133, maquette téléphone du
                    01/11/2026. Un lien vers Google Maps sur l'adresse de
                    l'étape : sur un téléphone, il ouvre l'application de
                    navigation. Rien n'est envoyé d'autre que l'adresse
                    déjà affichée. Absent quand l'étape n'a pas d'adresse. */}
                {lienItineraire(etape) !== null && (
                  <a
                    className="lien-itineraire"
                    href={lienItineraire(etape) ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Itinéraire GPS ›
                  </a>
                )}
              </dd>
            </div>
            <div className="case-lecture">
              <dt>Créneau</dt>
              <dd>
                {jour === null ? (
                  <Absent />
                ) : quand === null ? (
                  jour
                ) : (
                  `${jour} · ${quand}`
                )}
              </dd>
            </div>
            <div className="case-lecture">
              <dt>Quantité</dt>
              <dd>
                {etape.quantite === null ? (
                  <Absent />
                ) : (
                  `${lisible(Number(etape.quantite)) ?? etape.quantite}${
                    etape.support === null ? '' : ` ${etape.support}`
                  }`
                )}
              </dd>
            </div>
            <div className="case-lecture">
              <dt>Poids</dt>
              <dd>
                {etape.poids === null ? (
                  <Absent />
                ) : (
                  `${lisible(etape.poids) as string} kg`
                )}
              </dd>
            </div>
            {/* Revenue le 04/09/2026, une fois le lien posé en base. Elle
                manquait, et l'écran le disait plutôt que d'inventer. */}
            <div className="case-lecture">
              <dt>Ressource affectée</dt>
              <dd>
                {ressourceDite(etape) === null ? (
                  <span className="sourdine">pas encore planifiée</span>
                ) : (
                  <span className="code">{ressourceDite(etape) as string}</span>
                )}
              </dd>
            </div>
            {etape.reference !== null && (
              <div className="case-lecture">
                <dt>Référence</dt>
                <dd>{etape.reference}</dd>
              </div>
            )}
            {etape.note !== null && (
              <div className="case-lecture">
                <dt>Instructions</dt>
                <dd>{etape.note}</dd>
              </div>
            )}
          </dl>
        ) : (
          <>
            <FormulaireEtape
              edition={edition}
              sites={sites}
              motifs={motifs}
              supports={supports}
              vehicules={vehicules}
              chauffeurs={chauffeurs}
              remorques={remorques}
              neuve={false}
              quandChamp={quandChamp}
              quandSite={quandSite}
              quandOuvrirSite={quandOuvrirSite}
              quandChampSite={quandChampSite}
              quandCreerSite={quandCreerSite}
              quandFermerSite={quandFermerSite}
            />
            {edition.refus !== null && <p className="bandeau">{edition.refus}</p>}
          </>
        )}
      </div>

      {/* Le litige est SUR LA CARTE DE SON ÉTAPE, et non dans un onglet à part :
          il faudrait sinon faire l'aller-retour pour savoir de quelle étape on
          parle. Vérifié dans la base v1 : les six incidents y sont tous portés
          par une étape. */}
      {litige !== null && edition === null && (
        <div className="bandeau-litige">
          <span className="motif">{etape.motif_litige ?? 'Litige'}</span>
          {etape.litige_note !== null && <span>{etape.litige_note}</span>}
          {etape.litige_resolution !== null && (
            <span className="sourdine">{etape.litige_resolution}</span>
          )}
          <span className="ou">{litige}</span>
          {/* RÉSOUDRE DEPUIS LA CARTE, ajouté le 07/09/2026 quand le litige a
              quitté le formulaire de l'étape. Sans ce bouton, il faudrait
              passer par l'écran Litiges pour clore ce qu'on vient d'ouvrir. */}
          {etape.litige_id !== null && etape.litige_resolu_le === null && (
            <button
              className="lien-action"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                quandResoudre(etape.litige_id as string);
              }}
            >
              Résoudre
            </button>
          )}
        </div>
      )}

      {edition !== null && (
        <div className="pied-edition">
          <button
            className="bouton"
            type="button"
            disabled={edition.envoi}
            onClick={quandEnregistrer}
          >
            {edition.envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={edition.envoi}
            onClick={quandAnnuler}
          >
            Annuler
          </button>
          {edition.confirme ? (
            <button
              className="bouton bouton-risque a-droite"
              type="button"
              disabled={edition.envoi}
              onClick={quandSupprimer}
            >
              Supprimer définitivement
            </button>
          ) : (
            <button
              className="bouton bouton-risque a-droite"
              type="button"
              disabled={edition.envoi}
              onClick={quandDemanderSuppression}
            >
              Supprimer l’étape
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

export interface ActionsOrdre {
  readonly confirmeAnnulation: boolean;
  readonly confirmeSuppression: boolean;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandAnnulerOrdre: () => void;
  readonly quandRouvrirOrdre: () => void;
  readonly quandSupprimerOrdre: () => void;
  readonly quandDemanderAnnulation: () => void;
  readonly quandDemanderSuppression: () => void;
  readonly quandRenoncer: () => void;
  /**
   * ENVOYER L'ORDRE EN FACTURATION, ou l'en retirer.
   *
   * Vision de l'utilisateur, 09/09/2026 : « un OT terminé et qui comporte les
   * documents nécessaires est envoyé en facturation via le menu ordres de
   * transport ». C'est un GESTE, pas une déduction — « terminé » se déduit des
   * étapes, « envoyé en facturation » se décide.
   *
   * Le retrait existe parce qu'un envoi qu'on ne peut pas défaire est un envoi
   * qu'on n'ose plus faire. Il n'est offert que tant qu'aucune facture ne porte
   * l'ordre.
   */
  readonly quandEnvoyerEnFacturation: () => void;
  readonly quandRetirerDeLaFacturation: () => void;
}

// ---------------------------------------------------------------------------
// L'onglet Documents
// ---------------------------------------------------------------------------

/**
 * Les pièces jointes d'un ordre.
 *
 * Une ligne par pièce, et rien de plus : un document n'a pas de fiche à lui.
 * L'état `en_envoi` se voit — c'est une pièce dont le fichier n'est jamais
 * arrivé, et il vaut mieux la montrer que la cacher.
 */
export function OngletDocuments({
  documents,
  types,
  ajout,
  envoi,
  refus,
  quandOuvrirAjout,
  quandChampAjout,
  quandFichier,
  quandJoindre,
  quandFermerAjout,
  quandOuvrirDocument,
  quandSupprimerDocument,
}: {
  readonly documents: readonly DocumentJoint[];
  readonly types: readonly LigneReglee[];
  readonly ajout: AjoutDocument | null;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandOuvrirAjout: () => void;
  readonly quandChampAjout: (typeId: string) => void;
  readonly quandFichier: (fichier: File | null) => void;
  readonly quandJoindre: () => void;
  readonly quandFermerAjout: () => void;
  readonly quandOuvrirDocument: (document: DocumentJoint) => void;
  readonly quandSupprimerDocument: (document: DocumentJoint) => void;
}) {
  const total = documents.reduce((somme, d) => somme + Number(d.poids ?? 0), 0);

  return (
    <div className="documents-ot">
      <div className="barre-documents">
        <span className="compte-documents">
          {documents.length === 0
            ? 'Aucun document'
            : `${documents.length} document${documents.length > 1 ? 's' : ''}${
                total > 0 ? ` · ${poidsDit(total) ?? ''}` : ''
              }`}
        </span>
        <button
          className="bouton"
          type="button"
          disabled={envoi}
          onClick={quandOuvrirAjout}
        >
          Ajouter un document
        </button>
      </div>

      {refus !== null && ajout === null && <p className="bandeau-refus">{refus}</p>}

      {documents.length === 0 ? (
        <p className="rien">
          Aucun document joint à cet ordre. Commande client, lettre de voiture, bon de
          livraison signé, photo…
        </p>
      ) : (
        <table className="table-documents">
          <thead>
            <tr>
              <th>Type</th>
              <th>Fichier</th>
              <th>Poids</th>
              <th>Joint le</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id}>
                <td>
                  {document.type === null ? (
                    <span className="sourdine">sans type</span>
                  ) : (
                    <span className="type-document">{document.type}</span>
                  )}
                </td>
                <td>
                  {document.nom}
                  {document.etat === 'en_envoi' && (
                    <span className="statut attente"> envoi inachevé</span>
                  )}
                </td>
                <td className="code">{poidsDit(document.poids) ?? '—'}</td>
                <td className="code">
                  {enFrancais(document.cree_le.slice(0, 10)) ?? ''}
                  {document.ajoute_par_nom === null
                    ? ''
                    : ` · ${document.ajoute_par_nom}`}
                </td>
                <td className="actions-document">
                  {document.etat === 'joint' && (
                    <button
                      className="lien-action"
                      type="button"
                      disabled={envoi}
                      onClick={() => quandOuvrirDocument(document)}
                    >
                      Ouvrir
                    </button>
                  )}
                  <button
                    className="lien-action risque"
                    type="button"
                    disabled={envoi}
                    onClick={() => quandSupprimerDocument(document)}
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {ajout !== null && (
        <div className="voile-boite" role="presentation">
          <div className="boite-decrochage" role="dialog" aria-modal="true">
            <h3>Ajouter un document</h3>
            <form
              onSubmit={(evenement) => {
                evenement.preventDefault();
                quandJoindre();
              }}
            >
              <label className="etiquette-boite" htmlFor="document-type">
                Type de document
              </label>
              <select
                id="document-type"
                className="champ-boite"
                value={ajout.typeId}
                disabled={envoi}
                onChange={(evenement) => quandChampAjout(evenement.target.value)}
              >
                <option value="">Sans type</option>
                {types
                  .filter((t) => t.actif)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nom}
                    </option>
                  ))}
              </select>

              <label className="etiquette-boite" htmlFor="document-fichier">
                Fichier
              </label>
              <input
                id="document-fichier"
                className="champ-boite"
                type="file"
                disabled={envoi}
                onChange={(evenement) =>
                  quandFichier(evenement.target.files?.[0] ?? null)
                }
              />
              {/* PRENDRE UNE PHOTO — lot 133 : le même champ, qui ouvre
                  l'appareil photo du téléphone. Caché sur ordinateur. */}
              <label className="bouton bouton-discret photo-tel">
                Prendre une photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  disabled={envoi}
                  onChange={(evenement) =>
                    quandFichier(evenement.target.files?.[0] ?? null)
                  }
                />
              </label>
              <p className="aide-boite">20 Mo au plus par fichier.</p>
              {refus !== null && <p className="faute-boite">{refus}</p>}

              <div className="pied-boite">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={quandFermerAjout}
                >
                  Annuler
                </button>
                <button
                  className="bouton"
                  type="submit"
                  disabled={envoi || ajout.fichier === null}
                >
                  {envoi ? 'Envoi en cours…' : 'Joindre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/** Ce qu'on est en train de joindre, s'il y a quelque chose. */
export interface AjoutDocument {
  readonly typeId: string;
  readonly fichier: File | null;
}

export interface ProprietesVueOrdres {
  /**
   * LE JETON, DESCENDU JUSQU'ICI LE 23/09/2026 pour la confirmation de prise
   * en charge : elle lit sa propre route, à part de la fiche, pour ne pas
   * alourdir `GET /ordres-transport/:id` d'un document que presque personne
   * n'ouvre.
   */
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /** Les supports réglés par la société — voir `FormulaireEtape`. */
  readonly supports: readonly LigneReglee[];
  readonly documents: readonly DocumentJoint[];
  readonly typesDocument: readonly LigneReglee[];
  readonly ajoutDocument: AjoutDocument | null;
  readonly envoiDocument: boolean;
  readonly refusDocument: string | null;
  readonly quandOuvrirAjout: () => void;
  readonly quandChampAjout: (typeId: string) => void;
  readonly quandFichier: (fichier: File | null) => void;
  readonly quandJoindre: () => void;
  readonly quandFermerAjout: () => void;
  readonly quandOuvrirDocument: (document: DocumentJoint) => void;
  readonly quandSupprimerDocument: (document: DocumentJoint) => void;
  readonly vue: 'liste' | 'fiche' | 'creation';
  readonly liste: EtatListe;
  readonly fiche: EtatFiche;
  readonly onglet: Onglet;
  readonly fenetre: { readonly du: string; readonly au: string };
  /** Combien d'ordres la fenêtre de dates écarte — 0 quand elle n'en cache aucun. */
  readonly horsPeriode: number;
  readonly quandToutesLesDates: () => void;
  /**
   * COMBIEN L'API EN A TROUVÉ, avant sa coupe à 500. Égal à la longueur de la
   * liste tant que le plafond n'a pas mordu : c'est à ce moment-là, et à ce
   * moment-là seulement, que l'écran a quelque chose à dire.
   */
  readonly trouves: number;
  /**
   * LE TERME D'UNE RECHERCHE ÉTENDUE EN COURS, ou `null` pour la liste
   * ordinaire. Quand il est posé, la liste affichée ne vient plus de la fenêtre
   * de dates mais d'une question posée à toute la base, et l'écran doit le dire
   * — sinon on croirait que la fenêtre s'est élargie toute seule.
   */
  readonly etendue: string | null;
  readonly quandChercherPartout: () => void;
  readonly quandRevenirALaListe: () => void;
  readonly filtres: Filtres;
  readonly tri: Tri;
  readonly panneauOuvert: boolean;
  readonly recherche: string;
  readonly clients: readonly LigneListe[];
  readonly centres: readonly LigneReglee[];
  readonly sites: readonly LigneListe[];
  readonly gabarits: readonly Vehicule[];
  readonly motifs: readonly MotifLitige[];
  /** Les trois parcs, pour poser un équipage sur une étape. */
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly editionOrdre: EditionOrdre | null;
  readonly editionEtape: EditionEtape | null;
  readonly creation: EtatCreation | null;
  readonly actions: ActionsOrdre;
  readonly quandFenetre: (bornes: { readonly du: string; readonly au: string }) => void;
  readonly quandFiltres: (filtres: Filtres) => void;
  readonly quandTri: (colonne: ColonneTri) => void;
  readonly quandPanneau: (ouvert: boolean) => void;
  readonly quandRecherche: (texte: string) => void;
  readonly quandOuvrir: (id: string) => void;
  readonly quandRetour: () => void;
  /**
   * CE QUE DIT LE BOUTON DE RETOUR — 14/09/2026.
   *
   * « Les OT » quand on est arrivé par Exploitation. Le nom de l'écran
   * d'origine quand on vient d'ailleurs : « À facturer », « Litiges »,
   * « Planning », « Affrètement ».
   *
   * Demande de Mohamed : « en cas de retour on revient dans la page à facturer
   * et pas sur la liste de tous les OT ». Le bouton ne mentait pas — il
   * ramenait bien aux OT — mais il ramenait au mauvais endroit. Le mot suit le
   * geste, sans quoi on aurait un bouton qui dit « Les OT » et qui va ailleurs.
   */
  readonly libelleRetour: string;
  readonly quandOnglet: (onglet: Onglet) => void;
  readonly quandModifierOrdre: () => void;
  /**
   * Ouvrir un NOUVEL ordre pré-rempli depuis celui-ci. Rien n'est écrit : voir
   * `ordreDuplique` dans `ot.ts`.
   */
  readonly quandDupliquerOrdre: () => void;
  readonly quandChampOrdre: (cle: keyof SaisieOrdre, valeur: string) => void;
  readonly quandEnregistrerOrdre: () => void;
  readonly quandAnnulerEdition: () => void;
  readonly quandModifierEtape: (id: string) => void;
  readonly quandBasculerFait: (id: string) => void;
  readonly quandLitige: (id: string) => void;
  /** Résoudre le litige ouvert d'une étape, depuis son bandeau. */
  readonly quandResoudre: (litigeId: string) => void;
  /** La boîte de résolution ouverte, ou `null`. */
  readonly resolution: {
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null;
  readonly quandChampResolution: (
    cle: 'texte' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  readonly quandEnregistrerResolution: () => void;
  readonly quandEchange: (id: string) => void;
  /** Le panneau d'affrètement : ouvrir, saisir, enregistrer, rendre. */
  readonly affretement: SaisieAffretement | null;
  readonly proposes: readonly TransporteurTrouve[];
  /** L'annuaire ENTIER des transporteurs, chargé en même temps que la recherche. */
  readonly tousTransporteurs: readonly LigneListe[];
  readonly chercheEnCours: boolean;
  readonly quandAffreter: () => void;
  readonly quandNouveauTransporteur: () => void;
  /** La fiche en cours de création par-dessus le panneau, ou `null`. */
  readonly nouveauTransporteur: {
    readonly saisie: Saisie;
    readonly fautes: Readonly<Record<string, string>>;
    readonly refus: string | null;
  } | null;
  readonly quandChampNouveauTransporteur: (cle: string, valeur: string) => void;
  readonly quandNomQuitteNouveauTransporteur: () => void;
  readonly quandEnregistrerNouveauTransporteur: () => void;
  readonly quandAnnulerNouveauTransporteur: () => void;
  readonly quandNouveauClient: () => void;
  /**
   * La fiche CLIENT en cours de création, ou `null`.
   *
   * Elle vit au niveau de `VueOrdres` et non dans une des trois vues : le
   * bouton qui l'ouvre existe à la fois sur la fiche d'un ordre et dans la
   * section de création. Une boîte par vue en ferait deux à tenir d'accord.
   */
  readonly nouveauClient: {
    readonly saisie: Saisie;
    readonly fautes: Readonly<Record<string, string>>;
    readonly refus: string | null;
  } | null;
  readonly quandChampNouveauClient: (cle: string, valeur: string) => void;
  readonly quandNomQuitteNouveauClient: () => void;
  readonly quandEnregistrerNouveauClient: () => void;
  readonly quandAnnulerNouveauClient: () => void;
  readonly quandChampAffretement: (
    cle: keyof SaisieAffretement,
    valeur: string,
  ) => void;
  readonly quandEnregistrerAffretement: () => void;
  readonly quandAnnulerAffretement: () => void;
  /** La boîte ouverte, ou `null`. Une seule des trois à la fois. */
  readonly validation: SaisieValidation | null;
  readonly litige: SaisieLitige | null;
  /** L'échange en cours de saisie, avec l'étape qu'il concerne. */
  readonly echange: { readonly etapeId: string; readonly saisie: SaisieEchange } | null;
  readonly quandChampValidation: (
    cle: 'jour' | 'heureArrivee' | 'heureDepart',
    valeur: string,
  ) => void;
  /** Le temps passé, saisi dans la boîte « Valider » de la dernière étape. */
  readonly tempsPasse: string;
  readonly quandTemps: (valeur: string) => void;
  /** Le coût de revient de l'ordre ouvert, ou `null` tant qu'il n'est pas lu. */
  readonly cout: CoutOrdre | null;
  readonly quandRecalculerLeCout: () => void;
  /** Les prestations annexes de l'ordre ouvert, `null` tant qu'elles se lisent. */
  readonly prestations: PrestationsDeLOrdre | null;
  /** La liste paramétrable des natures, pour le menu déroulant de la boîte. */
  readonly naturesPrestation: readonly LigneReglee[];
  /** La prestation en cours de saisie, ou `null` — la boîte est alors fermée. */
  readonly saisiePrestation: SaisiePrestation | null;
  readonly quandOuvrirPrestation: (prestation: PrestationAnnexe | null) => void;
  readonly quandRetirerPrestation: (prestationId: string) => void;
  readonly quandChampPrestation: (
    cle: 'natureId' | 'detail' | 'quantite' | 'prixUnitaire',
    valeur: string,
  ) => void;
  readonly quandEnregistrerPrestation: () => void;
  readonly quandFermerBoite: () => void;
  readonly quandConfirmerValidation: () => void;
  readonly quandChampLitige: (
    cle: 'motifId' | 'note' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  readonly quandNouveauMotif: (nom: string | null) => void;
  readonly quandOuvrirLitige: () => void;
  readonly quandChampEchange: (cle: keyof SaisieEchange, valeur: string) => void;
  readonly quandEnregistrerEchange: () => void;
  readonly quandEffacerEchange: () => void;
  /** Décale l'étape du rang donné de `pas` places. Renumérote tout l'ordre. */
  readonly quandDeplacerEtape: (rang: number, pas: -1 | 1) => void;
  readonly quandAjouterEtape: (type: TypeEtape) => void;
  readonly quandChampEtape: (cle: keyof SaisieEtape, valeur: string | boolean) => void;
  readonly quandSiteEtape: (id: string) => void;
  readonly quandOuvrirSite: () => void;
  readonly quandChampSite: (cle: keyof SaisieSite, valeur: string) => void;
  readonly quandCreerSite: () => void;
  readonly quandFermerSite: () => void;
  readonly quandEnregistrerEtape: () => void;
  readonly quandAnnulerEtape: () => void;
  readonly quandDemanderSuppressionEtape: () => void;
  readonly quandSupprimerEtape: () => void;
  readonly quandCreer: () => void;
  readonly quandChampEtapeCreation: (
    rang: number,
    cle: keyof SaisieEtape,
    valeur: string | boolean,
  ) => void;
  readonly quandSiteCreation: (rang: number, id: string) => void;
  readonly quandOuvrirSiteCreation: (rang: number) => void;
  readonly quandChampSiteCreation: (
    rang: number,
    cle: keyof SaisieSite,
    valeur: string,
  ) => void;
  readonly quandCreerSiteCreation: (rang: number) => void;
  readonly quandFermerSiteCreation: (rang: number) => void;
  readonly quandAjouterEtapeCreation: (type: TypeEtape) => void;
  readonly quandRetirerEtapeCreation: (rang: number) => void;
  /** À la création, les étapes n'existent pas en base : on déplace la liste. */
  readonly quandDeplacerEtapeCreation: (rang: number, pas: -1 | 1) => void;
  readonly quandEnregistrerCreation: () => void;
  /**
   * LE RELAIS — F3, 31/10/2026. Coché, chaque étape choisit son véhicule ;
   * décoché, le véhicule de la première étape est reporté sur toutes.
   */
  readonly quandRelaisCreation: (actif: boolean) => void;
  /**
   * L'INDEXATION DE LA SAISIE EN COURS — F3. `null` tant que le client n'est
   * pas choisi, ou que l'aperçu n'a pas répondu.
   */
  readonly indexationCreation?: IndexationLue | null;
  readonly quandAjouterPrestationCreation: () => void;
  readonly quandChampPrestationCreation: (
    rang: number,
    cle: keyof PrestationNeuve,
    valeur: string,
  ) => void;
  readonly quandRetirerPrestationCreation: (rang: number) => void;
  readonly quandAjouterDocumentCreation: (ajout: AjoutDocument) => void;
  readonly quandRetirerDocumentCreation: (rang: number) => void;
  readonly quandAffreterCreation: (oui: boolean) => void;
  readonly quandAjouterMarchandiseCreation: () => void;
  readonly quandChampMarchandiseCreation: (
    rang: number,
    changement: Partial<LigneMarchandise>,
  ) => void;
  readonly quandSupportMarchandiseCreation: (rang: number, nom: string) => void;
  readonly quandRetirerMarchandiseCreation: (rang: number) => void;
  /**
   * LE PACKAGING D'UN ORDRE EXISTANT, EN MODIFICATION — F4. `null` : on le
   * lit. Sinon, les lignes en cours de saisie.
   */
  readonly editionPackaging?: readonly LigneMarchandise[] | null;
  readonly quandModifierPackaging: () => void;
  readonly quandAjouterPackaging: () => void;
  readonly quandChampPackaging: (rang: number, changement: Partial<LigneMarchandise>) => void;
  readonly quandSupportPackaging: (rang: number, nom: string) => void;
  readonly quandRetirerPackaging: (rang: number) => void;
  readonly quandEnregistrerPackaging: () => void;
  readonly quandAnnulerPackaging: () => void;
  /**
   * LA LECTURE D'UNE CONFIRMATION — lot 124, 26/10/2026.
   *
   * L'écran ne lit rien lui-même : il reçoit où en est la lecture, et il
   * rend un fichier quand on en dépose un. Tout le reste — l'encodage,
   * l'appel, le raccordement — vit ailleurs et se teste sans navigateur.
   */
  readonly lecture: EtatLecture;
  readonly quandLireConfirmation: (fichier: File | null) => void;
  /** Range le bandeau de la lecture, sans toucher à la saisie en cours. */
  readonly quandOublierLecture: () => void;
  /**
   * LES CONFIRMATIONS À CRÉER — lot 147, 02/11/2026. Facultatives : l'aperçu
   * et les anciens appels gardent la liste seule.
   */
  readonly ongletListe?: 'ordres' | 'a_creer';
  readonly aCreer?: EtatACreer;
  readonly quandOngletListe?: (o: 'ordres' | 'a_creer') => void;
  readonly quandDeposer?: (fichiers: readonly File[]) => void;
  readonly quandVerifier?: (c: ConfirmationACreer) => void;
  readonly quandEcarter?: (c: ConfirmationACreer) => void;
  readonly quandRelire?: (c: ConfirmationACreer) => void;
}

/** Où en est la liste des confirmations à créer. */
export interface EtatACreer {
  readonly liste: readonly ConfirmationACreer[] | null;
  /** Combien de fichiers partent en ce moment. */
  readonly envois: number;
  readonly refus: string | null;
}

/** Le dessin, et rien d'autre : aucun appel réseau, aucun état gardé. */
export function VueOrdres(p: ProprietesVueOrdres) {
  const contenu =
    p.vue === 'creation' && p.creation !== null ? (
      <VueCreation p={p} creation={p.creation} />
    ) : p.vue === 'fiche' ? (
      <VueFiche p={p} />
    ) : (
      <VueListe p={p} />
    );

  /**
   * LA BOÎTE DU CLIENT EST ICI, au-dessus des trois vues.
   *
   * Le bouton « + Créer un client » vit dans `FormulaireOrdre`, qui sert à la
   * fois à la modification d'un ordre et à sa création. La poser dans chacune
   * des deux vues ferait deux boîtes à tenir d'accord ; ici, il n'y en a
   * qu'une, et elle s'affiche par-dessus quoi que ce soit qui soit ouvert.
   */
  return (
    <>
      {contenu}
      {p.nouveauClient !== null && (
        <BoiteNouvelleFiche
          annuaire={ANNUAIRE_CLIENTS}
          saisie={p.nouveauClient.saisie}
          fautes={p.nouveauClient.fautes}
          refus={p.nouveauClient.refus ?? p.actions.refus}
          envoi={p.actions.envoi}
          quandChamp={p.quandChampNouveauClient}
          quandNomQuitte={p.quandNomQuitteNouveauClient}
          quandEnregistrer={p.quandEnregistrerNouveauClient}
          quandAnnuler={p.quandAnnulerNouveauClient}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function VueListe({ p }: { readonly p: ProprietesVueOrdres }) {
  const ordres = p.liste.nom === 'prete' ? ranger(p.liste.ordres) : [];
  /**
   * TROIS PASSES, DANS CET ORDRE : la recherche libre, les filtres, le tri.
   *
   * La recherche d'abord parce qu'elle est la plus grossière ; le tri en
   * dernier parce qu'il porte sur ce qui reste. `retenus` sert au décompte
   * « 3 sur 6 » : c'est le total AVANT filtres mais APRÈS recherche, sinon le
   * chiffre parlerait d'ordres que la recherche a déjà écartés.
   */
  const cherches = filtrer(ordres, p.recherche);
  const visibles = trier(appliquerFiltres(cherches, p.filtres), p.tri);
  const combienDeFiltres = compteFiltres(p.filtres);

  /**
   * CE QUE LA LISTE NE MONTRE PAS — sur `ordres`, et non sur `visibles` : le
   * plafond est une coupe de l'API, pas un effet des filtres de l'écran. Les
   * comparer aux visibles ferait parler le bandeau à chaque filtre posé.
   */
  const cache = ceQueLaListeCache({
    etendue: p.etendue,
    recherche: p.recherche,
    affiches: ordres.length,
    trouves: p.trouves,
  });
  const plusLoinSert = chercherPlusLoinSert({
    etendue: p.etendue,
    affiches: ordres.length,
    trouves: p.trouves,
    horsPeriode: p.horsPeriode,
  });

  /** Les cases d'un critère : figées pour certains, déduites de la liste pour
      les clients et les activités, qui changent d'une société à l'autre. */
  const choixDe = (cle: string) =>
    CRITERES_FILTRE.find((c) => c.cle === cle)?.choix ?? choixDeduits(cherches, cle);

  const etiquettes = etiquettesDeFiltre(p.filtres, choixDe);

  return (
    <div className="corps">
      {/* LE TITRE DE LA LISTE — maquette « Liste-ordres » validée le 31/10. */}
      <h1 className="titre-page">Ordres de transport</h1>
      <div className="ac-pile">
      {p.aCreer !== undefined && p.quandOngletListe !== undefined && (
        <div className="ac-onglets" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={p.ongletListe !== 'a_creer'}
            className={p.ongletListe !== 'a_creer' ? 'ac-onglet actif' : 'ac-onglet'}
            onClick={() => p.quandOngletListe?.('ordres')}
          >
            Ordres
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={p.ongletListe === 'a_creer'}
            className={p.ongletListe === 'a_creer' ? 'ac-onglet actif' : 'ac-onglet'}
            onClick={() => p.quandOngletListe?.('a_creer')}
          >
            À créer
            {p.aCreer.liste !== null && nombreAVerifier(p.aCreer.liste) > 0 && (
              <span className="ac-compte">{nombreAVerifier(p.aCreer.liste)}</span>
            )}
          </button>
        </div>
      )}
      {p.ongletListe === 'a_creer' && p.aCreer !== undefined ? (
        <VueACreer p={p} etat={p.aCreer} />
      ) : (
      <section className="liste">
        <div className="barre-outils">
          <input
            className="recherche"
            type="search"
            placeholder="N° d’OT, client, référence, ville…"
            aria-label="Rechercher un ordre de transport"
            value={p.recherche}
            onChange={(e) => p.quandRecherche(e.target.value)}
          />
          {/* Les mêmes classes que la barre de l'écran Affectation : `champ-date`
              tient l'étiquette et son champ ensemble, et `champ` donne au champ
              la taille des autres. */}
          <label className="champ-date">
            <span className="etiquette">Du</span>
            <input
              className="champ"
              type="date"
              value={p.fenetre.du}
              onChange={(e) => p.quandFenetre({ ...p.fenetre, du: e.target.value })}
            />
          </label>
          <label className="champ-date">
            <span className="etiquette">au</span>
            <input
              className="champ"
              type="date"
              value={p.fenetre.au}
              onChange={(e) => p.quandFenetre({ ...p.fenetre, au: e.target.value })}
            />
          </label>
          {/* LE DÉROULANT « STATUT » A DISPARU D'ICI le 10/09/2026 : il est
              devenu un critère du panneau. Deux endroits filtrant la même
              chose, on ne savait plus lequel gagnait. */}
          <button
            className="bouton bouton-discret"
            type="button"
            aria-expanded={p.panneauOuvert}
            onClick={() => p.quandPanneau(!p.panneauOuvert)}
          >
            Filtrer
            {combienDeFiltres > 0 && (
              <span className="compte-filtres"> · {combienDeFiltres}</span>
            )}
          </button>
          {/* `a-droite` pousse le bouton au bout de la barre. La barre PASSE À
              LA LIGNE sous 420 px (mesuré le 04/09) : le bouton se retrouve
              alors seul sur sa ligne, à droite, ce qui reste juste. */}
          <div className="a-droite">
            {/*
              LIRE UNE CONFIRMATION — lot 124.

              UN `label` ET NON UN `button`, et ce n'est pas une coquetterie :
              un bouton qui déclenche un champ de fichier caché demande du
              JavaScript et perd le clavier. Un `label` lié au champ ouvre le
              sélecteur nativement, se tabule, et s'annonce aux lecteurs
              d'écran — pour le prix d'une classe de style.

              LE CHAMP SE VIDE À CHAQUE OUVERTURE (`value=''` au clic) : sans
              cela, redéposer DEUX FOIS LE MÊME FICHIER ne déclenche rien, le
              navigateur estimant que la valeur n'a pas changé. C'est un défaut
              qu'on ne voit qu'en réessayant après une erreur — donc au pire
              moment.
            */}
            <label
              className={
                p.lecture.quoi === 'en_cours'
                  ? 'bouton bouton-discret bouton-attente'
                  : 'bouton bouton-discret'
              }
              htmlFor="confirmation-fichier"
            >
              {p.lecture.quoi === 'en_cours' ? 'Lecture en cours…' : 'Lire une confirmation'}
            </label>
            <input
              id="confirmation-fichier"
              className="fichier-cache"
              type="file"
              accept="application/pdf,.pdf"
              disabled={p.lecture.quoi === 'en_cours'}
              onClick={(evenement) => {
                (evenement.target as HTMLInputElement).value = '';
              }}
              onChange={(evenement) =>
                p.quandLireConfirmation(evenement.target.files?.[0] ?? null)
              }
            />
            <button className="bouton bouton-nouvel-ordre" type="button" onClick={p.quandCreer}>
              + Nouvel ordre
            </button>
          </div>
        </div>

        {p.lecture.quoi === 'refus' && (
          /*
            LE REFUS SE DIT ICI, sur la liste, parce qu'on n'a pas bougé :
            aucune saisie ne s'est ouverte. Le montrer dans un écran de
            création qui n'existe pas l'aurait rendu invisible.

            CE N'EST PAS FORCÉMENT UNE PANNE — la clé peut manquer, le modèle
            peut être lent, le document peut ne pas être une confirmation. On
            dit ce qui s'est passé, et la saisie à la main reste à un clic.
          */
          <p className="bandeau-refus">
            {p.lecture.message}{' '}
            <button
              className="bouton bouton-discret"
              type="button"
              onClick={p.quandOublierLecture}
            >
              Fermer
            </button>
          </p>
        )}

        {p.panneauOuvert && (
          <div className="panneau-filtres">
            <div className="grille-criteres">
              {CRITERES_FILTRE.map((critere) => (
                <fieldset className="critere" key={critere.cle}>
                  <legend className="critere-nom">{critere.nom}</legend>
                  {choixDe(critere.cle).map((choix, rang) => (
                    <Fragment key={choix.valeur}>
                      {/* « OU » ENTRE LES CASES, écrit une fois entre les deux
                          premières : c'est ce qui apprend la règle sans
                          alourdir les listes longues. */}
                      {rang === 1 && <p className="ou-filtre">OU</p>}
                      <label>
                        <input
                          type="checkbox"
                          checked={cochesDe(p.filtres, critere.cle).includes(choix.valeur)}
                          onChange={() =>
                            p.quandFiltres(basculer(p.filtres, critere.cle, choix.valeur))
                          }
                        />
                        {choix.texte}
                      </label>
                    </Fragment>
                  ))}
                  {choixDe(critere.cle).length === 0 && (
                    <p className="empechement">Aucune valeur sur cette période.</p>
                  )}
                </fieldset>
              ))}

              <fieldset className="critere">
                <legend className="critere-nom">Vente HT</legend>
                <div className="bornes-filtre">
                  <span>de</span>
                  <input
                    className="champ"
                    type="text"
                    inputMode="decimal"
                    aria-label="Vente HT minimale"
                    value={p.filtres.venteMin}
                    onChange={(e) => p.quandFiltres({ ...p.filtres, venteMin: e.target.value })}
                  />
                  <span>à</span>
                  <input
                    className="champ"
                    type="text"
                    inputMode="decimal"
                    aria-label="Vente HT maximale"
                    value={p.filtres.venteMax}
                    onChange={(e) => p.quandFiltres({ ...p.filtres, venteMax: e.target.value })}
                  />
                </div>
              </fieldset>

              <fieldset className="critere">
                <legend className="critere-nom">Axe</legend>
                <div className="bornes-filtre">
                  <input
                    className="champ"
                    type="text"
                    placeholder="FR45"
                    aria-label="Zone de départ"
                    value={p.filtres.zoneDepart}
                    onChange={(e) => p.quandFiltres({ ...p.filtres, zoneDepart: e.target.value })}
                  />
                  <span aria-hidden="true">→</span>
                  <input
                    className="champ"
                    type="text"
                    placeholder="FR76"
                    aria-label="Zone d’arrivée"
                    value={p.filtres.zoneArrivee}
                    onChange={(e) => p.quandFiltres({ ...p.filtres, zoneArrivee: e.target.value })}
                  />
                </div>
              </fieldset>
            </div>

            <div className="pied-filtres">
              <button
                className="bouton bouton-discret"
                type="button"
                disabled={combienDeFiltres === 0}
                onClick={() => p.quandFiltres(AUCUN_FILTRE)}
              >
                Tout décocher
              </button>
              {/* PAS DE BOUTON « APPLIQUER » : le tableau suit la case cochée.
                  La maquette en dessinait un ; à l'écrire, il n'avait aucun
                  travail — le filtrage est instantané et rien n'attend. Un
                  bouton qui ne fait rien apprend à ne pas lire les boutons. */}
              {/* MÊME RÈGLE QUE LE DÉCOMPTE : « 6 ordres sur 6 » n'apprend
                  rien et se lit comme un filtre posé. On ne parle que quand
                  quelque chose est écarté. */}
              <p className="empechement">
                {visibles.length} ordre{visibles.length > 1 ? 's' : ''}
                {visibles.length !== cherches.length && <> sur {cherches.length}</>}
              </p>
            </div>
          </div>
        )}

        {etiquettes.length > 0 && (
          <div className="filtres-actifs">
            {etiquettes.map((e, rang) => (
              <Fragment key={e.cle}>
                {rang > 0 && <span className="et-filtre">ET</span>}
                <span className="puce-filtre">
                  <b>{e.nom} :</b> {e.valeurs}
                  <button
                    className="oter-filtre"
                    type="button"
                    aria-label={`Retirer le filtre ${e.nom}`}
                    onClick={() => p.quandFiltres(oterFiltre(p.filtres, e.cle))}
                  >
                    ✕
                  </button>
                </span>
              </Fragment>
            ))}
            <button
              className="tout-effacer"
              type="button"
              onClick={() => p.quandFiltres(AUCUN_FILTRE)}
            >
              Tout effacer
            </button>
          </div>
        )}

        {/* LA RECHERCHE ÉTENDUE, ajoutée le 26/10/2026.
            La liste affichée ne vient plus de la fenêtre de dates : elle
            répond à une question posée à toute la base. L'écran le dit, ET dit
            SUR QUOI la question a porté — sans ça, on croirait qu'elle couvre
            les villes et les sites comme la barre de recherche, et on
            conclurait à tort qu'un ordre n'existe pas. */}
        {cache.nom === 'etendue' && (
          <p className="bandeau bandeau-attente">
            Cherché dans <b>toute la base</b> : « {cache.terme} », sur le numéro,
            la référence client et le nom du client — la période est ignorée.
            {cache.trouves > cache.affiches && (
              <>
                {' '}
                Trop de résultats : {cache.affiches} affichés sur {cache.trouves}.
                Précisez le terme.
              </>
            )}{' '}
            <button
              className="lien-action"
              type="button"
              onClick={p.quandRevenirALaListe}
            >
              Revenir à la liste
            </button>
          </p>
        )}

        {/* LE PLAFOND QUI PARLE, ajouté le 26/10/2026.
            La coupe des 500 existait depuis le premier jour et mordait en
            silence : « 500 ordres » se lisait « 500 au total », et un ordre
            plus ancien devenait introuvable — la barre de recherche ne filtrant
            que ce qui était déjà chargé.

            IL NE PARLE QUE QUAND IL A MORDU : `trouves` vaut la longueur de la
            liste le reste du temps, et un avertissement permanent serait un
            avertissement qu'on n'écoute plus. */}
        {/* PAS DEUX FOIS LA MÊME PROPOSITION. Constaté à l'aperçu le
            26/10/2026 : quand la recherche ne rend rien, le bloc du milieu
            offre déjà « Chercher dans toute la base », et ce bandeau le
            répétait trois centimètres plus haut. Le bandeau sert à dire ce
            qu'on ne voit pas DANS une liste ; sans liste, il n'a rien à
            commenter. */}
        {cache.nom === 'plafond' && visibles.length > 0 && (
          <p className="bandeau bandeau-attente">
            Les <b>{cache.affiches}</b> ordres les plus récents sont affichés, sur{' '}
            <b>{cache.trouves}</b> trouvés.{' '}
            {cache.terme === '' ? (
              <>Posez une période, ou tapez un numéro pour chercher plus loin.</>
            ) : (
              <button
                className="lien-action"
                type="button"
                onClick={p.quandChercherPartout}
              >
                Chercher « {cache.terme} » dans toute la base
              </button>
            )}
          </p>
        )}

        {visibles.length > 0 && (
          <p className="decompte">
            {/* « 3 ordres de transport SUR 6 ». Le « sur 6 » n'est pas
                décoratif : sans lui, un filtre oublié fait croire que des
                ordres ont disparu — c'est exactement ce qui s'était passé
                avec le planning début septembre. Il est passé DANS
                `decompte()` le 10/09 : posé après, il s'accrochait au détail
                et donnait « dont 1 planifié sur 6 ». */}
            {decompte(visibles, cherches.length)}
            {p.horsPeriode > 0 && (
              <>
                {' — '}
                <button
                  className="lien-action"
                  type="button"
                  onClick={p.quandToutesLesDates}
                >
                  {p.horsPeriode === 1
                    ? '1 ordre hors de cette période'
                    : `${p.horsPeriode} ordres hors de cette période`}
                </button>
              </>
            )}
          </p>
        )}

        {p.liste.nom === 'chargement' && <p className="attente">Chargement…</p>}

        {p.liste.nom === 'panne' && (
          <div className="vide">
            <p>Stafa ne répond pas. Vérifiez votre connexion et réessayez.</p>
          </div>
        )}

        {p.liste.nom === 'prete' && ordres.length === 0 && (
          <div className="vide">
            <p>
              {p.fenetre.du === '' && p.fenetre.au === ''
                ? 'Aucun ordre de transport.'
                : `Aucun ordre de transport entre le ${
                    enFrancais(p.fenetre.du) ?? p.fenetre.du
                  } et le ${enFrancais(p.fenetre.au) ?? p.fenetre.au}.`}
            </p>
            {/* Ce qui manquait le 04/09/2026 : la liste cachait un ordre daté
                du mois d'avant et n'en disait rien. */}
            {p.horsPeriode > 0 ? (
              <>
                <p className="sourdine">
                  {p.horsPeriode === 1
                    ? '1 ordre est hors de cette période.'
                    : `${p.horsPeriode} ordres sont hors de cette période.`}
                </p>
                <button
                  className="bouton"
                  type="button"
                  onClick={p.quandToutesLesDates}
                >
                  Voir toutes les dates
                </button>
              </>
            ) : (
              <button className="bouton" type="button" onClick={p.quandCreer}>
                Créer un ordre de transport
              </button>
            )}
          </div>
        )}

        {/* DEUX CAUSES POSSIBLES DEPUIS LE 10/09/2026, et le message doit dire
            LAQUELLE. Jusqu'ici il accusait toujours la recherche ; avec les
            filtres, il aurait proposé d'effacer une recherche vide pendant
            qu'un filtre écartait tout. Le vide dit aussi combien d'ordres la
            période contient — « aucun résultat » tout seul laisse croire à une
            panne. */}
        {p.liste.nom === 'prete' && ordres.length > 0 && visibles.length === 0 && (
          <div className="vide">
            {cherches.length === 0 ? (
              <>
                <p>Aucun ordre ne correspond à « {p.recherche.trim()} ».</p>
                {/* « RIEN ICI » N'EST PAS « RIEN DU TOUT », 26/10/2026.
                    La barre de recherche ne filtre que ce qui est chargé : les
                    500 plus récents, bornés par la fenêtre de dates. Un ordre
                    au-delà n'y est pas, et l'écran disait « aucun ordre » sans
                    distinguer les deux cas.

                    LE BOUTON NE PARAÎT QUE S'IL A QUELQUE CHOSE À CHERCHER :
                    quand le plafond a mordu, ou quand la fenêtre écarte des
                    ordres. Si toute la base est déjà à l'écran, « aucun ordre »
                    veut vraiment dire aucun, et proposer d'aller voir ailleurs
                    serait une fausse piste. */}
                {plusLoinSert && (
                  <>
                    <p className="sourdine">
                      La liste n’en montre qu’une partie. Vous pouvez chercher ce
                      numéro, cette référence ou ce client dans toute la base.
                    </p>
                    <button
                      className="bouton"
                      type="button"
                      onClick={p.quandChercherPartout}
                    >
                      Chercher dans toute la base
                    </button>
                  </>
                )}
                <button
                  className="bouton bouton-discret"
                  type="button"
                  onClick={() => p.quandRecherche('')}
                >
                  Effacer la recherche
                </button>
              </>
            ) : (
              <>
                <p>Aucun ordre ne correspond à ces filtres.</p>
                <p className="sourdine">
                  {cherches.length === 1
                    ? '1 ordre sur cette période.'
                    : `${cherches.length} ordres sur cette période.`}
                </p>
                <button
                  className="bouton bouton-discret"
                  type="button"
                  onClick={() => p.quandFiltres(AUCUN_FILTRE)}
                >
                  Effacer les filtres
                </button>
              </>
            )}
          </div>
        )}

        {visibles.length > 0 && (
          <div className="tableau-cadre">
            <table>
              <thead>
                {/* CHAQUE EN-TÊTE EST UN BOUTON, pas un `onClick` sur le `th` :
                    sans cela le tri est inatteignable au clavier et muet pour
                    un lecteur d'écran. `aria-sort` dit lequel porte le tri.

                    LA FLÈCHE N'APPARAÎT QUE SUR LA COLONNE ACTIVE — huit
                    flèches grises en permanence feraient un en-tête bruyant. */}
                <tr>
                  <th
                    scope="col"
                    className={p.tri.colonne === 'numero' ? 'triee' : undefined}
                    aria-sort={
                      p.tri.colonne === 'numero'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('numero')}>
                      N° d’OT
                      {p.tri.colonne === 'numero' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th
                    scope="col"
                    className={`col-souple ${p.tri.colonne === 'client' ? 'triee' : ''}`.trim()}
                    aria-sort={
                      p.tri.colonne === 'client'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('client')}>
                      Client
                      {p.tri.colonne === 'client' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th
                    scope="col"
                    className={p.tri.colonne === 'chargement' ? 'triee' : undefined}
                    aria-sort={
                      p.tri.colonne === 'chargement'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('chargement')}>
                      Chargement
                      {p.tri.colonne === 'chargement' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th
                    scope="col"
                    className={`col-large col-souple ${p.tri.colonne === 'chargement_lieu' ? 'triee' : ''}`.trim()}
                    aria-sort={
                      p.tri.colonne === 'chargement_lieu'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('chargement_lieu')}>
                      Adresse de chargement
                      {p.tri.colonne === 'chargement_lieu' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th
                    scope="col"
                    className={p.tri.colonne === 'livraison' ? 'triee' : undefined}
                    aria-sort={
                      p.tri.colonne === 'livraison'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('livraison')}>
                      Livraison
                      {p.tri.colonne === 'livraison' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th
                    scope="col"
                    className={`col-large col-souple ${p.tri.colonne === 'livraison_lieu' ? 'triee' : ''}`.trim()}
                    aria-sort={
                      p.tri.colonne === 'livraison_lieu'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('livraison_lieu')}>
                      Adresse de livraison
                      {p.tri.colonne === 'livraison_lieu' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th
                    scope="col"
                    className={p.tri.colonne === 'statut' ? 'triee' : undefined}
                    aria-sort={
                      p.tri.colonne === 'statut'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('statut')}>
                      Statut
                      {p.tri.colonne === 'statut' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  {/* `col-large` : elle disparaît sous 760 px, comme les deux
                      adresses. MESURÉ le 04/09/2026 — sans elle, le tableau
                      débordait de 32 px de son cadre à cette largeur.

                      La colonne Gabarit a été RETIRÉE le 05/09/2026 pour faire
                      la place aux deux adresses. Mesuré sur la maquette :
                      1406 px à 1440 px de fenêtre, pas de débord. Le gabarit
                      reste lisible sur la fiche de l'ordre. */}
                  <th
                    scope="col"
                    className={`prix col-large ${p.tri.colonne === 'vente' ? 'triee' : ''}`.trim()}
                    aria-sort={
                      p.tri.colonne === 'vente'
                        ? p.tri.sens === 'croissant'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button className="tete-tri" type="button" onClick={() => p.quandTri('vente')}>
                      Total HT
                      {p.tri.colonne === 'vente' && (
                        <span className="sens-tri" aria-hidden="true">
                          {p.tri.sens === 'croissant' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((o) => {
                  const charge = chargementDit(o);
                  const livre = livraisonDit(o);
                  return (
                    <tr
                      className="rangee"
                      key={o.id}
                      tabIndex={0}
                      onClick={() => p.quandOuvrir(o.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          p.quandOuvrir(o.id);
                        }
                      }}
                    >
                      <td className="code">{o.numero}</td>
                      <td className="col-souple">
                        {o.client}
                        {o.reference_client !== null && (
                          <span className="secondaire">{o.reference_client}</span>
                        )}
                      </td>
                      <td>
                        {charge.jour ?? <Absent />}
                        {charge.heure !== null && (
                          <span className="secondaire">{charge.heure}</span>
                        )}
                      </td>
                      <td className="col-large col-souple">
                        {charge.site ?? <Absent />}
                        {charge.localite !== null && (
                          <span className="secondaire">{charge.localite}</span>
                        )}
                      </td>
                      <td>
                        {livre.jour ?? <Absent />}
                        {livre.heure !== null && (
                          <span className="secondaire">{livre.heure}</span>
                        )}
                      </td>
                      <td className="col-large col-souple">
                        {livre.site ?? <Absent />}
                        {livre.localite !== null && (
                          <span className="secondaire">{livre.localite}</span>
                        )}
                      </td>
                      <td>
                        <Statut statut={o.statut} />
                        {/* LA PASTILLE « AFFRÉTÉ », remise le 07/09/2026.
                            La colonne « Traité par » qui portait cette
                            information a sauté le 06/09 pour faire place aux
                            adresses de chargement et de livraison, et plus rien
                            ne disait qu'un ordre était sous-traité — constaté à
                            l'essai. Une pastille plutôt qu'une colonne : le
                            tableau tient déjà tout juste en 1440 px. Le nom du
                            transporteur et le prix d'achat sont dans
                            l'infobulle, où ils ne coûtent aucune largeur. */}
                        {estAffrete(o) && (
                          <span
                            className="statut affrete"
                            title={affreteDit(o) ?? undefined}
                          >
                            Affrété
                          </span>
                        )}
                        {o.litiges_ouverts > 0 && (
                          <span className="statut litige">
                            {o.litiges_ouverts > 1
                              ? `${o.litiges_ouverts} litiges`
                              : '1 litige'}
                          </span>
                        )}
                        {/* LES DEUX VIGNETTES DE FACTURATION, posées le
                            09/09/2026. Demande de l'utilisateur : « ne donne
                            pas le numéro de facture, seulement la vignette »,
                            et « ne modifie pas le tableau que l'on a créé,
                            juste les vignettes ». Le tableau ne gagne donc
                            aucune colonne.

                            ELLES S'EXCLUENT : « En facturation » dit que
                            l'ordre attend sa facture, « Facturé » qu'il l'a.
                            Les montrer toutes deux ferait croire à deux états
                            simultanés. */}
                        {o.facture ? (
                          <span className="statut emise">Facturé</span>
                        ) : (
                          o.envoye_facturation_le !== null && (
                            <span
                              className="statut a-venir"
                              title={
                                o.envoye_facturation_par === null
                                  ? undefined
                                  : `Envoyé par ${o.envoye_facturation_par}`
                              }
                            >
                              En facturation
                            </span>
                          )
                        )}
                      </td>
                      <td className="prix col-large">{enEuros(o.total_ht ?? o.prix_vente) ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* LES MÊMES ORDRES EN CARTES, POUR LE TÉLÉPHONE — lot 133, maquette
            « Téléphone — Ordres de transport » validée le 01/11/2026. Un
            tableau à huit colonnes ne tient pas sur 390 px. La feuille de
            style montre l'un ou l'autre selon la largeur ; les données et le
            clic sont les mêmes que ceux du tableau ci-dessus. */}
        {visibles.length > 0 && (
          <ul className="cartes-ot" aria-label="Ordres de transport">
            {visibles.map((o) => {
              const charge = chargementDit(o);
              const livre = livraisonDit(o);
              return (
                <li key={o.id}>
                  <button className="carte-ot" type="button" onClick={() => p.quandOuvrir(o.id)}>
                    <span className="tete-carte-ot">
                      <span className="code">{o.numero}</span>
                      <Statut statut={o.statut} />
                      {estAffrete(o) && <span className="statut affrete">Affrété</span>}
                      {o.litiges_ouverts > 0 && (
                        <span className="statut litige">
                          {o.litiges_ouverts > 1 ? `${o.litiges_ouverts} litiges` : '1 litige'}
                        </span>
                      )}
                      <span className="prix-carte-ot">{enEuros(o.total_ht ?? o.prix_vente) ?? '—'}</span>
                    </span>
                    <span className="client-carte-ot">
                      {o.client}
                      {o.reference_client !== null && ` · ${o.reference_client}`}
                    </span>
                    <span className="etape-carte-ot">
                      <span className="sens-ch" aria-label="Chargement">▼</span>
                      <span className="lieu-carte-ot">
                        {[charge.site, charge.localite].filter((x) => x !== null).join(' · ') || '—'}
                      </span>
                      <span className="quand-carte-ot">
                        {[charge.jour, charge.heure].filter((x) => x !== null).join(' ')}
                      </span>
                    </span>
                    <span className="etape-carte-ot">
                      <span className="sens-li" aria-label="Livraison">▲</span>
                      <span className="lieu-carte-ot">
                        {[livre.site, livre.localite].filter((x) => x !== null).join(' · ') || '—'}
                      </span>
                      <span className="quand-carte-ot">
                        {[livre.jour, livre.heure].filter((x) => x !== null).join(' ')}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * L'ONGLET « À CRÉER » — lot 147, 02/11/2026.
 *
 * On dépose plusieurs PDF ; Stafa les lit en arrière-plan ; chaque transport
 * lu devient une ligne « À vérifier ». « Vérifier » ouvre la saisie
 * pré-remplie ; à l'enregistrement, la suivante s'ouvre.
 */
function VueACreer({ p, etat }: { readonly p: ProprietesVueOrdres; readonly etat: EtatACreer }) {
  const liste = etat.liste ?? [];
  const premiere = suivanteAVerifier(liste);
  return (
    <section className="liste ac-espace">
      <div className="barre-outils">
        <label
          className={etat.envois > 0 ? 'bouton bouton-attente' : 'bouton'}
          htmlFor="ac-fichiers"
        >
          {etat.envois > 0 ? `Envoi de ${etat.envois} fichier${etat.envois > 1 ? 's' : ''}…` : 'Déposer des confirmations'}
        </label>
        <input
          id="ac-fichiers"
          className="fichier-cache"
          type="file"
          accept="application/pdf,.pdf"
          multiple
          onClick={(e) => {
            (e.target as HTMLInputElement).value = '';
          }}
          onChange={(e) => p.quandDeposer?.(Array.from(e.target.files ?? []))}
        />
        <div className="a-droite">
          <button
            className="bouton"
            type="button"
            disabled={premiere === undefined}
            onClick={() => {
              if (premiere !== undefined) p.quandVerifier?.(premiere);
            }}
          >
            Tout vérifier
          </button>
        </div>
      </div>
      {etat.refus !== null && <p className="bandeau">{etat.refus}</p>}
      <div
        className="ac-depot"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          p.quandDeposer?.(Array.from(e.dataTransfer.files));
        }}
      >
        {etat.liste === null ? (
          <p className="ac-vide">Chargement…</p>
        ) : liste.length === 0 ? (
          <p className="ac-vide">Déposez ici les PDF des confirmations.</p>
        ) : (
          <table className="ac-table">
            <thead>
              <tr>
                <th>Fichier</th>
                <th>Client lu</th>
                <th>Trajet</th>
                <th>Chargement</th>
                <th className="ac-nombre">Prix</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liste.map((c) => {
                const prop = c.proposition;
                const jour = prop === null ? null : jourDe(prop);
                return (
                  <tr key={c.id} className={`ac-ligne ac-${c.etat}`}>
                    <td>
                      <span className="ac-fichier">{c.nom}</span>
                      {(c.transports_du_document ?? 1) > 1 && (
                        <span className="ac-rang">
                          {' '}
                          · {c.rang_transport}/{c.transports_du_document}
                        </span>
                      )}
                    </td>
                    <td>{prop?.client_nom ?? '—'}</td>
                    <td>{prop === null ? '—' : trajetDe(prop)}</td>
                    <td>{jour === null ? '—' : dateCourte(jour)}</td>
                    <td className="ac-nombre">
                      {prop?.prix === null || prop?.prix === undefined
                        ? '—'
                        : (enEuros(prop.prix.toFixed(2)) ?? '—')}
                    </td>
                    <td>
                      <span className={`ac-pastille ac-pastille-${c.etat}`}>{etatDit(c.etat)}</span>
                      {c.etat === 'echec' && c.message !== null && (
                        <span className="ac-message">{c.message}</span>
                      )}
                      {c.doublon !== null && (
                        <span className="ac-doublon">Déjà saisie ? OT {c.doublon.numero}</span>
                      )}
                    </td>
                    <td className="ac-actions">
                      {c.etat === 'lue' && (
                        <button className="bouton" type="button" onClick={() => p.quandVerifier?.(c)}>
                          Vérifier
                        </button>
                      )}
                      {c.etat === 'echec' && (
                        <button
                          className="bouton bouton-discret"
                          type="button"
                          onClick={() => p.quandRelire?.(c)}
                        >
                          Relire
                        </button>
                      )}
                      <button
                        className="bouton bouton-discret"
                        type="button"
                        onClick={() => p.quandEcarter?.(c)}
                      >
                        Écarter
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

function VueFiche({ p }: { readonly p: ProprietesVueOrdres }) {
  /**
   * LA CONFIRMATION DE PRISE EN CHARGE, ouverte ou non — 23/09/2026.
   *
   * L'état est posé AVANT les retours anticipés qui suivent : un `useState`
   * placé après le premier `if` ne serait pas appelé à tous les rendus, ce que
   * React interdit.
   */
  const [confirmation, setConfirmation] = useState(false);
  /**
   * LES ACTIONS DE LA FICHE SUR TÉLÉPHONE — lot 133, maquette du 01/11/2026.
   * Sept boutons ne tiennent pas sur 390 px : on montre le premier, et « ⋯ »
   * ouvre les autres en panneau par le bas. Sur ordinateur, cet état ne change
   * rien — la feuille de style ne regarde la classe que sous 640 px.
   */
  const [actionsTel, setActionsTel] = useState(false);
  /** La liste « Documents » dépliée dans le menu « ⋯ » — lot 152. */
  const [documentsOuverts, setDocumentsOuverts] = useState(false);
  /**
   * UN CLIC HORS DES ACTIONS REFERME LE MENU — lot 152. Sur ordinateur, le
   * menu est une liste qui tombe sous « ⋯ » ; sans cela il resterait ouvert
   * par-dessus la fiche jusqu'au prochain choix.
   */
  const refActions = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!actionsTel) return;
    const dehors = (e: MouseEvent): void => {
      if (refActions.current !== null && !refActions.current.contains(e.target as Node)) {
        setActionsTel(false);
        setDocumentsOuverts(false);
      }
    };
    document.addEventListener('mousedown', dehors);
    return () => {
      document.removeEventListener('mousedown', dehors);
    };
  }, [actionsTel]);

  /**
   * L'ORDRE D'AFFRÈTEMENT, ouvert ou non — 21/10/2026.
   *
   * Un état distinct de celui de la confirmation, et non un seul état à
   * trois valeurs : les deux feuilles se montent chacune dans son portail,
   * et un état partagé ferait que l'une se fermerait en ouvrant l'autre
   * sans que rien ne le demande. Posé ici pour la même raison que
   * ci-dessus : avant les retours anticipés.
   */
  const [ordreAffretement, setOrdreAffretement] = useState(false);

  if (p.fiche.nom === 'chargement') {
    return (
      <div className="corps corps-ot">
        <p className="attente">Chargement…</p>
      </div>
    );
  }

  if (p.fiche.nom !== 'prete') {
    return (
      <div className="corps corps-ot">
        <div className="bandeau-fiche">
          <button className="retour" type="button" onClick={p.quandRetour}>
            ← {p.libelleRetour}
          </button>
        </div>
        <div className="vide">
          <p>
            {p.fiche.nom === 'panne' ? REFUS[p.fiche.cause] : 'Aucun ordre ouvert.'}
          </p>
        </div>
      </div>
    );
  }

  const { ordre_transport: ordre, etapes } = p.fiche.fiche;
  const t = totaux(etapes);

  return (
    <div className="corps corps-ot">
      {/* Les deux boîtes passent PAR-DESSUS la fiche : on valide ou on ouvre un
          litige en un geste, sans ouvrir le formulaire de l'étape. */}
      {p.validation !== null && (
        <BoiteValidation
          saisie={p.validation}
          {...(() => {
            // LA DERNIÈRE À FAIRE : toutes les autres sont déjà validées.
            // C'est alors que le transport est fini, et que sa durée se sait.
            const restantes = etapes.filter(
              (e) => !e.fait && e.id !== p.validation?.etapeId,
            );
            return { derniere: restantes.length === 0 };
          })()}
          tempsPasse={p.tempsPasse}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampValidation}
          quandTemps={p.quandTemps}
          quandFermer={p.quandFermerBoite}
          quandValider={p.quandConfirmerValidation}
        />
      )}
      {p.affretement !== null && (
        <BoiteAffretement
          saisie={p.affretement}
          prixVente={ordre.prix_vente}
          transporteurs={p.proposes}
          tous={p.tousTransporteurs}
          chargement={p.chercheEnCours}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampAffretement}
          quandNouveau={p.quandNouveauTransporteur}
          quandFermer={p.quandFermerBoite}
          quandEnregistrer={p.quandEnregistrerAffretement}
        />
      )}
      {p.saisiePrestation !== null && (
        <BoitePrestation
          saisie={p.saisiePrestation}
          natures={p.naturesPrestation}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampPrestation}
          quandFermer={p.quandFermerBoite}
          quandEnregistrer={p.quandEnregistrerPrestation}
        />
      )}
      {p.resolution !== null && (
        <BoiteResolution
          saisie={p.resolution}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampResolution}
          quandFermer={p.quandFermerBoite}
          quandEnregistrer={p.quandEnregistrerResolution}
        />
      )}
      {/* PAR-DESSUS le panneau, qui reste ouvert dessous : le prix et le
          contact déjà tapés ne sont pas perdus. */}
      {p.nouveauTransporteur !== null && (
        <BoiteNouvelleFiche
          annuaire={ANNUAIRE_TRANSPORTEURS}
          saisie={p.nouveauTransporteur.saisie}
          fautes={p.nouveauTransporteur.fautes}
          refus={p.nouveauTransporteur.refus ?? p.actions.refus}
          envoi={p.actions.envoi}
          quandChamp={p.quandChampNouveauTransporteur}
          quandNomQuitte={p.quandNomQuitteNouveauTransporteur}
          quandEnregistrer={p.quandEnregistrerNouveauTransporteur}
          quandAnnuler={p.quandAnnulerNouveauTransporteur}
        />
      )}
      {p.echange !== null && (
        <BoiteEchange
          saisie={p.echange.saisie}
          supports={p.supports}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampEchange}
          quandFermer={p.quandFermerBoite}
          quandEnregistrer={p.quandEnregistrerEchange}
          quandEffacer={
            etapes.find((e) => e.id === p.echange?.etapeId)?.echange_support ===
            undefined
              ? null
              : etapes.find((e) => e.id === p.echange?.etapeId)?.echange_support ===
                  null
                ? null
                : p.quandEffacerEchange
          }
        />
      )}
      {p.litige !== null && (
        <BoiteLitige
          saisie={p.litige}
          motifs={p.motifs}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampLitige}
          quandNouveauMotif={p.quandNouveauMotif}
          quandFermer={p.quandFermerBoite}
          quandOuvrir={p.quandOuvrirLitige}
        />
      )}
      <div className="bandeau-fiche">
        <button className="retour" type="button" onClick={p.quandRetour}>
          ← {p.libelleRetour}
        </button>
        <div className="titre-fiche">
          <div className="ligne">
            {/* LE NUMÉRO EN TITRE — cadrage de la saisie d'OT §2, 31/10/2026 :
                « une fois l'OT créé, tout en haut le numéro d'ordre doit
                apparaître si on retourne dans l'OT ». */}
            <h3 className="titre-fiche-ot">Ordre de transport n° {ordre.numero}</h3>
            <Statut statut={ordre.statut} />
            {ordre.litiges_ouverts > 0 && (
              <span className="statut litige">
                {ordre.litiges_ouverts > 1
                  ? `${ordre.litiges_ouverts} litiges`
                  : '1 litige'}
              </span>
            )}
            {ordre.facture ? (
              <span className="statut emise">Facturé</span>
            ) : (
              ordre.envoye_facturation_le !== null && (
                <span className="statut a-venir">En facturation</span>
              )
            )}
          </div>
          <span className="client">
            {ordre.client}
            {ordre.reference_client !== null &&
              ` · référence ${ordre.reference_client}`}
          </span>
          {/* CE QUI A REMPLACÉ LE MODULE AFFRÈTEMENT le 05/09/2026 : l'ordre ne
              change pas de liste, c'est une ligne qui apparaît. La marge se
              calcule des deux prix figés — elle n'est stockée nulle part. */}
          {ordre.affrete_par !== null && (
            <span className="client">
              Affrété · {ordre.affrete_par}
              {(() => {
                const m = marge(ordre.prix_vente, ordre.prix_achat);
                return m === null ? null : (
                  <span className={`marge-${m.couleur}`}> · {margeDite(m)}</span>
                );
              })()}
            </span>
          )}
        </div>
        <div
          ref={refActions}
          className={actionsTel ? 'actions-fiche actions-tel-ouvertes' : 'actions-fiche'}
          onClick={(e) => {
            // UN CHOIX FAIT DANS LE PANNEAU LE REFERME, quel que soit le bouton.
            if (actionsTel && e.target instanceof HTMLButtonElement) {
              setActionsTel(false);
              setDocumentsOuverts(false);
            }
          }}
        >
          {p.editionOrdre === null && (
            <>
              {/* « ⋯ » — LOT 152, 03/11/2026. Demande de Mohamed : « dans les
                  OT, affrété ou non, il y a trop de boutons ». Restent
                  visibles Modifier et la facturation, ses deux choix ; le reste
                  passe derrière ce bouton, sur ordinateur comme sur téléphone
                  (où il existait depuis le lot 133). */}
              <button
                className="plus-actions-tel"
                type="button"
                aria-label="Plus d’actions"
                aria-haspopup="menu"
                aria-expanded={actionsTel}
                onClick={(e) => {
                  e.stopPropagation();
                  setActionsTel((o) => !o);
                  setDocumentsOuverts(false);
                }}
              >
                ⋯
              </button>
              {/* MODIFIER DISPARAÎT DÈS QUE L'ORDRE EST PARTI EN FACTURATION —
                  règle de l'utilisateur du 09/09/2026 : « un OT envoyé en
                  facturation ne peut pas être modifié, il faut faire revenir
                  l'OT pour cela ».

                  Il DISPARAÎT au lieu d'être grisé : un bouton éteint invite à
                  chercher pourquoi, là où son absence, avec le bandeau qui suit
                  et le bouton « Retirer de la facturation » juste à côté, dit
                  quoi faire. L'API refuse de son côté — deux barrières, comme
                  partout. */}
              {ordre.envoye_facturation_le === null && (
                <button className="bouton" type="button" onClick={p.quandModifierOrdre}>
                  Modifier
                </button>
              )}
              {/* LE GESTE DE FACTURATION, posé le 09/09/2026.

                  Il n'apparaît PAS sur un ordre annulé ni sur un ordre déjà
                  facturé — l'API refuse les deux, et un bouton qui ne peut que
                  produire un refus n'a rien à faire là.

                  Aucune condition sur le statut : l'API n'en pose pas non plus.
                  L'exploitation décide quand un ordre part en facturation ; le
                  logiciel refuse ce qui n'a pas de sens, il ne décide pas à sa
                  place. */}
              {ordre.statut !== 'annule' && !ordre.facture && (
                <button
                  className={
                    ordre.envoye_facturation_le === null
                      ? 'bouton'
                      : 'bouton bouton-discret'
                  }
                  type="button"
                  disabled={p.actions.envoi}
                  onClick={
                    ordre.envoye_facturation_le === null
                      ? p.actions.quandEnvoyerEnFacturation
                      : p.actions.quandRetirerDeLaFacturation
                  }
                >
                  {ordre.envoye_facturation_le === null
                    ? 'Envoyer en facturation'
                    : 'Retirer de la facturation'}
                </button>
              )}
              {/* Rouvrir est la seule décision qui reste visible sur un ordre
                  annulé. Il ne demande pas confirmation : il ne détruit rien,
                  et l'API rend l'état réel des étapes — un ordre annulé dont
                  tout était livré revient à « Livré », pas à « En attente ». */}
              {ordre.statut === 'annule' && (
                <button
                  className="bouton"
                  type="button"
                  disabled={p.actions.envoi}
                  onClick={p.actions.quandRouvrirOrdre}
                >
                  {p.actions.envoi ? 'Réouverture…' : 'Rouvrir'}
                </button>
              )}
              <div className="menu-actions-fiche" role="menu">
                {/* DUPLIQUER — demandé le 08/09/2026 : « éviter de réécrire
                    50 fois la même chose ». Le bouton n'écrit RIEN : il ouvre le
                    formulaire de création pré-rempli, dates vides. Il reste
                    offert sur un ordre annulé, qui est justement celui qu'on
                    refait le plus souvent. */}
                <button
                  className="bouton bouton-discret"
                  type="button"
                  role="menuitem"
                  onClick={p.quandDupliquerOrdre}
                >
                  Dupliquer
                </button>
                {/* LES DOCUMENTS — lot 152 : un seul bouton pour les deux
                    feuilles, à la demande de Mohamed.

                    La CONFIRMATION DE PRISE EN CHARGE (23/09/2026) va au
                    client ; l'ORDRE D'AFFRÈTEMENT (21/10/2026) au
                    sous-traitant, et n'existe que si l'ordre porte un
                    affrètement actif — sans transporteur ni prix d'achat,
                    l'API répond 409 `non_affrete`. Aucune n'écrit rien.

                    UN ORDRE ANNULÉ N'A AUCUN DOCUMENT : confirmer ou commander
                    un transport qui n'aura pas lieu est le contraire de ce que
                    ces feuilles servent à dire. */}
                {ordre.statut !== 'annule' && (
                  <button
                    className="bouton bouton-discret"
                    type="button"
                    role="menuitem"
                    aria-expanded={documentsOuverts}
                    onClick={(e) => {
                      // OUVRIR LA LISTE NE REFERME PAS LE MENU.
                      e.stopPropagation();
                      setDocumentsOuverts((o) => !o);
                    }}
                  >
                    Documents {documentsOuverts ? '▴' : '▾'}
                  </button>
                )}
                {ordre.statut !== 'annule' && documentsOuverts && (
                  <>
                    <button
                      className="bouton bouton-discret sous-action"
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setConfirmation(true);
                      }}
                    >
                      Confirmation client
                    </button>
                    {ordre.affretement_id !== null && (
                      <button
                        className="bouton bouton-discret sous-action"
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setOrdreAffretement(true);
                        }}
                      >
                        Ordre d’affrètement
                      </button>
                    )}
                  </>
                )}
                {/* AFFRÉTER OU RENDRE. Un ordre annulé ne s'affrète pas — l'API
                    le refuse aussi, deux barrières comme partout. */}
                {ordre.statut !== 'annule' && ordre.affretement_id === null && (
                  <button
                    className="bouton bouton-discret"
                    type="button"
                    role="menuitem"
                    disabled={p.actions.envoi}
                    onClick={p.quandAffreter}
                  >
                    Affréter
                  </button>
                )}
                {ordre.affretement_id !== null && (
                  <button
                    className="bouton bouton-risque"
                    type="button"
                    role="menuitem"
                    disabled={p.actions.envoi}
                    onClick={p.quandAnnulerAffretement}
                  >
                    Rendre à l’exploitation
                  </button>
                )}
                {/* ANNULER SUIT LA MÊME RÈGLE que Modifier : annuler un ordre
                    qu'on a poussé en facturation reviendrait à facturer un
                    transport annulé, ou à laisser une facture sans objet. */}
                {ordre.statut !== 'annule' && ordre.envoye_facturation_le === null && (
                  <button
                    className="bouton bouton-risque"
                    type="button"
                    role="menuitem"
                    disabled={p.actions.envoi}
                    onClick={p.actions.quandDemanderAnnulation}
                  >
                    Annuler l’OT
                  </button>
                )}
                {ordre.statut === 'annule' && (
                  <button
                    className="bouton bouton-risque"
                    type="button"
                    role="menuitem"
                    disabled={p.actions.envoi}
                    onClick={p.actions.quandDemanderSuppression}
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* La feuille se rend dans `document.body` par un portail : elle n'est
          ici que dans l'arbre React, pas dans la mise en page de la fiche. */}
      {confirmation && (
        <EcranConfirmation
          jeton={p.jeton}
          otId={ordre.id}
          quandFermer={() => {
            setConfirmation(false);
          }}
          quandAccesPerdu={p.quandAccesPerdu}
        />
      )}

      {/* L'ORDRE D'AFFRÈTEMENT, dans son propre portail — 21/10/2026. */}
      {ordreAffretement && (
        <EcranOrdreAffretement
          jeton={p.jeton}
          otId={ordre.id}
          quandFermer={() => {
            setOrdreAffretement(false);
          }}
          quandAccesPerdu={p.quandAccesPerdu}
        />
      )}

      {/* LE BANDEAU QUI DIT POURQUOI, ET QUOI FAIRE. Sans lui, l'absence de
          « Modifier » se lirait comme un défaut. Il distingue les deux états,
          parce que la suite n'est pas la même :

            - ENVOYÉ, pas encore facturé → « Retirer de la facturation »,
              le bouton est juste au-dessus ;
            - FACTURÉ → il faut d'abord défaire la facture. Si c'est un
              brouillon, on le supprime dans Comptabilité et l'ordre revient.
              Si elle est émise, c'est fini : on corrige par un avoir. */}
      {ordre.envoye_facturation_le !== null && (
        <p className="bandeau">
          {ordre.facture
            ? 'Cet ordre est porté par une facture : il ne se modifie plus. Supprimez le brouillon dans Comptabilité pour le récupérer — si la facture est déjà émise, elle se corrige par un avoir.'
            : 'Cet ordre est parti en facturation : il ne se modifie plus. Utilisez « Retirer de la facturation » ci-dessus pour le reprendre.'}
        </p>
      )}

      {p.actions.refus !== null && <p className="bandeau">{p.actions.refus}</p>}

      {/* Annuler garde l'ordre dans l'histoire ; supprimer l'efface AVEC SES
          ÉTAPES et ne se propose qu'une fois l'ordre annulé — même règle que
          « désactiver puis supprimer » ailleurs. */}
      {p.actions.confirmeAnnulation && (
        <div className="confirmation">
          <p className="empechement">
            Passer <strong>{ordre.numero}</strong> en « Annulé » ? Il restera
            consultable et dans les états, comme les transports annulés de l’ancien
            logiciel.
          </p>
          <div className="actions">
            <button
              className="bouton bouton-risque"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandAnnulerOrdre}
            >
              {p.actions.envoi ? 'Annulation…' : 'Annuler l’OT'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandRenoncer}
            >
              Ne rien faire
            </button>
          </div>
        </div>
      )}

      {p.actions.confirmeSuppression && (
        <div className="confirmation">
          <p className="empechement">
            Supprimer définitivement <strong>{ordre.numero}</strong> ?{' '}
            <strong>Ses {ordre.etapes} étapes seront effacées avec lui.</strong> À ne
            faire que pour une saisie fautive : un transport qui n’a pas eu lieu
            s’annule. Cette action est sans retour.
          </p>
          <div className="actions">
            <button
              className="bouton bouton-risque"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandSupprimerOrdre}
            >
              {p.actions.envoi ? 'Suppression…' : 'Supprimer définitivement'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandRenoncer}
            >
              Ne rien faire
            </button>
          </div>
        </div>
      )}

      <div className="onglets">
        <button
          className="onglet-fiche"
          type="button"
          aria-selected={p.onglet === 'commande'}
          onClick={() => p.quandOnglet('commande')}
        >
          Commande
        </button>
        <button
          className="onglet-fiche"
          type="button"
          aria-selected={p.onglet === 'etapes'}
          onClick={() => p.quandOnglet('etapes')}
        >
          Étapes <span className="compteur">{etapes.length}</span>
        </button>
        {/* L'EXÉCUTION — lot 161, 04/11/2026 (eCMR, bêta) : les lettres de
            voiture de l'ordre. */}
        <button
          className="onglet-fiche"
          type="button"
          aria-selected={p.onglet === 'execution'}
          onClick={() => p.quandOnglet('execution')}
        >
          Exécution
        </button>
        <button
          className="onglet-fiche"
          type="button"
          aria-selected={p.onglet === 'documents'}
          onClick={() => p.quandOnglet('documents')}
        >
          Documents
          {p.documents.length > 0 && (
            <span className="compteur">{p.documents.length}</span>
          )}
        </button>
      </div>

      <div className="contenu-onglet">
        {p.onglet === 'execution' ? (
          <OngletExecution jeton={p.jeton} otId={ordre.id} etapes={etapes} quandAccesPerdu={p.quandAccesPerdu} />
        ) : p.onglet === 'documents' ? (
          <OngletDocuments
            documents={p.documents}
            types={p.typesDocument}
            ajout={p.ajoutDocument}
            envoi={p.envoiDocument}
            refus={p.refusDocument}
            quandOuvrirAjout={p.quandOuvrirAjout}
            quandChampAjout={p.quandChampAjout}
            quandFichier={p.quandFichier}
            quandJoindre={p.quandJoindre}
            quandFermerAjout={p.quandFermerAjout}
            quandOuvrirDocument={p.quandOuvrirDocument}
            quandSupprimerDocument={p.quandSupprimerDocument}
          />
        ) : p.onglet === 'commande' ? (
          p.editionOrdre === null ? (
            <>
            <OngletCommande
              ordre={ordre}
              etapes={etapes}
              totaux={t}
              cout={p.cout}
              prestations={p.prestations}
              actions={p.actions}
              quandRecalculerLeCout={p.quandRecalculerLeCout}
              quandOuvrirPrestation={p.quandOuvrirPrestation}
              quandRetirerPrestation={p.quandRetirerPrestation}
            />
            {/* LE PACKAGING DE L'ORDRE — F4, 01/11/2026. Les lignes
                enregistrées, et leur modification avec le même dessin que la
                fiche neuve. */}
            <section className="fn-bloc packaging-fiche-fn">
              <h3 className="titre-bloc-ot">Packaging</h3>
              {p.editionPackaging === null || p.editionPackaging === undefined ? (
                <>
                  {(p.fiche.fiche.marchandises ?? []).length === 0 ? (
                    <p className="rien">
                      Aucune ligne de marchandise : la quantité, le support et le poids
                      sont saisis sur les étapes.
                    </p>
                  ) : (
                    <table className="table-packaging-fn">
                      <thead>
                        <tr>
                          <th>Support</th>
                          <th>Quantité</th>
                          <th>Dimensions</th>
                          <th>Gerbé</th>
                          <th>Poids</th>
                          <th>ML</th>
                          <th>Consignées</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(p.fiche.fiche.marchandises ?? []).map((m) => (
                          <tr key={m.id}>
                            <td>{m.support ?? '—'}</td>
                            <td>{m.quantite === null ? '—' : Number(m.quantite).toLocaleString('fr-FR')}</td>
                            <td>
                              {[m.longueur_cm, m.largeur_cm, m.hauteur_cm]
                                .map((d) => (d === null ? '—' : String(d)))
                                .join(' × ')}
                            </td>
                            <td>{m.etages > 1 ? `par ${String(m.etages)}` : 'non'}</td>
                            <td>{m.poids === null ? '—' : `${m.poids.toLocaleString('fr-FR')} kg`}</td>
                            <td>
                              {m.ml_unitaire === null || m.quantite === null
                                ? '—'
                                : mlLisible(Number(m.ml_unitaire) * Number(m.quantite))}
                            </td>
                            <td>{m.consigne ? 'oui' : 'non'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <button
                    className="bouton bouton-discret ajout-marchandise-fn"
                    type="button"
                    onClick={p.quandModifierPackaging}
                  >
                    Modifier le packaging
                  </button>
                </>
              ) : (
                <>
                  <LignesPackaging
                    lignes={p.editionPackaging}
                    etapes={etapes.map((e) => ({ type: e.type, nom: e.site_nom }))}
                    supports={p.supports}
                    quandAjouter={p.quandAjouterPackaging}
                    quandChamp={p.quandChampPackaging}
                    quandSupport={p.quandSupportPackaging}
                    quandRetirer={p.quandRetirerPackaging}
                  />
                  <div className="pied-creation-site">
                    <button
                      className="bouton"
                      type="button"
                      disabled={p.actions.envoi}
                      onClick={p.quandEnregistrerPackaging}
                    >
                      Enregistrer le packaging
                    </button>
                    <button
                      className="bouton bouton-discret"
                      type="button"
                      disabled={p.actions.envoi}
                      onClick={p.quandAnnulerPackaging}
                    >
                      Annuler
                    </button>
                  </div>
                </>
              )}
            </section>
            </>
          ) : (
            <FormulaireOrdre
              edition={p.editionOrdre}
              clients={p.clients}
              centres={p.centres}
              gabarits={p.gabarits}
              quandChamp={p.quandChampOrdre}
              quandNouveauClient={p.quandNouveauClient}
              quandEnregistrer={p.quandEnregistrerOrdre}
              quandAnnuler={p.quandAnnulerEdition}
            />
          )
        ) : (
          <div className="cartes-etapes">
            {etapes.length === 0 && (
              <p className="rien">
                Aucune étape. Un ordre sans étape n’a ni date ni trajet : ajoutez-en
                une.
              </p>
            )}
            {etapes.map((e, rang) => (
              <CarteEtape
                key={e.id}
                etape={e}
                supports={p.supports}
                edition={
                  p.editionEtape !== null && p.editionEtape.id === e.id
                    ? p.editionEtape
                    : null
                }
                sites={p.sites}
                motifs={p.motifs}
                vehicules={p.vehicules}
                chauffeurs={p.chauffeurs}
                remorques={p.remorques}
                gelee={ordre.envoye_facturation_le !== null}
                quandModifier={() => p.quandModifierEtape(e.id)}
                quandBasculerFait={() => p.quandBasculerFait(e.id)}
                quandLitige={() => p.quandLitige(e.id)}
                quandResoudre={(litigeId) => p.quandResoudre(litigeId)}
                quandEchange={() => p.quandEchange(e.id)}
                quandMonter={rang === 0 ? null : () => p.quandDeplacerEtape(rang, -1)}
                quandDescendre={
                  rang === etapes.length - 1
                    ? null
                    : () => p.quandDeplacerEtape(rang, 1)
                }
                occupee={p.actions.envoi}
                quandChamp={p.quandChampEtape}
                quandSite={p.quandSiteEtape}
                quandOuvrirSite={p.quandOuvrirSite}
                quandChampSite={p.quandChampSite}
                quandCreerSite={p.quandCreerSite}
                quandFermerSite={p.quandFermerSite}
                quandEnregistrer={p.quandEnregistrerEtape}
                quandAnnuler={p.quandAnnulerEtape}
                quandDemanderSuppression={p.quandDemanderSuppressionEtape}
                quandSupprimer={p.quandSupprimerEtape}
              />
            ))}

            {/* L'étape qu'on ajoute : même formulaire, dans une carte sans
                numéro — le rang est déduit par l'API, l'écran n'a pas à
                compter. */}
            {p.editionEtape !== null && p.editionEtape.id === null && (
              <div className="carte-etape ouverte">
                <div className="entete-etape">
                  <span className="rang">+</span>
                  <span className="type-etape">
                    {p.editionEtape.saisie.type === 'chargement'
                      ? 'Chargement'
                      : 'Livraison'}
                  </span>
                  <span className="site-etape">Nouvelle étape</span>
                </div>
                <div className="corps-etape">
                  <FormulaireEtape
                    edition={p.editionEtape}
                    sites={p.sites}
                    motifs={p.motifs}
                    supports={p.supports}
                    vehicules={p.vehicules}
                    chauffeurs={p.chauffeurs}
                    remorques={p.remorques}
                    neuve
                    quandChamp={p.quandChampEtape}
                    quandSite={p.quandSiteEtape}
                    quandOuvrirSite={p.quandOuvrirSite}
                    quandChampSite={p.quandChampSite}
                    quandCreerSite={p.quandCreerSite}
                    quandFermerSite={p.quandFermerSite}
                  />
                  {p.editionEtape.refus !== null && (
                    <p className="bandeau">{p.editionEtape.refus}</p>
                  )}
                </div>
                <div className="pied-edition">
                  <button
                    className="bouton"
                    type="button"
                    disabled={p.editionEtape.envoi}
                    onClick={p.quandEnregistrerEtape}
                  >
                    {p.editionEtape.envoi ? 'Enregistrement…' : 'Ajouter l’étape'}
                  </button>
                  <button
                    className="bouton bouton-discret"
                    type="button"
                    disabled={p.editionEtape.envoi}
                    onClick={p.quandAnnulerEtape}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {p.editionEtape === null && ordre.envoye_facturation_le === null && (
              <div className="ajouter-etape">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  onClick={() => p.quandAjouterEtape('chargement')}
                >
                  + Un chargement
                </button>
                <button
                  className="bouton bouton-discret"
                  type="button"
                  onClick={() => p.quandAjouterEtape('livraison')}
                >
                  + Une livraison
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * LA SOMME DES NATURES. Elle ne se stocke pas : voir la migration.
 *
 * QUATRE DEPUIS LE 14/09/2026 : l'achat s'ajoute aux trois autres. Sur un
 * transport affrété, les trois premières valent zéro et tout le coût est dans
 * l'achat ; sur un transport fait en propre, c'est l'inverse. La somme est
 * juste dans les deux cas — c'est pourquoi elle ne se branche pas sur la
 * nature du transport.
 */
function totalDuCout(c: CoutOrdre): number {
  if (c.fige === null) return 0;
  return (
    Number(c.fige.cout_temps) +
    Number(c.fige.cout_km) +
    Number(c.fige.cout_peage) +
    Number(c.fige.cout_achat ?? 0)
  );
}

/**
 * L'UNITÉ TELLE QU'ELLE S'ÉCRIT DERRIÈRE UNE QUANTITÉ — 15/09/2026.
 *
 * « 2,00 h », « 6,00 u ». Le forfait ne rend RIEN : « 1,00 forfait » n'apprend
 * rien de plus que « 1,00 », et allonge une colonne de chiffres.
 */
function uniteDite(unite: string): string {
  if (unite === 'heure') return 'h';
  if (unite === 'jour') return 'j';
  if (unite === 'unite') return 'u';
  if (unite === 'kilometre') return 'km';
  return '';
}

/**
 * L'UNITÉ EN TOUTES LETTRES, pour une étiquette de saisie : « Quantité
 * (unité) ». La valeur stockée est sans accent — c'est une clé, pas un
 * libellé — et l'afficher telle quelle donnait « Quantité (unite) », constaté
 * au rendu le 15/09/2026.
 */
function uniteEnToutesLettres(unite: string): string {
  if (unite === 'unite') return 'unité';
  if (unite === 'kilometre') return 'kilomètre';
  return unite;
}

/**
 * UNE QUANTITÉ À LA FRANÇAISE. La base rend « 2.00 » ; l'écran doit dire
 * « 2,00 ». Constaté au rendu le 15/09/2026 — la colonne affichait « 2.00 h »
 * à côté de prix en « 45,00 € », deux conventions dans le même tableau.
 */
function quantiteDite(quantite: string): string {
  const nombre = Number(quantite);
  if (!Number.isFinite(nombre)) return quantite;
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(nombre);
}

/**
 * UN MONTANT TEL QU'ON LE TAPE. La base rend « 45.00 » ; un champ de saisie
 * français montre « 45,00 ». `enNombre` refait le chemin inverse à l'envoi.
 */
function pourLaSaisie(montant: string | null | undefined): string {
  return montant == null ? '' : montant.replace('.', ',');
}

/**
 * LE CHIFFRE D'AFFAIRES D'UN ORDRE : sa vente PLUS ses prestations annexes.
 *
 * POURQUOI CETTE FONCTION EXISTE — 15/09/2026. Jusqu'ici le CA d'un ordre
 * était `prix_vente`, et rien d'autre. Deux heures d'attente coûtaient du
 * camion et du chauffeur — ce coût entrait bien dans le coût de revient — et
 * les 90 € refacturés n'entraient nulle part : la marge réelle affichée était
 * inférieure à la marge réalisée, sur le chiffre même qui sert à décider de
 * reprendre un trafic.
 *
 * EN CENTIMES ENTIERS, et non en nombres à virgule : 780,10 + 161,90 vaut
 * 942.0000000000001 en JavaScript. C'est la même raison qui fait voyager tous
 * les montants du projet en chaînes de caractères.
 */
export function caDeLOrdre(
  prixVente: string | null,
  totalAnnexes: string | null,
): string | null {
  const centimes = (valeur: string | null): number | null => {
    if (valeur === null) return null;
    const nombre = Number(valeur);
    return Number.isFinite(nombre) ? Math.round(nombre * 100) : null;
  };
  const vente = centimes(prixVente);
  const annexes = centimes(totalAnnexes) ?? 0;
  // UN ORDRE SANS PRIX DE VENTE MAIS AVEC UNE ANNEXE a bien un CA : c'est le
  // cas d'une immobilisation facturée seule. Rendre `null` le ferait
  // disparaître de la marge comme s'il n'avait rien rapporté.
  if (vente === null) return annexes === 0 ? null : (annexes / 100).toFixed(2);
  return ((vente + annexes) / 100).toFixed(2);
}

function OngletCommande({
  ordre,
  etapes,
  totaux: t,
  cout,
  prestations,
  actions,
  quandRecalculerLeCout,
  quandOuvrirPrestation,
  quandRetirerPrestation,
}: {
  readonly ordre: OrdreTransport;
  readonly etapes: readonly Etape[];
  readonly totaux: {
    readonly quantite: number | null;
    readonly support: string | null;
    readonly poids: number | null;
  };
  readonly cout: CoutOrdre | null;
  /** `null` tant que la lecture n'est pas revenue. */
  readonly prestations: PrestationsDeLOrdre | null;
  readonly actions: ActionsOrdre;
  readonly quandRecalculerLeCout: () => void;
  /** `null` pour en ajouter une ; une prestation pour la modifier. */
  readonly quandOuvrirPrestation: (prestation: PrestationAnnexe | null) => void;
  readonly quandRetirerPrestation: (prestationId: string) => void;
}) {
  const chargement = etapes.find((e) => e.type === 'chargement');
  const livraison = [...etapes].reverse().find((e) => e.type === 'livraison');

  const quandOu = (e: Etape | undefined) => {
    if (e === undefined) return null;
    const jour = enFrancais(e.jour);
    const heures = creneau(e);
    return `${jour ?? e.jour}${heures === null ? '' : ` · ${heures}`}`;
  };

  const alaPalette = rapport(ordre.prix_vente, t.quantite);
  const auKilometre = rapport(ordre.prix_vente, ordre.distance_km);

  return (
    <div className="groupes">
      <div className="groupe">
        <p className="titre-groupe">Le client</p>
        <dl className="champs-ot">
          <Champ nom="Client">{ordre.client}</Champ>
          <Champ nom="Référence client">{ordre.reference_client ?? <Absent />}</Champ>
          <Champ nom="N° d’OT" calcule="attribué à la création">
            <span className="code">{ordre.numero}</span>
          </Champ>
          {/* « par qui » n'est pas affiché : l'auteur n'est pas sur l'ordre, il
              n'existe que dans le journal des traces. L'inventer serait pire
              que de le taire. */}
          <Champ nom="Saisi le">{instantDit(ordre.cree_le) ?? <Absent />}</Champ>
        </dl>
      </div>

      <div className="groupe">
        <p className="titre-groupe">Le transport</p>
        <dl className="champs-ot">
          <Champ nom="Chargement" calcule={chargement?.site_ville ?? undefined}>
            {quandOu(chargement) ?? <Absent />}
          </Champ>
          <Champ nom="Livraison" calcule={livraison?.site_ville ?? undefined}>
            {quandOu(livraison) ?? <Absent />}
          </Champ>
          <Champ nom="Gabarit demandé">{ordre.gabarit ?? <Absent />}</Champ>
          <Champ nom="Distance">
            {ordre.distance_km === null ? (
              <Absent />
            ) : (
              <span className="code">{lisible(ordre.distance_km) as string} km</span>
            )}
          </Champ>
        </dl>
      </div>

      <div className="groupe">
        <p className="titre-groupe">La marchandise</p>
        <dl className="champs-ot">
          <Champ nom="Nature">{ordre.marchandise ?? <Absent />}</Champ>
          <Champ nom="Total chargé" calcule="somme des chargements">
            {t.quantite === null ? (
              <Absent />
            ) : (
              `${lisible(t.quantite) as string}${t.support === null ? '' : ` ${t.support}`}`
            )}
          </Champ>
          <Champ nom="Poids total" calcule="somme des chargements">
            {t.poids === null ? <Absent /> : `${lisible(t.poids) as string} kg`}
          </Champ>
        </dl>
      </div>

      <div className="groupe">
        <p className="titre-groupe">Le prix</p>
        <dl className="champs-ot">
          <Champ nom="Vente HT">
            {ordre.prix_vente === null ? (
              <Absent />
            ) : (
              <span className="grand">{enEuros(ordre.prix_vente) as string}</span>
            )}
          </Champ>
          {/* LES ANNEXES NE SE DESSINENT QUE S'IL Y EN A : « Prestations
              annexes : 0,00 » sur la majorité des ordres serait du bruit.

              LE TOTAL HT COMPREND L'INDEXATION — 02/11/2026, demande de
              Mohamed : « les OT avec indexation affichent le total et pas le
              total avant indexation ». Le prix indexé est celui que calcule
              `indexation.ts` (`prix_indexe`) ; rien n'est recalculé ici, et
              `prix_vente` reste le prix convenu. Le total ne se dessine que
              s'il diffère de la vente : annexes ou indexation. */}
          {prestations !== null && prestations.prestations.length > 0 && (
            <Champ nom="Prestations annexes" calcule="somme">
              <span className="code">+ {enEuros(prestations.total) ?? '—'}</span>
            </Champ>
          )}
          {(() => {
            const indexe = cout?.indexation.prix_indexe ?? null;
            const avecAnnexes = prestations !== null && prestations.prestations.length > 0;
            if (indexe === null && !avecAnnexes) return null;
            return (
              <Champ nom="Total HT" calcule="calculé">
                <span className="grand">
                  {enEuros(
                    caDeLOrdre(indexe ?? ordre.prix_vente, avecAnnexes ? prestations.total : null),
                  ) ?? '—'}
                </span>
              </Champ>
            );
          })()}
          <Champ nom="À la palette" calcule="calculé">
            {alaPalette === null ? (
              <Absent />
            ) : (
              <span className="code">{alaPalette} €</span>
            )}
          </Champ>
          <Champ nom="Au kilomètre" calcule="calculé">
            {auKilometre === null ? (
              <Absent />
            ) : (
              <span className="code">{auKilometre} €/km</span>
            )}
          </Champ>
        </dl>
      </div>

      {/* L'INDEXATION GASOIL, Étape 20 lot 4 — 15/09/2026.

          DEMANDÉE DEUX FOIS par Mohamed : « la surcharge ne se met pas et ne se
          calcule pas automatiquement », puis « ELLE NE SE CALCULE TOUJOURS PAS
          SUR LA FACTURE ET PAS SUR L'OT ».

          TROIS LIGNES, comme la maquette écran 4 les dessine : le prix nu, ce
          que la clause ajoute, et le prix avec indexation. Les deux premiers
          sont DISTINCTS et le restent — `prix_vente` n'est jamais écrasé, sans
          quoi la facture porterait l'indexation deux fois.

          UN GROUPE À PART DU COÛT, parce que c'est de la VENTE. Les loger
          ensemble ferait croire qu'une surcharge est une charge. */}
      <div className="groupe">
        <p className="titre-groupe">L’indexation gasoil</p>
        {cout === null ? (
          <p className="sourdine">Chargement…</p>
        ) : cout.indexation.montant === null ? (
          /* RIEN N'EST INVENTÉ QUAND IL MANQUE QUELQUE CHOSE. Un « 0,00 € » se
             lirait « la clause n'a rien donné ce mois-ci » alors qu'elle n'a
             pas été calculée — et rien ne distinguerait les deux. */
          <p className="sourdine">
            {manquesDIndexationDits([...cout.indexation.manques]) ??
              'Cet ordre n’est sous aucune grille indexée.'}
          </p>
        ) : (
          <dl className="champs-ot">
            <Champ nom="Prix de vente" calcule="saisi">
              <span className="code">{enEuros(cout.indexation.base) ?? '—'}</span>
            </Champ>
            <Champ nom="Indexation gasoil" calcule="calculée">
              <span className="code">
                {Number(cout.indexation.montant) >= 0 ? '+ ' : ''}
                {enEuros(cout.indexation.montant) ?? '—'}
              </span>
            </Champ>
            {/* D'OÙ VIENT LE CHIFFRE, en une ligne : le mois employé, la valeur
                publiée, la référence de la grille et la part. Sans elle, le
                montant serait à croire sur parole. */}
            <Champ nom="D’après" calcule="grille et mois">
              <span className="sourdine">
                {/* EN FRANÇAIS, comme partout : « septembre 2026 » et non
                    « 2026-09 ». Constaté au rendu. */}
                {cout.indexation.mois === null
                  ? '—'
                  : moisDit(cout.indexation.mois)}{' '}
                · valeur{' '}
                {enQuatreDecimales(cout.indexation.valeur_du_mois)} · réf.{' '}
                {enQuatreDecimales(cout.indexation.reference)} ·{' '}
                {Number(cout.indexation.part)} %
                {cout.indexation.grille === null ? '' : ` · ${cout.indexation.grille}`}
              </span>
            </Champ>
            <Champ nom="Prix avec indexation" calcule="somme">
              <span className="grand">
                {enEuros(cout.indexation.prix_indexe) ?? '—'}
              </span>
            </Champ>
          </dl>
        )}
      </div>

      {/* LE COÛT DE REVIENT, lot 2 du 07/09/2026.
          Il est FIGÉ : ce qui est écrit là est ce que le transport a coûté au
          moment du calcul, avec les prix de ce moment-là. Corriger un loyer
          aujourd'hui ne le réécrit pas — c'est la règle du §6. */}
      <div className="groupe">
        <p className="titre-groupe">Le coût de revient</p>
        {cout === null ? (
          <p className="sourdine">Chargement…</p>
        ) : cout.fige === null ? (
          <>
            <p className="sourdine">
              {manquesDits([...cout.calcule.manques]) ??
                'Le coût n’a pas encore été calculé.'}
            </p>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={actions.envoi}
              onClick={quandRecalculerLeCout}
            >
              Recalculer
            </button>
          </>
        ) : (
          <>
            <dl className="champs-ot">
              <Champ nom="Temps et ressources" calcule="figé">
                <span className="code">{enEuros(cout.fige.cout_temps) ?? '—'}</span>
              </Champ>
              <Champ nom="Kilométrique" calcule="figé">
                <span className="code">{enEuros(cout.fige.cout_km) ?? '—'}</span>
              </Champ>
              <Champ nom="Péage" calcule="figé">
                <span className="code">{enEuros(cout.fige.cout_peage) ?? '—'}</span>
              </Champ>
              {/* L'ACHAT NE SE DESSINE QUE S'IL Y EN A UN. Une ligne
                  « Sous-traitance : — » sur tous les transports faits en
                  propre serait du bruit sur la majorité d'entre eux ; et sur
                  un affrété, c'est la SEULE ligne qui porte un montant. */}
              {cout.fige.cout_achat !== null && (
                <Champ nom="Sous-traitance" calcule="prix d’achat">
                  <span className="code">{enEuros(cout.fige.cout_achat) ?? '—'}</span>
                </Champ>
              )}
              <Champ nom="Coût total" calcule="somme des natures">
                <span className="grand">
                  {enEuros(String(totalDuCout(cout))) ?? '—'}
                </span>
              </Champ>
              {/* LA MARGE RÉELLE : la vente moins ce que NOS moyens ont coûté.
                  Distincte de la marge d'affrètement, qui compare au prix d'un
                  sous-traitant — les confondre ferait croire qu'un transport
                  fait en propre n'a pas de coût. */}
              <Champ nom="Marge réelle" calcule="calculée">
                {(() => {
                  // SUR LE CA DE L'ORDRE, vente ET annexes — 15/09/2026.
                  // Les deux heures d'attente sont DANS le coût ci-dessus ;
                  // les comparer à la seule vente rendait la marge plus basse
                  // que la vérité.
                  const m = margeReelle(
                    caDeLOrdre(
                      ordre.prix_vente,
                      prestations === null ? null : prestations.total,
                    ),
                    totalDuCout(cout),
                  );
                  return m === null ? (
                    <Absent />
                  ) : (
                    <span className={`grand marge-${m.couleur}`}>
                      {margeReelleDite(m)}
                    </span>
                  );
                })()}
              </Champ>
              <Champ nom="Temps passé" calcule="saisi">
                {minutesDites(cout.temps_reel_minutes) ?? <Absent />}
              </Champ>
            </dl>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={actions.envoi}
              onClick={quandRecalculerLeCout}
            >
              Recalculer
            </button>
          </>
        )}
      </div>

      {/* LES PRESTATIONS ANNEXES — 15/09/2026, maquette
          `docs/maquettes/prestations-annexes.html`.

          EN PLEINE LARGEUR, et sous les deux blocs de chiffres : c'est une
          liste qui s'allonge, pas une fiche de trois champs. Elle se dessine
          MÊME VIDE, avec son bouton — un bloc qui n'apparaîtrait qu'une fois
          rempli ne s'apprendrait jamais. */}
      <div className="groupe pleine">
        <p className="titre-groupe">Prestations annexes</p>
        {prestations === null ? (
          <p className="sourdine">Chargement…</p>
        ) : prestations.prestations.length === 0 ? (
          <p className="sourdine">
            Aucune prestation annexe. Attente, immobilisation, palettes non
            rendues : ce qui se facture en plus du transport se pose ici, et
            part sur la facture sous le même numéro d’OT, dans son propre
            groupe.
          </p>
        ) : (
          <>
            <table className="tableau-annexes">
              <thead>
                <tr>
                  <th>Nature et précision</th>
                  <th>État</th>
                  <th className="a-droite-annexes">Qté</th>
                  <th className="a-droite-annexes">P.U. HT</th>
                  <th className="a-droite-annexes">Total HT</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {prestations.prestations.map((prestation) => (
                  <tr key={prestation.id}>
                    <td>
                      <span className="nature-annexe">{prestation.nature}</span>
                      {prestation.detail !== null && (
                        <span className="precision-annexe">{prestation.detail}</span>
                      )}
                    </td>
                    <td>
                      {prestation.facturee ? (
                        <span className="pastille-annexe pastille-facturee">
                          Facturée
                        </span>
                      ) : (
                        <span className="pastille-annexe pastille-a-facturer">
                          À facturer
                        </span>
                      )}
                    </td>
                    <td className="a-droite-annexes code">
                      {quantiteDite(prestation.quantite)}{' '}
                      {uniteDite(prestation.unite)}
                    </td>
                    <td className="a-droite-annexes code">
                      {enEuros(prestation.prix_unitaire) ?? '—'}
                    </td>
                    <td className="a-droite-annexes code">
                      {enEuros(prestation.total) ?? '—'}
                    </td>
                    <td className="a-droite-annexes">
                      {/* UNE PRESTATION FACTURÉE NE PROPOSE RIEN : ni
                          « Modifier », ni « Retirer ». Les afficher grisés
                          inviterait à cliquer pour comprendre pourquoi. */}
                      {!prestation.facturee && (
                        <>
                          <button
                            className="bouton bouton-discret"
                            type="button"
                            disabled={actions.envoi}
                            onClick={() => quandOuvrirPrestation(prestation)}
                          >
                            Modifier
                          </button>{' '}
                          <button
                            className="bouton bouton-discret"
                            type="button"
                            disabled={actions.envoi}
                            onClick={() => quandRetirerPrestation(prestation.id)}
                          >
                            Retirer
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="total-annexes">
              Total annexes HT{' '}
              <span className="grand">{enEuros(prestations.total) ?? '—'}</span>
            </p>
          </>
        )}
        <p className="ajout-annexe">
          <button
            className="bouton"
            type="button"
            disabled={actions.envoi}
            onClick={() => quandOuvrirPrestation(null)}
          >
            + Ajouter une prestation
          </button>
        </p>
      </div>

      {ordre.note !== null && (
        <div className="groupe pleine">
          <p className="titre-groupe">Note</p>
          <p className="note-ot">{ordre.note}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function FormulaireOrdre({
  edition,
  clients,
  centres,
  gabarits,
  quandChamp,
  quandNouveauClient,
  quandEnregistrer,
  quandAnnuler,
}: {
  readonly edition: EditionOrdre;
  readonly clients: readonly LigneListe[];
  readonly centres: readonly LigneReglee[];
  readonly gabarits: readonly Vehicule[];
  readonly quandChamp: (cle: keyof SaisieOrdre, valeur: string) => void;
  /** Ouvre la boîte de création d'un client, par-dessus le formulaire. */
  readonly quandNouveauClient: () => void;
  /**
   * Vrai dans la section de création. Il commandait l'affichage du champ
   * « N° », retiré le 31/10/2026 (le numéro est attribué). La propriété reste
   * pour ne pas toucher les appelants ; le formulaire ne la lit plus.
   */
  readonly creation?: boolean;
  readonly quandEnregistrer?: () => void;
  readonly quandAnnuler?: () => void;
}) {
  const { saisie, fautes } = edition;
  const faute = (cle: string) =>
    fautes[cle] === undefined ? null : (
      <span className="erreur-champ">{fautes[cle]}</span>
    );

  return (
    <div className="fiche-modif-fn">
      {/* LA MODIFICATION D'UN ORDRE EXISTANT, EN BLOCS — F3, 01/11/2026.
          Mêmes blocs que la fiche neuve (maquette validée le 31/10) : la
          commande, le prix avec le choix de l'indexation, le véhicule
          demandé, le packaging. Les étapes et les documents gardent leurs
          onglets : ils portent le suivi (fait, litiges, échanges), que la
          fiche neuve n'a pas. */}
      <section className="fn-bloc">
        <h3 className="titre-bloc-ot">Commande</h3>
        <div className="grille-bloc-ot fn-trois">
          <div className="fn-champ">
            <label htmlFor="ot-client">Client *</label>
            <ChoixCherchable
              id="ot-client"
              choix={clients
                .filter((c) => c.actif || c.id === saisie.client_id)
                .map((c) => ({ id: c.id, libelle: c.nom }))}
              valeur={saisie.client_id}
              invite="Chercher un client…"
              fautif={fautes['client_id'] !== undefined}
              quandChoisir={(id) => quandChamp('client_id', id)}
            />
            {faute('client_id')}
            <button className="fn-lien" type="button" onClick={quandNouveauClient}>
              + Nouveau client
            </button>
          </div>
          <div className="fn-champ">
            <label htmlFor="ot-centre">Activité *</label>
            <select
              id="ot-centre"
              className="boite"
              value={saisie.centre_id}
              onChange={(e) => quandChamp('centre_id', e.target.value)}
            >
              <option value="">Choisir une activité…</option>
              {centres
                .filter((c) => c.actif || c.id === saisie.centre_id)
                .map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.nom}
                  </option>
                ))}
            </select>
            {faute('centre_id')}
          </div>
          <div className="fn-champ">
            <label htmlFor="ot-reference">Référence client</label>
            <input
              id="ot-reference"
              className="boite"
              type="text"
              value={saisie.reference_client}
              onChange={(e) => quandChamp('reference_client', e.target.value)}
            />
            {faute('reference_client')}
          </div>
        </div>
      </section>

      <div className="ligne-prix-vehicule">
        <section className="fn-bloc">
          <h3 className="titre-bloc-ot">Prix</h3>
          <div className="grille-bloc-ot fn-trois">
            <div className="fn-champ">
              <label htmlFor="ot-prix">Prix de vente HT *</label>
              <input
                id="ot-prix"
                className="boite"
                type="text"
                inputMode="decimal"
                value={saisie.prix_vente}
                onChange={(e) => quandChamp('prix_vente', e.target.value)}
              />
              {faute('prix_vente')}
            </div>
            <div className="fn-champ">
              {/* LE CHOIX FORCÉ OU NON — migration 20261101100000. Le montant
                  et son détail restent dans « Coût et marge », qui les lit
                  du même calcul que la facture. */}
              <label htmlFor="ot-indexation">Indexation gasoil</label>
              <select
                id="ot-indexation"
                className="boite"
                value={saisie.indexation}
                onChange={(e) => quandChamp('indexation', e.target.value)}
              >
                <option value="">Selon la grille du client</option>
                <option value="avec">Avec</option>
                <option value="sans">Sans</option>
              </select>
            </div>
            <div className="fn-champ">
              <label htmlFor="ot-distance">Distance (km)</label>
              <input
                id="ot-distance"
                className="boite"
                type="text"
                inputMode="numeric"
                value={saisie.distance_km}
                onChange={(e) => quandChamp('distance_km', e.target.value)}
              />
              {faute('distance_km')}
            </div>
          </div>
        </section>

        <section className="fn-bloc">
          <h3 className="titre-bloc-ot">Véhicule</h3>
          <div className="fn-champ">
            <label htmlFor="ot-gabarit">Véhicule demandé</label>
            <select
              id="ot-gabarit"
              className="boite"
              value={saisie.gabarit_code}
              onChange={(e) => quandChamp('gabarit_code', e.target.value)}
            >
              <option value="">Sans préférence</option>
              {gabarits.map((g) => (
                <option value={g.code} key={g.code}>
                  {g.libelle}
                </option>
              ))}
            </select>
          </div>
        </section>
      </div>

      <section className="fn-bloc">
        <h3 className="titre-bloc-ot">Packaging</h3>
        <div className="fn-champ fn-nature">
          <label htmlFor="ot-marchandise">Nature de la marchandise</label>
          {/* TROIS CHOIX depuis le 31/10/2026. Une valeur ancienne hors de la
              liste (« Produits secs ») reste proposée telle quelle : les ordres
              déjà enregistrés ne sont pas touchés. */}
          <select
            id="ot-marchandise"
            className="boite"
            value={saisie.marchandise}
            onChange={(e) => quandChamp('marchandise', e.target.value)}
          >
            {saisie.marchandise !== '' &&
              !(NATURES_MARCHANDISE as readonly string[]).includes(saisie.marchandise) && (
                <option value={saisie.marchandise}>{saisie.marchandise}</option>
              )}
            {saisie.marchandise === '' && <option value="">Non renseignée</option>}
            {NATURES_MARCHANDISE.map((n) => (
              <option value={n} key={n}>
                {n}
              </option>
            ))}
          </select>
          {faute('marchandise')}
        </div>
      </section>

      {/* LA NOTE DE L'ORDRE n'est plus sur la fiche (cadrage §3). Un ordre
          ancien qui en porte une la garde visible et modifiable : la faire
          disparaître de l'écran la ferait oublier sans l'effacer. */}
      {edition.initiale.note.trim() !== '' && (
        <section className="fn-bloc">
          <h3 className="titre-bloc-ot">Note</h3>
          <textarea
            id="ot-note"
            className="boite haute"
            value={saisie.note}
            onChange={(e) => quandChamp('note', e.target.value)}
          />
          {faute('note')}
        </section>
      )}

      {edition.refus !== null && <p className="bandeau">{edition.refus}</p>}

      {quandEnregistrer !== undefined && (
        <div className="pied-creation-site">
          <button
            className="bouton"
            type="button"
            disabled={edition.envoi}
            onClick={quandEnregistrer}
          >
            {edition.envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={edition.envoi}
            onClick={quandAnnuler}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * CE QUE LA LECTURE A TROUVÉ, ET CE QU'ELLE N'A PAS TROUVÉ — lot 124.
 *
 * IL NE DÉCIDE RIEN ET NE CALCULE RIEN : la liste des points vient de
 * `pointsAVerifier`, qui se teste sans navigateur. Ici on ne fait que
 * dessiner — c'est la seule façon d'être sûr que ce qui est écrit est bien ce
 * qui a été calculé.
 *
 * LES BLOQUANTS D'ABORD, et séparés des simples attentions : ce qui empêche
 * d'enregistrer et ce qui mérite un coup d'œil ne se lisent pas de la même
 * façon, et les mêler ferait négliger les deux.
 */
function BandeauLecture({
  lecture,
  quandFermer,
}: {
  readonly lecture: EtatLecture;
  readonly quandFermer: () => void;
}) {
  if (lecture.quoi !== 'faite') return null;
  const bloquants = lecture.points.filter((x: PointAVerifier) => x.gravite === 'bloquant');
  const attentions = lecture.points.filter((x: PointAVerifier) => x.gravite === 'attention');

  return (
    <div className="bandeau-lecture">
      <div className="bandeau-lecture-tete">
        <b>Pré-rempli d’après « {lecture.nom} »</b>
        <span className="aide-saisie">
          Relisez chaque champ : une lecture automatique se trompe sur n’importe lequel.
        </span>
        <button className="bouton bouton-discret" type="button" onClick={quandFermer}>
          Fermer
        </button>
      </div>

      {lecture.autres > 0 && (
        /*
          ON NE PERD PAS LES AUTRES TRANSPORTS EN SILENCE. Le formulaire n'en
          crée qu'un à la fois ; sans cette ligne, deux transports d'un même
          document disparaîtraient sans que personne ne s'en aperçoive.
        */
        <p className="bandeau-lecture-autres">
          Ce document porte {lecture.autres + 1} transports. Celui-ci est le premier :
          les {lecture.autres} autres sont à saisir séparément.
        </p>
      )}

      {bloquants.length > 0 && (
        <div className="bandeau-lecture-bloc">
          <p className="titre-section">À compléter avant d’enregistrer</p>
          <ul>
            {bloquants.map((x: PointAVerifier) => (
              <li key={x.texte}>{x.texte}</li>
            ))}
          </ul>
        </div>
      )}

      {attentions.length > 0 && (
        <div className="bandeau-lecture-bloc bandeau-lecture-doux">
          <p className="titre-section">À vérifier</p>
          <ul>
            {attentions.map((x: PointAVerifier) => (
              <li key={x.texte}>{x.texte}</li>
            ))}
          </ul>
        </div>
      )}

      {lecture.remarques.length > 0 && (
        <details className="bandeau-lecture-detail">
          <summary>Ce que la lecture a signalé ({lecture.remarques.length})</summary>
          <ul>
            {lecture.remarques.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * LA FICHE D'UN ORDRE NEUF — F3, refonte de la saisie d'OT (31/10/2026).
 *
 * Disposition de la maquette validée par Mohamed le 31/10 (canevas « Saisie OT
 * — style portail », `Main.dc.html`), de haut en bas :
 *   1. le titre, Annuler et Enregistrer ;
 *   2. Commande, puis Prix (2/3) et Véhicule (1/3) ; à droite le volet ;
 *   3. l'Itinéraire sur toute la largeur ;
 *   4. le Packaging sur toute la largeur.
 *
 * RIEN N'A CHANGÉ DANS CE QUI EST ENVOYÉ : les mêmes champs partent par les
 * mêmes gestionnaires (`quandChampOrdre`, `quandChampEtapeCreation`…). Seule la
 * place de chaque champ change, avec trois exceptions voulues :
 *   - la note de l'ordre n'est plus saisie (cadrage §3, confirmé le 31/10) ;
 *   - le véhicule se choisit UNE fois et se porte sur toutes les étapes, sauf
 *     relais (cadrage §5). Le chauffeur et la remorque ne sont plus demandés :
 *     l'API les recopie de l'attelage du jour (`completerLEquipage`) ;
 *   - la marchandise se saisit dans Packaging, sur les CHARGEMENTS. Les
 *     livraisons la reprennent par le report qui existait déjà
 *     (`reporterSurLesLivraisons`). La table des marchandises viendra en F4.
 *
 * L'état « plié » des étapes et le menu « Ajouter une étape » sont de
 * l'affichage pur : ils vivent ici et ne partent nulle part.
 */
/**
 * LES LIGNES DU PACKAGING — F4, 01/11/2026. Partagées par la fiche neuve et
 * par la modification du Packaging d'un ordre existant : un seul dessin, un
 * seul calcul. Cadrage §7 et §9.
 */
function LignesPackaging({
  lignes,
  etapes,
  supports,
  quandAjouter,
  quandChamp,
  quandSupport,
  quandRetirer,
}: {
  readonly lignes: readonly LigneMarchandise[];
  /** Le type et le nom de chaque étape, dans l'ordre : les lignes y pointent par rang. */
  readonly etapes: readonly { readonly type: string; readonly nom: string }[];
  readonly supports: readonly LigneReglee[];
  readonly quandAjouter: () => void;
  readonly quandChamp: (rang: number, changement: Partial<LigneMarchandise>) => void;
  readonly quandSupport: (rang: number, nom: string) => void;
  readonly quandRetirer: (rang: number) => void;
}) {
  const types = etapes.map((e) => e.type);
  const totaux = totauxDesMarchandises(lignes);
  return (
    <>
        {lignes.map((l, rang) => {
          const fl = validerLigneMarchandise(l, types);
          const autre = l.support === AUTRE_DIMENSION;
          const ml = mlUnitaireDeLaLigne(l);
          const n = String(rang);
          const fauteL = (cle: string) =>
            fl[cle] === undefined ? null : <span className="erreur-champ">{fl[cle]}</span>;
          return (
            <div className="ligne-marchandise-fn" key={`march-${n}`}>
              {/* « CHARGÉ À → LIVRÉ À » N'APPARAÎT QU'À PARTIR DE TROIS ÉTAPES
                  (cadrage §7) : avec un chargement et une livraison,
                  l'affectation est d'office et rien n'est à dire. */}
              {etapes.length >= 3 && (
                <div className="affectation-fn">
                  <label htmlFor={`march-charge-${n}`}>Chargé à</label>
                  <select
                    id={`march-charge-${n}`}
                    className="boite"
                    value={String(l.chargement)}
                    onChange={(e) =>
                      quandChamp(rang, { chargement: Number(e.target.value) })
                    }
                  >
                    {etapes.map((e, i) =>
                      e.type === 'chargement' ? (
                        <option value={String(i)} key={`c${String(i)}`}>
                          {e.nom === '' ? `Étape ${String(i + 1)}` : e.nom}
                        </option>
                      ) : null,
                    )}
                  </select>
                  <label htmlFor={`march-livre-${n}`}>→ livré à</label>
                  <select
                    id={`march-livre-${n}`}
                    className="boite"
                    value={String(l.livraison)}
                    onChange={(e) =>
                      quandChamp(rang, { livraison: Number(e.target.value) })
                    }
                  >
                    {etapes.map((e, i) =>
                      e.type === 'livraison' ? (
                        <option value={String(i)} key={`l${String(i)}`}>
                          {e.nom === '' ? `Étape ${String(i + 1)}` : e.nom}
                        </option>
                      ) : null,
                    )}
                  </select>
                  {fauteL('chargement')}
                  {fauteL('livraison')}
                </div>
              )}
              <div className="grille-marchandise-fn">
                <div className="fn-champ">
                  <label htmlFor={`march-support-${n}`}>Support</label>
                  <select
                    id={`march-support-${n}`}
                    className="boite"
                    value={l.support}
                    onChange={(e) => quandSupport(rang, e.target.value)}
                  >
                    <option value="">Choisir…</option>
                    {supportsAvec(
                      l.support === AUTRE_DIMENSION ? '' : l.support,
                      supports.map((x) => x.nom),
                    ).map((x) => (
                      <option value={x} key={x}>
                        {x}
                      </option>
                    ))}
                    <option value={AUTRE_DIMENSION}>{AUTRE_DIMENSION}</option>
                  </select>
                  {ml !== null && (
                    <span className="calcule">{mlLisible(ml)} ml l’unité</span>
                  )}
                </div>
                <div className="fn-champ">
                  <label htmlFor={`march-quantite-${n}`}>Quantité</label>
                  <input
                    id={`march-quantite-${n}`}
                    className="boite"
                    type="text"
                    inputMode="decimal"
                    value={l.quantite}
                    onChange={(e) => quandChamp(rang, { quantite: e.target.value })}
                  />
                  {fauteL('quantite')}
                </div>
                {/* LONGUEUR ET LARGEUR VIENNENT DU SUPPORT et ne se modifient
                    pas ; « Autre dimension » les ouvre. LA HAUTEUR RESTE
                    TOUJOURS MODIFIABLE (décision du 31/10). */}
                <div className="fn-champ">
                  <label htmlFor={`march-longueur-${n}`}>Longueur (cm)</label>
                  <input
                    id={`march-longueur-${n}`}
                    className="boite"
                    type="text"
                    inputMode="numeric"
                    readOnly={!autre}
                    value={l.longueur}
                    onChange={(e) => quandChamp(rang, { longueur: e.target.value })}
                  />
                  {fauteL('longueur')}
                </div>
                <div className="fn-champ">
                  <label htmlFor={`march-largeur-${n}`}>Largeur (cm)</label>
                  <input
                    id={`march-largeur-${n}`}
                    className="boite"
                    type="text"
                    inputMode="numeric"
                    readOnly={!autre}
                    value={l.largeur}
                    onChange={(e) => quandChamp(rang, { largeur: e.target.value })}
                  />
                  {fauteL('largeur')}
                </div>
                <div className="fn-champ">
                  <label htmlFor={`march-hauteur-${n}`}>Hauteur (cm)</label>
                  <input
                    id={`march-hauteur-${n}`}
                    className="boite"
                    type="text"
                    inputMode="numeric"
                    value={l.hauteur}
                    onChange={(e) => quandChamp(rang, { hauteur: e.target.value })}
                  />
                  {fauteL('hauteur')}
                </div>
                <div className="fn-champ">
                  <label htmlFor={`march-poids-${n}`}>Poids (kg)</label>
                  <input
                    id={`march-poids-${n}`}
                    className="boite"
                    type="text"
                    inputMode="numeric"
                    value={l.poids}
                    onChange={(e) => quandChamp(rang, { poids: e.target.value })}
                  />
                  {fauteL('poids')}
                </div>
                <label className="case-ot consigne-fn">
                  <input
                    type="checkbox"
                    checked={l.consigne}
                    onChange={(e) => quandChamp(rang, { consigne: e.target.checked })}
                  />
                  Palettes consignées
                </label>
              </div>
              <div className="pied-marchandise-fn">
                <label className="case-ot">
                  <input
                    type="checkbox"
                    checked={l.gerbable}
                    onChange={(e) => quandChamp(rang, { gerbable: e.target.checked })}
                  />
                  Gerbable
                </label>
                {l.gerbable && (
                  <span className="par-fn">
                    <label htmlFor={`march-etages-${n}`}>par</label>
                    <input
                      id={`march-etages-${n}`}
                      className="boite"
                      type="text"
                      inputMode="numeric"
                      value={l.etages}
                      onChange={(e) => quandChamp(rang, { etages: e.target.value })}
                    />
                    {fauteL('etages')}
                  </span>
                )}
                <button
                  className="fn-lien retirer-marchandise-fn"
                  type="button"
                  onClick={() => quandRetirer(rang)}
                >
                  Retirer
                </button>
              </div>
            </div>
          );
        })}
        <button
          className="bouton bouton-discret ajout-marchandise-fn"
          type="button"
          onClick={quandAjouter}
        >
          + Ajouter une marchandise
        </button>
        <p className="totaux-ot">
          <span>
            Métrage linéaire total :{' '}
            <strong>{totaux.ml === null ? '—' : `${mlLisible(totaux.ml)} ml`}</strong>
          </span>
          <span>
            Poids total :{' '}
            <strong>
              {totaux.poids === null
                ? '—'
                : `${totaux.poids.toLocaleString('fr-FR')} kg`}
            </strong>
          </span>
        </p>
    </>
  );
}

/** La valeur du choix « Affréter à un transporteur… » : ce n'est pas un camion. */
const AFFRETER = '+affreter';

function VueCreation({
  p,
  creation,
}: {
  readonly p: ProprietesVueOrdres;
  readonly creation: EtatCreation;
}) {
  const [pliees, setPliees] = useState<ReadonlySet<number>>(new Set());
  const [menuEtape, setMenuEtape] = useState(false);
  const [volet, setVolet] = useState<'execution' | 'documents'>('execution');
  const [typeDocument, setTypeDocument] = useState('');
  const [tropGros, setTropGros] = useState<string | null>(null);
  const documents = creation.documents ?? [];
  const { saisie, fautes } = creation.ordre;
  const relais = creation.relais === true;
  // L'INDEXATION : déduite de la grille (une clause s'applique → Avec), sauf
  // si elle a été forcée. `idx` est l'aperçu calculé par l'API.
  const idx = p.indexationCreation;
  const indexationDeduite: 'avec' | 'sans' = idx?.clause === true ? 'avec' : 'sans';
  const indexationActive = saisie.indexation === '' ? indexationDeduite : saisie.indexation;
  const prestations = creation.prestations ?? [];
  const totalHt = totalHtDeLaSaisie(
    saisie.prix_vente,
    indexationActive === 'avec' ? (idx?.montant ?? null) : null,
    prestations,
  );
  const faute = (cle: string) =>
    fautes[cle] === undefined ? null : (
      <span className="erreur-champ">{fautes[cle]}</span>
    );

  // LE VÉHICULE DU HAUT est celui de la première étape : sans relais, toutes
  // portent le même, puisque le choisir l'écrit sur chacune.
  const vehiculeCommun = creation.etapes[0]?.saisie.vehicule_id ?? '';
  const poserVehiculePartout = (id: string) => {
    creation.etapes.forEach((_, rang) => {
      p.quandChampEtapeCreation(rang, 'vehicule_id', id);
      p.quandChampEtapeCreation(rang, 'chauffeur_id', '');
      p.quandChampEtapeCreation(rang, 'remorque_id', '');
    });
  };

  // LES LIGNES DU PACKAGING — F4. Déduites des chargements tant qu'on n'y a
  // pas touché.
  const lignes = lignesDe(creation, p.supports);

  const basculerPli = (rang: number) =>
    setPliees((actuelles) => {
      const suite = new Set(actuelles);
      if (suite.has(rang)) suite.delete(rang);
      else suite.add(rang);
      return suite;
    });

  return (
    <div className="corps corps-ot fiche-neuve">
      <div className="tete-fiche-neuve">
        <button className="retour" type="button" onClick={p.quandRetour}>
          ← {p.libelleRetour}
        </button>
        <h2 className="titre-fiche-neuve">Nouvel ordre de transport</h2>
        <div className="actions-fiche-neuve">
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={creation.ordre.envoi}
            onClick={p.quandRetour}
          >
            Annuler
          </button>
          <button
            className="bouton"
            type="button"
            disabled={creation.ordre.envoi}
            onClick={p.quandEnregistrerCreation}
          >
            {creation.ordre.envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <BandeauLecture lecture={p.lecture} quandFermer={p.quandOublierLecture} />
      {creation.ordre.refus !== null && (
        <p className="bandeau">{creation.ordre.refus}</p>
      )}

      <div className="haut-fiche-neuve">
        <div className="colonne-fiche-neuve">
          <section className="fn-bloc">
            <h3 className="titre-bloc-ot">Commande</h3>
            <div className="grille-bloc-ot fn-trois">
              <div className="fn-champ">
                <label htmlFor="ot-client">Client *</label>
                <ChoixCherchable
                  id="ot-client"
                  choix={p.clients
                    .filter((c) => c.actif || c.id === saisie.client_id)
                    .map((c) => ({ id: c.id, libelle: c.nom }))}
                  valeur={saisie.client_id}
                  invite="Chercher un client…"
                  fautif={fautes['client_id'] !== undefined}
                  quandChoisir={(id) => p.quandChampOrdre('client_id', id)}
                />
                {faute('client_id')}
                <button
                  className="fn-lien"
                  type="button"
                  onClick={p.quandNouveauClient}
                >
                  + Nouveau client
                </button>
              </div>
              <div className="fn-champ">
                <label htmlFor="ot-centre">Activité *</label>
                <select
                  id="ot-centre"
                  className="boite"
                  value={saisie.centre_id}
                  onChange={(e) => p.quandChampOrdre('centre_id', e.target.value)}
                >
                  <option value="">Choisir une activité…</option>
                  {p.centres
                    .filter((c) => c.actif || c.id === saisie.centre_id)
                    .map((c) => (
                      <option value={c.id} key={c.id}>
                        {c.nom}
                      </option>
                    ))}
                </select>
                {faute('centre_id')}
              </div>
              <div className="fn-champ">
                <label htmlFor="ot-reference">Référence client</label>
                <input
                  id="ot-reference"
                  className="boite"
                  type="text"
                  value={saisie.reference_client}
                  onChange={(e) => p.quandChampOrdre('reference_client', e.target.value)}
                />
                {faute('reference_client')}
              </div>
            </div>
          </section>

          <div className="ligne-prix-vehicule">
            <section className="fn-bloc">
              <h3 className="titre-bloc-ot">Prix</h3>
              <div className="grille-bloc-ot fn-trois">
                <div className="fn-champ">
                  <label htmlFor="ot-prix">Prix de vente HT *</label>
                  <input
                    id="ot-prix"
                    className="boite"
                    type="text"
                    inputMode="decimal"
                    value={saisie.prix_vente}
                    onChange={(e) => p.quandChampOrdre('prix_vente', e.target.value)}
                  />
                  {faute('prix_vente')}
                </div>
                <div className="fn-champ fn-deux">
                  <span className="libelle-fn">Indexation gasoil</span>
                  {/* SANS / AVEC — cadrage §4 et migration
                      20261101100000_indexation_forcee.sql. La grille du client
                      DÉCIDE ; cliquer l'autre choix le force, recliquer celui
                      que la grille aurait pris rend la décision à la grille. */}
                  <div className="pilules-ot" role="group" aria-label="Indexation gasoil">
                    {(['sans', 'avec'] as const).map((choix) => (
                      <button
                        type="button"
                        key={choix}
                        className="pilule-ot"
                        aria-pressed={indexationActive === choix}
                        onClick={() =>
                          p.quandChampOrdre(
                            'indexation',
                            choix === indexationDeduite ? '' : choix,
                          )
                        }
                      >
                        {choix === 'sans' ? 'Sans' : 'Avec'}
                      </button>
                    ))}
                  </div>
                </div>
                {indexationActive === 'avec' && idx !== null && idx !== undefined && (
                  <div className="detail-indexation fn-tout">
                    {idx.montant === null ? (
                      <span>
                        {manquesDIndexationDits([...idx.manques]) ??
                          'La surcharge gasoil ne se calcule pas encore.'}
                      </span>
                    ) : (
                      <>
                        <span>
                          Grille <strong>{idx.grille ?? '—'}</strong>
                        </span>
                        <span>
                          Indice de référence <strong>{enQuatreDecimales(idx.reference)}</strong>
                        </span>
                        <span>
                          Part gasoil <strong>{Number(idx.part)} %</strong>
                        </span>
                        <span>
                          Indice de {idx.mois === null ? '—' : moisDit(idx.mois)}{' '}
                          <strong>{enQuatreDecimales(idx.valeur_du_mois)}</strong>
                        </span>
                        <span>
                          Indexation{' '}
                          <strong>
                            {Number(idx.montant) >= 0 ? '+ ' : ''}
                            {enEuros(idx.montant) ?? '—'}
                          </strong>
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {prestations.map((l, rang) => (
                <div className="ligne-prestation-fn" key={`prest-${String(rang)}`}>
                  <div className="fn-champ">
                    <label htmlFor={`prest-nature-${String(rang)}`}>Prestation annexe</label>
                    <select
                      id={`prest-nature-${String(rang)}`}
                      className="boite"
                      value={l.natureId}
                      onChange={(e) =>
                        p.quandChampPrestationCreation(rang, 'natureId', e.target.value)
                      }
                    >
                      <option value="">—</option>
                      {p.naturesPrestation
                        .filter((n) => n.actif || n.id === l.natureId)
                        .map((n) => (
                          <option value={n.id} key={n.id}>
                            {n.nom}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="fn-champ">
                    <label htmlFor={`prest-detail-${String(rang)}`}>Détail</label>
                    <input
                      id={`prest-detail-${String(rang)}`}
                      className="boite"
                      type="text"
                      value={l.detail}
                      onChange={(e) =>
                        p.quandChampPrestationCreation(rang, 'detail', e.target.value)
                      }
                    />
                  </div>
                  <div className="fn-champ">
                    <label htmlFor={`prest-quantite-${String(rang)}`}>Quantité</label>
                    <input
                      id={`prest-quantite-${String(rang)}`}
                      className="boite"
                      type="text"
                      inputMode="decimal"
                      value={l.quantite}
                      onChange={(e) =>
                        p.quandChampPrestationCreation(rang, 'quantite', e.target.value)
                      }
                    />
                  </div>
                  <div className="fn-champ">
                    <label htmlFor={`prest-pu-${String(rang)}`}>Prix unitaire</label>
                    <input
                      id={`prest-pu-${String(rang)}`}
                      className="boite"
                      type="text"
                      inputMode="decimal"
                      value={l.prixUnitaire}
                      onChange={(e) =>
                        p.quandChampPrestationCreation(rang, 'prixUnitaire', e.target.value)
                      }
                    />
                  </div>
                  <button
                    className="bouton-icone-ot retirer-prestation-fn"
                    type="button"
                    aria-label="Retirer la prestation"
                    onClick={() => p.quandRetirerPrestationCreation(rang)}
                  >
                    ×
                  </button>
                </div>
              ))}

              <div className="pied-prix-fn">
                <button
                  className="fn-lien"
                  type="button"
                  onClick={p.quandAjouterPrestationCreation}
                >
                  + Ajouter une prestation
                </button>
                <span className="total-fn">
                  Total HT {totalHt === null ? '—' : (enEuros(totalHt) ?? '—')}
                </span>
              </div>
            </section>

            <section className="fn-bloc">
              <h3 className="titre-bloc-ot">Véhicule</h3>
              {/* « VÉHICULE DEMANDÉ » A QUITTÉ CE BLOC — lot 154 : il se choisit
                  en icônes, sous le Packaging. Restent ici le camion et le relais. */}
              {!relais && (
                <div className="fn-champ">
                  <label htmlFor="ot-vehicule">Véhicule</label>
                  <select
                    id="ot-vehicule"
                    className="boite"
                    value={creation.affreter === true ? AFFRETER : vehiculeCommun}
                    onChange={(e) => {
                      if (e.target.value === AFFRETER) {
                        p.quandAffreterCreation(true);
                        return;
                      }
                      if (creation.affreter === true) p.quandAffreterCreation(false);
                      poserVehiculePartout(e.target.value);
                    }}
                  >
                    <option value="">À affecter</option>
                    <option value={AFFRETER}>Affréter à un transporteur…</option>
                    {p.vehicules
                      .filter((v) => v.actif === true || v.id === vehiculeCommun)
                      .map((v) => (
                        <option value={v.id} key={v.id}>
                          {texteParc(v, 'immatriculation') ?? v.id}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <label className="case-ot">
                <input
                  type="checkbox"
                  checked={relais}
                  onChange={(e) => p.quandRelaisCreation(e.target.checked)}
                />
                Relais : un véhicule par étape
              </label>
            </section>
          </div>
        </div>

        <aside className="fn-bloc volet-fiche-neuve">
          <div className="pilules-ot" role="tablist">
            {(['execution', 'documents'] as const).map((o) => (
              <button
                type="button"
                role="tab"
                key={o}
                className="pilule-ot"
                aria-selected={volet === o}
                aria-pressed={volet === o}
                onClick={() => setVolet(o)}
              >
                {o === 'execution'
                  ? 'Exécution'
                  : `Documents${documents.length > 0 ? ` (${String(documents.length)})` : ''}`}
              </button>
            ))}
          </div>
          {volet === 'execution' ? (
            <ol className="fil-etapes-ot">
              {creation.etapes.map((e, rang) => (
                <li key={`fil-${String(rang)}`}>
                  <span className="rond-fil" />
                  <span className="texte-fil">
                    <span className="quoi-fil">
                      {e.saisie.type === 'chargement' ? 'Chargement' : 'Livraison'}
                      {e.saisie.site_nom === '' ? '' : ` — ${e.saisie.site_nom}`}
                    </span>
                    <span className="quand-fil">
                      {[
                        e.saisie.jour === '' ? null : dateCourte(e.saisie.jour),
                        plageHoraire(e.saisie.heure_debut, e.saisie.heure_fin),
                      ]
                        .filter((x) => x !== null && x !== '')
                        .join(' · ')}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="documents-fn">
              {documents.map((d, rang) => (
                <div className="document-fn" key={`doc-${String(rang)}`}>
                  <span className="nom-document-fn">{d.fichier?.name ?? '—'}</span>
                  <span className="type-document-fn">
                    {p.typesDocument.find((t) => t.id === d.typeId)?.nom ?? 'Sans type'}
                  </span>
                  <button
                    className="bouton-icone-ot"
                    type="button"
                    aria-label="Retirer ce document"
                    onClick={() => p.quandRetirerDocumentCreation(rang)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="fn-champ">
                <label htmlFor="doc-type-neuf">Type</label>
                <select
                  id="doc-type-neuf"
                  className="boite"
                  value={typeDocument}
                  onChange={(e) => setTypeDocument(e.target.value)}
                >
                  <option value="">Sans type</option>
                  {p.typesDocument
                    .filter((t) => t.actif)
                    .map((t) => (
                      <option value={t.id} key={t.id}>
                        {t.nom}
                      </option>
                    ))}
                </select>
              </div>
              <label className="bouton bouton-discret depot-fn">
                + Ajouter un fichier
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const fichier = e.target.files?.[0] ?? null;
                    e.target.value = '';
                    if (fichier === null) return;
                    if (fichier.size > POIDS_MAXIMUM) {
                      setTropGros(fichier.name);
                      return;
                    }
                    setTropGros(null);
                    p.quandAjouterDocumentCreation({ typeId: typeDocument, fichier });
                  }}
                />
              </label>
              {/* PRENDRE UNE PHOTO — lot 133, maquette téléphone du 01/11/2026.
                  Le même dépôt que ci-dessus, mais qui ouvre l'appareil photo
                  du téléphone (`capture`). Caché sur ordinateur, qui n'en a
                  pas. */}
              <label className="bouton bouton-discret depot-fn photo-tel">
                Prendre une photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    const fichier = e.target.files?.[0] ?? null;
                    e.target.value = '';
                    if (fichier === null) return;
                    if (fichier.size > POIDS_MAXIMUM) {
                      setTropGros(fichier.name);
                      return;
                    }
                    setTropGros(null);
                    p.quandAjouterDocumentCreation({ typeId: typeDocument, fichier });
                  }}
                />
              </label>
              {tropGros !== null && (
                <span className="erreur-champ">« {tropGros} » dépasse 20 Mo.</span>
              )}
            </div>
          )}
        </aside>
      </div>

      <section className="fn-bloc">
        <h3 className="titre-bloc-ot">Itinéraire</h3>
        <div className="etapes-ot">
          {creation.etapes.map((edition, rang) => (
            <EtapeNeuve
              key={`etape-${String(rang)}`}
              p={p}
              edition={edition}
              rang={rang}
              total={creation.etapes.length}
              pliee={pliees.has(rang)}
              relais={relais}
              quandPlier={() => basculerPli(rang)}
            />
          ))}
        </div>
        <div className="pied-itineraire">
          <div className="cadre-ajout-etape">
            <button
              className="bouton bouton-discret"
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuEtape}
              onClick={() => setMenuEtape((o) => !o)}
            >
              + Ajouter une étape
            </button>
            {menuEtape && (
              <div className="menu-deroulant" role="menu">
                {(['chargement', 'livraison'] as const).map((type) => (
                  <button
                    type="button"
                    role="menuitem"
                    key={type}
                    onClick={() => {
                      p.quandAjouterEtapeCreation(type);
                      setMenuEtape(false);
                    }}
                  >
                    {type === 'chargement' ? 'Chargement' : 'Livraison'}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="fn-champ distance-ot">
            <label htmlFor="ot-distance">Distance totale (km)</label>
            <input
              id="ot-distance"
              className="boite"
              type="text"
              inputMode="numeric"
              value={saisie.distance_km}
              onChange={(e) => p.quandChampOrdre('distance_km', e.target.value)}
            />
            {faute('distance_km')}
          </div>
        </div>
      </section>

      <section className="fn-bloc">
        <h3 className="titre-bloc-ot">Packaging</h3>
        <div className="pilules-ot" role="group" aria-label="Nature de la marchandise">
          {saisie.marchandise !== '' &&
            !(NATURES_MARCHANDISE as readonly string[]).includes(saisie.marchandise) && (
              <button type="button" aria-pressed="true" className="pilule-ot">
                {saisie.marchandise}
              </button>
            )}
          {NATURES_MARCHANDISE.map((n) => (
            <button
              type="button"
              key={n}
              className="pilule-ot"
              aria-pressed={saisie.marchandise === n}
              onClick={() => p.quandChampOrdre('marchandise', n)}
            >
              {n}
            </button>
          ))}
        </div>
        <LignesPackaging
          lignes={lignes}
          etapes={creation.etapes.map((e) => ({ type: e.saisie.type, nom: e.saisie.site_nom }))}
          supports={p.supports}
          quandAjouter={p.quandAjouterMarchandiseCreation}
          quandChamp={p.quandChampMarchandiseCreation}
          quandSupport={p.quandSupportMarchandiseCreation}
          quandRetirer={p.quandRetirerMarchandiseCreation}
        />
      </section>

      {/* LE VÉHICULE DEMANDÉ EN ICÔNES — lot 154, 03/11/2026. Demande de
          Mohamed : « des icônes avec sélection au clic pour tous les types de
          carrosserie, pas besoin de préciser le gabarit », plusieurs à la fois.
          Un clic retient le semi de la carrosserie. */}
      <section className="fn-bloc">
        <h3 className="titre-bloc-ot">Véhicule demandé</h3>
        <ChoixCarrosseries
          codes={codesAcceptes(saisie)}
          quandBasculer={(lettres) => {
            const r = basculerCarrosserie(saisie, lettres);
            p.quandChampOrdre('gabarits_acceptes', r.gabarits_acceptes);
            p.quandChampOrdre('gabarit_code', r.gabarit_code);
          }}
        />
      </section>
    </div>
  );
}

/** Le dessin d'une carrosserie : un tracteur et sa semi, vus de côté. */
function DessinCarrosserie({ lettres }: { readonly lettres: string }) {
  return (
    <svg viewBox="0 0 96 40" width="96" height="40" aria-hidden="true" className="dessin-carrosserie">
      {/* le tracteur */}
      <path d="M8 30V18l5-6h9v18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M13 13v6h9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      {/* la caisse, selon la carrosserie */}
      {lettres === 'P' ? (
        <>
          <path d="M24 28h66" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M30 28v-5M44 28v-5M58 28v-5M72 28v-5M86 28v-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </>
      ) : (
        <>
          <rect x="24" y="6" width="66" height="22" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" />
          {lettres === 'T' && (
            <path d="M33 8v18M42 8v18M51 8v18M60 8v18M69 8v18M78 8v18" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2" />
          )}
          {lettres === 'TD' && (
            <>
              <rect x="25" y="9" width="6" height="12" fill="currentColor" opacity="0.35" />
              <path d="M57 11v12M51 14l12 6M51 20l12-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </>
          )}
          {lettres === 'FRG' && <path d="M84 8v18" stroke="currentColor" strokeWidth="1.2" />}
        </>
      )}
      {/* les roues */}
      <circle cx="14" cy="32" r="3.2" fill="currentColor" />
      <circle cx="66" cy="32" r="3.2" fill="currentColor" />
      <circle cx="74" cy="32" r="3.2" fill="currentColor" />
      <circle cx="82" cy="32" r="3.2" fill="currentColor" />
    </svg>
  );
}

/** Les quatre carrosseries, cochables ensemble — lot 154. */
function ChoixCarrosseries({
  codes,
  quandBasculer,
}: {
  readonly codes: readonly string[];
  readonly quandBasculer: (lettres: string) => void;
}) {
  return (
    <div className="carrosseries" role="group" aria-label="Véhicule demandé">
      {CARROSSERIES.map((c) => {
        const choisie = carrosserieChoisie(codes, c.lettres);
        return (
          <button
            key={c.lettres}
            type="button"
            className="carrosserie"
            aria-pressed={choisie}
            onClick={() => {
              quandBasculer(c.lettres);
            }}
          >
            <DessinCarrosserie lettres={c.lettres} />
            <span>{c.nom}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * UNE ÉTAPE DE L'ITINÉRAIRE, sur la fiche d'un ordre neuf — F3.
 *
 * Pliée, elle tient sur une ligne : le type, le site avec son adresse, le jour
 * et l'heure. Dépliée : Site, Jour, De, À, Référence, Instructions — et le
 * véhicule de l'étape quand il y a relais.
 *
 * LE SITE S'ÉCRIT AVEC SON ADRESSE COMPLÈTE dans la case (demande du 31/10) :
 * le site retenu est libellé avec l'adresse figée de l'étape, que l'API
 * recopie de la fiche et que `adresseDuSite` vient compléter.
 */
function EtapeNeuve({
  p,
  edition,
  rang,
  total,
  pliee,
  relais,
  quandPlier,
}: {
  readonly p: ProprietesVueOrdres;
  readonly edition: EditionEtape;
  readonly rang: number;
  readonly total: number;
  readonly pliee: boolean;
  readonly relais: boolean;
  readonly quandPlier: () => void;
}) {
  const { saisie, fautes, site, fautesSite } = edition;
  const n = String(rang);
  const faute = (cle: string) =>
    fautes[cle] === undefined ? null : (
      <span className="erreur-champ">{fautes[cle]}</span>
    );
  const adresse = adresseEtape(saisie);
  const resume = [
    adresse,
    saisie.jour === '' ? null : dateCourte(saisie.jour),
    plageHoraire(saisie.heure_debut, saisie.heure_fin),
  ]
    .filter((x) => x !== null && x !== '')
    .join(' · ');

  return (
    <div className="etape-ot">
      <div className="tete-etape-ot">
        <span className="pastille-etape-ot" aria-hidden="true" />
        <span className="type-etape-ot">
          {saisie.type === 'chargement' ? 'Chargement' : 'Livraison'}
        </span>
        <span className="resume-etape-ot">{resume}</span>
        <span className="actions-etape-ot">
          {rang > 0 && (
            <button
              className="bouton-icone-ot"
              type="button"
              aria-label={`Monter l’étape ${String(rang + 1)}`}
              onClick={() => p.quandDeplacerEtapeCreation(rang, -1)}
            >
              ↑
            </button>
          )}
          {rang < total - 1 && (
            <button
              className="bouton-icone-ot"
              type="button"
              aria-label={`Descendre l’étape ${String(rang + 1)}`}
              onClick={() => p.quandDeplacerEtapeCreation(rang, 1)}
            >
              ↓
            </button>
          )}
          {total > 1 && (
            <button
              className="fn-lien"
              type="button"
              onClick={() => p.quandRetirerEtapeCreation(rang)}
            >
              Retirer l’étape
            </button>
          )}
          <button
            className="bouton-icone-ot"
            type="button"
            aria-expanded={!pliee}
            aria-label={pliee ? 'Déplier l’étape' : 'Plier l’étape'}
            onClick={quandPlier}
          >
            {pliee ? '⌄' : '⌃'}
          </button>
        </span>
      </div>

      {!pliee && (
        <div className="corps-etape-ot">
          <div className="grille-etape-ot">
            <div className="fn-champ site-ot">
              <span className="libelle-site-ot">
                <label htmlFor={`etape-site-${n}`}>Site *</label>
                {site === null && (
                  <button
                    className="fn-lien"
                    type="button"
                    onClick={() => p.quandOuvrirSiteCreation(rang)}
                  >
                    + Nouveau site
                  </button>
                )}
              </span>
              <ChoixCherchable
                id={`etape-site-${n}`}
                choix={p.sites.map((s) => ({
                  id: s.id,
                  libelle:
                    s.id === saisie.site_id && adresse !== ''
                      ? `${saisie.site_nom === '' ? s.nom : saisie.site_nom} — ${adresse}`
                      : nommerLeSite(s),
                }))}
                valeur={saisie.site_id}
                invite="Chercher un site…"
                fautif={fautes['site_id'] !== undefined}
                quandChoisir={(id) => p.quandSiteCreation(rang, id)}
              />
              {faute('site_id')}
            </div>
            <div className="fn-champ">
              <label htmlFor={`etape-jour-${n}`}>Jour *</label>
              <input
                id={`etape-jour-${n}`}
                className="boite"
                type="date"
                value={saisie.jour}
                onChange={(e) => p.quandChampEtapeCreation(rang, 'jour', e.target.value)}
              />
              {faute('jour')}
            </div>
            <div className="fn-champ">
              <label htmlFor={`etape-de-${n}`}>De</label>
              <input
                id={`etape-de-${n}`}
                className="boite"
                type="time"
                value={saisie.heure_debut}
                onChange={(e) =>
                  p.quandChampEtapeCreation(rang, 'heure_debut', e.target.value)
                }
              />
              {faute('heure_debut')}
            </div>
            <div className="fn-champ">
              <label htmlFor={`etape-a-${n}`}>À</label>
              <input
                id={`etape-a-${n}`}
                className="boite"
                type="time"
                value={saisie.heure_fin}
                onChange={(e) => p.quandChampEtapeCreation(rang, 'heure_fin', e.target.value)}
              />
              {faute('heure_fin')}
            </div>
          </div>

          <div className="grille-etape-ot bas">
            <div className="fn-champ">
              <label htmlFor={`etape-reference-${n}`}>Référence</label>
              <input
                id={`etape-reference-${n}`}
                className="boite"
                type="text"
                value={saisie.reference}
                onChange={(e) => p.quandChampEtapeCreation(rang, 'reference', e.target.value)}
              />
              {faute('reference')}
            </div>
            <div className="fn-champ instructions-ot">
              <label htmlFor={`etape-note-${n}`}>Instructions</label>
              <input
                id={`etape-note-${n}`}
                className="boite"
                type="text"
                value={saisie.note}
                onChange={(e) => p.quandChampEtapeCreation(rang, 'note', e.target.value)}
              />
            </div>
          </div>

          {relais && (
            <div className="fn-champ vehicule-etape-ot">
              <label htmlFor={`etape-vehicule-${n}`}>Véhicule de cette étape</label>
              <select
                id={`etape-vehicule-${n}`}
                className="boite"
                value={saisie.vehicule_id}
                onChange={(e) => {
                  p.quandChampEtapeCreation(rang, 'vehicule_id', e.target.value);
                  p.quandChampEtapeCreation(rang, 'chauffeur_id', '');
                  p.quandChampEtapeCreation(rang, 'remorque_id', '');
                }}
              >
                <option value="">À affecter</option>
                {p.vehicules
                  .filter((v) => v.actif === true || v.id === saisie.vehicule_id)
                  .map((v) => (
                    <option value={v.id} key={v.id}>
                      {texteParc(v, 'immatriculation') ?? v.id}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {site !== null && (
            <div className="nouveau-site-ot">
              <p className="titre-nouveau-site">Nouveau site</p>
              <div className="grille-nouveau-site">
                <div className="fn-champ fn-large">
                  <label htmlFor={`site-nom-${n}`}>Nom du site *</label>
                  <input
                    id={`site-nom-${n}`}
                    className="boite"
                    type="text"
                    value={site.nom}
                    onChange={(e) => p.quandChampSiteCreation(rang, 'nom', e.target.value)}
                  />
                  {fautesSite['nom'] !== undefined && (
                    <span className="erreur-champ">{fautesSite['nom']}</span>
                  )}
                </div>
                <div className="fn-champ fn-large">
                  <label htmlFor={`site-adresse-${n}`}>Adresse</label>
                  <input
                    id={`site-adresse-${n}`}
                    className="boite"
                    type="text"
                    value={site.adresse}
                    onChange={(e) =>
                      p.quandChampSiteCreation(rang, 'adresse', e.target.value)
                    }
                  />
                </div>
                <div className="fn-champ">
                  <label htmlFor={`site-cp-${n}`}>Code postal</label>
                  <input
                    id={`site-cp-${n}`}
                    className="boite"
                    type="text"
                    value={site.code_postal}
                    onChange={(e) =>
                      p.quandChampSiteCreation(rang, 'code_postal', e.target.value)
                    }
                  />
                  {fautesSite['code_postal'] !== undefined && (
                    <span className="erreur-champ">{fautesSite['code_postal']}</span>
                  )}
                </div>
                <div className="fn-champ fn-large">
                  <label htmlFor={`site-ville-${n}`}>Ville</label>
                  <input
                    id={`site-ville-${n}`}
                    className="boite"
                    type="text"
                    value={site.ville}
                    onChange={(e) => p.quandChampSiteCreation(rang, 'ville', e.target.value)}
                  />
                </div>
                <div className="fn-champ">
                  <label htmlFor={`site-pays-${n}`}>Pays</label>
                  <select
                    id={`site-pays-${n}`}
                    className="boite"
                    value={site.pays}
                    onChange={(e) => p.quandChampSiteCreation(rang, 'pays', e.target.value)}
                  >
                    {(PAYS.includes(site.pays) ? PAYS : [site.pays, ...PAYS]).map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="pied-nouveau-site">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  onClick={() => p.quandFermerSiteCreation(rang)}
                >
                  Annuler
                </button>
                <button
                  className="bouton"
                  type="button"
                  onClick={() => p.quandCreerSiteCreation(rang)}
                >
                  Créer le site
                </button>
              </div>
            </div>
          )}
          {edition.refus !== null && <p className="bandeau">{edition.refus}</p>}
        </div>
      )}
    </div>
  );
}

/** « 12 rue de l'Industrie, 28310 Toury » — ce qui est connu, dans l'ordre. */
function adresseEtape(saisie: SaisieEtape): string {
  const ville = [saisie.site_code_postal.trim(), saisie.site_ville.trim()]
    .filter((x) => x !== '')
    .join(' ');
  return [saisie.site_adresse.trim(), ville].filter((x) => x !== '').join(', ');
}

/** « 26/08/2026 » depuis « 2026-08-26 ». Une valeur mal formée est rendue telle quelle. */
function dateCourte(jour: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  return m === null ? jour : `${m[3] ?? ''}/${m[2] ?? ''}/${m[1] ?? ''}`;
}

/** « 08:00 – 10:00 », « 08:00 », ou rien. */
function plageHoraire(de: string, a: string): string {
  if (de !== '' && a !== '') return `${de} – ${a}`;
  return de !== '' ? de : a;
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesOrdres {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /**
   * Un ordre à ouvrir tout de suite, demandé depuis un AUTRE écran — le
   * planning, où l'on clique un bloc pour voir sa fiche. `null` le reste du
   * temps.
   */
  readonly ouvrirOrdre?: string | null;
  /** Prévient que la demande a été honorée, pour qu'elle ne se rejoue pas. */
  readonly quandOrdreOuvert?: () => void;
  /**
   * D'OÙ L'ON VIENT, ET COMMENT Y RETOURNER — 14/09/2026.
   *
   * `null` veut dire « on est arrivé par le menu Exploitation » : le retour
   * ramène à la liste des ordres, et le bouton dit « Les OT ». C'est le cas
   * de loin le plus fréquent, d'où le défaut.
   *
   * Sinon, c'est l'écran qui nous a envoyés ici. Le bouton porte son nom et
   * `quand` y ramène — l'application rebascule de module.
   *
   * POURQUOI LE COUPLE ET NON UNE SIMPLE CLÉ D'ÉCRAN : une clé obligerait cet
   * écran-ci à connaître la liste des écrans du logiciel et la façon d'y
   * aller. Il n'a pas à la connaître ; celui qui envoie sait revenir.
   */
  readonly retourVers?: { readonly libelle: string; readonly quand: () => void } | null;
}

function editionVide(saisie: SaisieOrdre): EditionOrdre {
  return { initiale: saisie, saisie, fautes: {}, refus: null, envoi: false };
}

function editionEtapeVide(saisie: SaisieEtape, id: string | null): EditionEtape {
  return {
    id,
    saisie,
    initiale: saisie,
    fautes: {},
    refus: null,
    envoi: false,
    site: null,
    fautesSite: {},
    confirme: false,
  };
}

export function EcranOrdres({
  jeton,
  quandAccesPerdu,
  ouvrirOrdre = null,
  quandOrdreOuvert,
  retourVers = null,
}: ProprietesOrdres) {
  const [vue, setVue] = useState<'liste' | 'fiche' | 'creation'>('liste');
  const [liste, setListe] = useState<EtatListe>({ nom: 'chargement' });
  const [fiche, setFiche] = useState<EtatFiche>({ nom: 'aucune' });
  const [onglet, setOnglet] = useState<Onglet>('commande');
  /**
   * AUCUN FILTRE À L'ARRIVÉE — demandé le 07/09/2026, mot pour mot :
   * « lorsque l'on arrive sur un menu aucun filtre ne doit être appliqué de
   * base ».
   *
   * La fenêtre s'ouvrait sur le mois courant. C'était la cause du défaut du
   * 04/09 — « l'écran affichait "Aucun ordre entre le 01/09 et le 30/09" alors
   * que le seul ordre de la base était daté du 31 août » — qu'on avait alors
   * traité par un compteur (« 3 ordres hors de cette période ») plutôt qu'à sa
   * racine. Un écran qui cache par défaut oblige à comprendre pourquoi avant
   * de pouvoir travailler.
   *
   * Le compteur `hors_periode` RESTE utile : il parle dès qu'on pose une
   * fenêtre soi-même.
   */
  const [fenetre, setFenetre] = useState({ du: '', au: '' });
  /**
   * LES FILTRES ET LE TRI — ajoutés le 10/09/2026, maquette
   * `docs/maquettes/ordres-filtres-et-tri.html`.
   *
   * LE DÉROULANT « STATUT » A DISPARU, et son état avec lui. Il filtrait côté
   * serveur pendant que la recherche filtrait côté navigateur : deux endroits
   * pour la même chose, et personne ne savait lequel gagnait. Le statut est
   * devenu un critère parmi les autres.
   *
   * L'API n'est donc plus rappelée quand on filtre — la fenêtre de dates borne
   * déjà ce qui est chargé, et filtrer une liste en mémoire prend une
   * milliseconde là où un aller-retour fait clignoter le tableau.
   *
   * ON ARRIVE SANS AUCUN FILTRE, règle posée le 05/09 : « lorsque l'on arrive
   * sur un menu aucun filtre ne doit être appliqué de base ».
   */
  const [filtres, setFiltres] = useState<Filtres>(AUCUN_FILTRE);
  const [tri, setTri] = useState<Tri>(TRI_DEFAUT);
  const [panneauOuvert, setPanneauOuvert] = useState(false);
  /**
   * Combien d'ordres la fenêtre de dates écarte.
   *
   * L'écran le DIT désormais, au lieu de laisser croire qu'il n'y a rien :
   * le 04/09/2026, un ordre daté du 31 août n'apparaissait pas dans une liste
   * ouverte sur septembre, et personne ne pouvait le deviner.
   */
  const [horsPeriode, setHorsPeriode] = useState(0);
  /**
   * CE QUE LA COUPE DES 500 LAISSE DERRIÈRE, ajouté le 26/10/2026.
   *
   * Même défaut que `horsPeriode`, un étage plus haut : la liste en rendait 500
   * sans dire qu'il y en avait 521, et « 500 ordres » se lisait « 500 au
   * total ». Un ordre plus ancien devenait introuvable sans qu'aucun écran ne
   * le signale.
   *
   * `trouves` vaut la longueur de la liste tant que le plafond n'a pas mordu :
   * l'écran ne dit rien dans le cas ordinaire.
   */
  const [trouves, setTrouves] = useState(0);
  const [recherche, setRecherche] = useState('');
  /**
   * LA RECHERCHE ÉTENDUE : le terme cherché dans TOUTE la base, ou `null`.
   *
   * Posé par un geste volontaire, jamais par la frappe — c'est ce qui le
   * distingue du filtre de la barre de recherche, et c'est pourquoi les deux ne
   * se marchent pas sur les pieds (voir `lireOrdres` dans `api.ts`).
   */
  const [etendue, setEtendue] = useState<string | null>(null);
  /**
   * LA CLÉ DE RECHARGEMENT. Changer la fenêtre de dates relance la liste ; en
   * sortir d'une recherche étendue aussi, mais sans rien changer d'autre — il
   * faut donc un moyen de dire « recharge » sans mentir sur la fenêtre.
   */
  const [rechargement, setRechargement] = useState(0);

  const [clients, setClients] = useState<readonly LigneListe[]>([]);
  const [sites, setSites] = useState<readonly LigneListe[]>([]);
  const [gabarits, setGabarits] = useState<readonly Vehicule[]>([]);
  const [motifs, setMotifs] = useState<readonly MotifLitige[]>([]);
  /**
   * Les supports réglés par la société — les ACTIFS seulement : la liste sert à
   * proposer, et un support désactivé n'a plus à être proposé. Une étape
   * ancienne qui en porte un le garde quand même à l'écran, c'est le rôle de
   * `supportsAvec`.
   */
  const [supports, setSupports] = useState<readonly LigneReglee[]>([]);
  // Les centres de profit, depuis le 07/09/2026. Seuls les ACTIFS sont gardés :
  // un centre désactivé ne doit plus se proposer à la saisie. Celui que porte
  // un ordre déjà enregistré reste affiché — le filtre du déroulant, plus bas,
  // le rajoute au cas par cas.
  const [centres, setCentres] = useState<readonly LigneReglee[]>([]);
  /** Les pièces jointes de l'ordre ouvert, et les types pour en joindre une. */
  const [documents, setDocuments] = useState<readonly DocumentJoint[]>([]);
  const [typesDocument, setTypesDocument] = useState<readonly LigneReglee[]>([]);
  const [ajoutDocument, setAjoutDocument] = useState<AjoutDocument | null>(null);
  const [envoiDocument, setEnvoiDocument] = useState(false);
  const [refusDocument, setRefusDocument] = useState<string | null>(null);
  const [vehicules, setVehicules] = useState<readonly LigneParc[]>([]);
  const [chauffeurs, setChauffeurs] = useState<readonly LigneParc[]>([]);
  const [remorques, setRemorques] = useState<readonly LigneParc[]>([]);

  const [editionOrdre, setEditionOrdre] = useState<EditionOrdre | null>(null);
  const [editionEtape, setEditionEtape] = useState<EditionEtape | null>(null);
  const [creation, setCreation] = useState<EtatCreation | null>(null);

  /**
   * OÙ EN EST LA LECTURE D'UNE CONFIRMATION — lot 124.
   *
   * `venuDUneConfirmation` est à part, et il survit au bandeau : on peut
   * fermer le bandeau et créer un site cinq minutes plus tard, la fiche doit
   * quand même porter sa provenance. Les mêler aurait fait dépendre la
   * traçabilité d'un clic sur « Fermer ».
   */
  const [lecture, setLecture] = useState<EtatLecture>({ quoi: 'repos' });
  const [venuDUneConfirmation, setVenuDUneConfirmation] = useState(false);
  /**
   * LES CONFIRMATIONS À CRÉER — lot 147. `confirmationEnCours` : la ligne
   * dont vient la saisie ouverte, pour la marquer « créée » à l'enregistrement
   * et ouvrir la suivante.
   */
  const [ongletListe, setOngletListe] = useState<'ordres' | 'a_creer'>('ordres');
  const [aCreer, setACreer] = useState<EtatACreer>({ liste: null, envois: 0, refus: null });
  const [confirmationEnCours, setConfirmationEnCours] = useState<string | null>(null);

  const [validation, setValidation] = useState<SaisieValidation | null>(null);
  const [tempsPasse, setTempsPasse] = useState('');
  /**
   * LE COÛT DE L'ORDRE OUVERT, relu à chaque ouverture de fiche.
   *
   * Un appel à part, et non un champ de la fiche : le calcul interroge les
   * coûts de ressource, les frais et l'indice gazole, et le faire à chaque
   * ligne de la LISTE des ordres serait une requête par ordre.
   */
  const [cout, setCout] = useState<CoutOrdre | null>(null);
  const [prestations, setPrestations] = useState<PrestationsDeLOrdre | null>(null);
  const [naturesPrestation, setNaturesPrestation] = useState<
    readonly LigneReglee[]
  >([]);
  const [saisiePrestation, setSaisiePrestation] = useState<SaisiePrestation | null>(
    null,
  );
  const [litige, setLitige] = useState<SaisieLitige | null>(null);
  /**
   * LA RÉSOLUTION D'UN LITIGE, depuis le bandeau de la carte d'étape.
   *
   * Ajoutée le 07/09/2026 : le litige a quitté le formulaire de l'étape, et
   * sans ce geste il aurait fallu passer par l'écran Litiges pour clore ce
   * qu'on venait d'ouvrir.
   */
  const [resolution, setResolution] = useState<{
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null>(null);
  /**
   * L'ÉCHANGE DE PALETTES EN COURS DE SAISIE.
   *
   * Un état à part et non un champ du formulaire d'étape : il s'écrit par une
   * autre route (`PUT /emballage/etapes/:id`) et il n'existe qu'une fois
   * l'étape enregistrée. Même forme que la boîte de litige, juste au-dessus.
   */
  const [echange, setEchange] = useState<{
    readonly etapeId: string;
    readonly saisie: SaisieEchange;
  } | null>(null);
  /**
   * LE PANNEAU D'AFFRÈTEMENT, et les transporteurs que le moteur propose.
   *
   * Les transporteurs sont cherchés À L'OUVERTURE, avec l'axe déduit de
   * l'ordre : c'est le panneau du cadrage §6, où l'on ne saisit ni l'axe ni le
   * matériel. Les charger d'avance, pour tous les ordres, ferait une recherche
   * par ligne de liste.
   */
  const [affretement, setAffretement] = useState<SaisieAffretement | null>(null);
  const [proposes, setProposes] = useState<readonly TransporteurTrouve[]>([]);
  const [chercheEnCours, setChercheEnCours] = useState(false);
  /**
   * L'ANNUAIRE ENTIER, chargé en même temps que la recherche par axe.
   *
   * Deux appels au lieu d'un, à l'ouverture du panneau seulement : la liste
   * complète est ce qui rend le panneau utilisable quand aucune couverture
   * n'est saisie (voir `grouperTransporteurs`). Elle n'est PAS chargée avec la
   * liste des ordres — ce serait un appel pour un écran qui n'affrète pas.
   */
  const [tousTransporteurs, setTousTransporteurs] = useState<readonly LigneListe[]>([]);
  /**
   * LA CRÉATION D'UN TRANSPORTEUR SANS QUITTER L'ORDRE — demandé le 07/09/2026.
   *
   * Le panneau d'affrètement reste ouvert DERRIÈRE : la saisie en cours (le
   * prix, le contact) n'est pas perdue. Le formulaire est celui de l'Annuaire,
   * au champ près — `ANNUAIRE_TRANSPORTEURS` est la seule source de vérité sur
   * ce qu'une fiche de transporteur porte.
   */
  const [nouveauTransporteur, setNouveauTransporteur] = useState<{
    readonly saisie: Saisie;
    readonly fautes: Readonly<Record<string, string>>;
    readonly refus: string | null;
  } | null>(null);

  /**
   * LA FICHE CLIENT en cours de création par-dessus l'ordre, ou `null`.
   *
   * Même forme et même circuit que celle du transporteur, depuis le 08/09/2026.
   * Le manque était réel : le site et le transporteur se créaient déjà sans
   * quitter l'ordre, le client obligeait à abandonner une commande à moitié
   * saisie pour aller ouvrir l'Annuaire.
   */
  const [nouveauClient, setNouveauClient] = useState<{
    readonly saisie: Saisie;
    readonly fautes: Readonly<Record<string, string>>;
    readonly refus: string | null;
  } | null>(null);

  const [action, setAction] = useState({
    confirmeAnnulation: false,
    confirmeSuppression: false,
    envoi: false,
    refus: null as string | null,
  });

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * L'INDEXATION DE L'ORDRE EN COURS DE SAISIE — F3, 01/11/2026.
   *
   * Recalculée par l'API (`POST /indexation-apercu`) dès que change ce dont
   * elle dépend : le client, le prix, le choix Sans / Avec, le jour le plus
   * tôt et le départ. Une demi-seconde d'attente après la dernière frappe :
   * taper « 852 » ne doit pas faire trois appels. Une réponse arrivée après
   * un nouveau changement est jetée — elle décrirait la saisie d'avant.
   */
  const [indexationCreation, setIndexationCreation] = useState<IndexationLue | null>(null);
  const cleIndexation = (() => {
    if (creation === null) return null;
    const o = creation.ordre.saisie;
    if (o.client_id === '') return null;
    const jours = creation.etapes.map((e) => e.saisie.jour).filter((j) => j !== '').sort();
    const depart = creation.etapes.find((e) => e.saisie.type === 'chargement')?.saisie;
    return JSON.stringify({
      client_id: o.client_id,
      prix_vente: o.prix_vente.trim() === '' ? null : o.prix_vente.trim().replace(',', '.'),
      jour: jours[0] ?? null,
      depart_site_id: depart === undefined || depart.site_id === '' ? null : depart.site_id,
      depart_code_postal:
        depart === undefined || depart.site_code_postal.trim() === ''
          ? null
          : depart.site_code_postal.trim(),
      indexation: o.indexation === '' ? null : o.indexation === 'avec',
    });
  })();
  useEffect(() => {
    if (cleIndexation === null) {
      setIndexationCreation(null);
      return;
    }
    let abandonne = false;
    const minuterie = setTimeout(() => {
      void (async () => {
        const j = await avecJeton();
        if (j === null || abandonne) return;
        const lu = await apercuIndexation(j, JSON.parse(cleIndexation) as Parameters<typeof apercuIndexation>[1]);
        if (abandonne) return;
        // UN REFUS NE S'AFFICHE PAS EN FAUTE : un prix mal tapé se corrige
        // dans son champ. L'aperçu se tait, simplement.
        setIndexationCreation(lu.etat === 'ok' ? lu.valeur : null);
      })();
    }, 500);
    return () => {
      abandonne = true;
      clearTimeout(minuterie);
    };
  }, [cleIndexation, avecJeton]);

  // Les référentiels, une seule fois : clients, sites, gabarits, motifs de
  // litige. Ils ne changent pas pendant qu'on saisit un ordre.
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const [c, s, g, m, v, ch, r, sup, td, cen, nat] = await Promise.all([
        lireListe(j, ANNUAIRE_CLIENTS),
        lireListe(j, ANNUAIRE_SITES),
        lireVehicules(j),
        lireMotifsLitige(j),
        lireParc(j, PARC_VEHICULES),
        lireParc(j, PARC_CHAUFFEURS),
        lireParc(j, PARC_REMORQUES),
        lireListeReglee(j, 'supports'),
        lireListeReglee(j, 'types_document'),
        lireListeReglee(j, 'centres'),
        // LES NATURES ENTIÈRES, actives ET éteintes — 15/09/2026. La boîte
        // filtre elle-même les éteintes, mais doit pouvoir NOMMER celle que
        // porte une vieille prestation. Ne charger que les actives ferait
        // sauter le menu sur une autre nature à la modification.
        lireListeReglee(j, 'natures_prestation'),
      ]);
      if (abandonne) return;
      if (c.etat === 'ok') setClients(c.valeur);
      if (s.etat === 'ok') setSites(s.valeur);
      if (g.etat === 'ok') setGabarits(g.valeur);
      if (m.etat === 'ok') setMotifs(m.valeur);
      if (v.etat === 'ok') setVehicules(v.valeur);
      if (ch.etat === 'ok') setChauffeurs(ch.valeur);
      if (r.etat === 'ok') setRemorques(r.valeur);
      if (sup.etat === 'ok') {
        setSupports(sup.valeur.filter((l) => l.actif));
      }
      if (td.etat === 'ok') setTypesDocument(td.valeur);
      if (cen.etat === 'ok') setCentres(cen.valeur.filter((l) => l.actif));
      if (nat.etat === 'ok') setNaturesPrestation(nat.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  // La liste, à chaque changement de fenêtre ou de statut. Le texte, lui, ne
  // rappelle jamais l'API : il filtre la liste déjà chargée.
  //
  // CE CHARGEMENT EST CELUI DE LA LISTE ORDINAIRE : il efface donc la recherche
  // étendue. Changer la fenêtre de dates pendant une recherche étendue, c'est
  // en sortir — et l'écran ne doit pas garder un bandeau qui parle d'une
  // question qu'il ne pose plus.
  useEffect(() => {
    let abandonne = false;
    setListe({ nom: 'chargement' });
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lu = await lireOrdres(j, fenetre);
      if (abandonne) return;
      if (lu.etat === 'ok') {
        setListe({ nom: 'prete', ordres: lu.valeur.ordres });
        setHorsPeriode(lu.valeur.horsPeriode);
        setTrouves(lu.valeur.trouves);
        setEtendue(null);
      } else {
        if (lu.cause === 'acces') quandAccesPerdu();
        setListe({ nom: 'panne' });
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [fenetre, rechargement, avecJeton, quandAccesPerdu]);

  /**
   * CHERCHER DANS TOUTE LA BASE — ajouté le 26/10/2026.
   *
   * La liste ordinaire s'arrête à 500 ordres. Au-delà, un ordre existait sans
   * qu'aucun écran ne permette de le retrouver : la barre de recherche ne
   * filtrait que ce qui était déjà chargé.
   *
   * CE N'EST PAS LA MÊME RECHERCHE, et l'écran ne doit pas le cacher. L'API ne
   * cherche que sur le NUMÉRO, la RÉFÉRENCE CLIENT et le NOM DU CLIENT
   * (vérifié `apps/api/src/ordres_transport.ts:1206-1209`) ; le filtre du
   * navigateur, lui, couvre aussi les villes, les sites et la marchandise. Ce
   * bouton sert donc à retrouver un ordre qu'on CONNAÎT, pas à explorer.
   *
   * LA FENÊTRE DE DATES EST ABANDONNÉE pendant l'appel — c'est tout l'objet :
   * un ordre qu'on ne trouve plus est, presque toujours, un ordre dont on ne
   * sait plus la date.
   */
  const chercherPartout = async (): Promise<void> => {
    const terme = recherche.trim();
    if (terme === '') return;
    setListe({ nom: 'chargement' });
    const j = await avecJeton();
    if (j === null) return;
    const lu = await lireOrdres(j, { du: '', au: '', recherche: terme });
    if (lu.etat === 'ok') {
      setListe({ nom: 'prete', ordres: lu.valeur.ordres });
      // La fenêtre est hors jeu : il n'y a plus de « hors période » à annoncer.
      setHorsPeriode(0);
      setTrouves(lu.valeur.trouves);
      setEtendue(terme);
    } else {
      if (lu.cause === 'acces') quandAccesPerdu();
      setListe({ nom: 'panne' });
    }
  };

  /** Range la fiche rendue par l'API, dans la liste ET dans la fiche ouverte. */
  const poser = useCallback((rendue: FicheOrdre) => {
    const ordre = rendue.ordre_transport;
    setFiche({ nom: 'prete', id: ordre.id, fiche: rendue });
    setListe((actuelle) => {
      if (actuelle.nom !== 'prete') return actuelle;
      const connu = actuelle.ordres.some((o) => o.id === ordre.id);
      return {
        nom: 'prete',
        ordres: connu
          ? actuelle.ordres.map((o) => (o.id === ordre.id ? ordre : o))
          : [...actuelle.ordres, ordre],
      };
    });
  }, []);

  const ouvrir = useCallback(
    (id: string) => {
      setVue('fiche');
      setOnglet('commande');
      setEditionOrdre(null);
      setEditionEtape(null);
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: null,
      });
      setFiche({ nom: 'chargement', id });
      setCout(null);
      setPrestations(null);
      setSaisiePrestation(null);
      setTempsPasse('');

      void (async () => {
        const j = await avecJeton();
        if (j === null) return;
        const lu = await lireOrdre(j, id);
        if (lu.etat === 'ok') {
          setFiche({ nom: 'prete', id, fiche: lu.valeur });
          // Les pièces jointes suivent la fiche : deux appels à l'ouverture,
          // et aucun ensuite tant qu'on ne joint rien.
          const pieces = await lireDocuments(j, id);
          if (pieces.etat === 'ok') setDocuments(pieces.valeur);
          // LE COÛT AUSSI. Un troisième appel, à l'ouverture seulement : le
          // calcul interroge les coûts de ressource, les frais et l'indice
          // gazole, et le faire pour chaque ligne de la liste serait une
          // requête par ordre.
          const lecture = await lireLeCout(j, id);
          if (lecture.etat === 'ok') setCout(lecture.valeur);
          // LES PRESTATIONS ANNEXES, quatrième appel à l'ouverture. Elles
          // pèsent sur le CA de l'ordre et donc sur sa marge : les charger
          // plus tard ferait afficher une marge fausse pendant un instant.
          const lues = await lireLesPrestations(j, id);
          if (lues.etat === 'ok') setPrestations(lues.valeur);
        } else {
          if (lu.cause === 'acces') quandAccesPerdu();
          setFiche({ nom: 'panne', id, cause: lu.cause });
        }
      })();
      // `useCallback` depuis le 04/09/2026 : le planning peut demander l'ouverture
      // d'un ordre, et l'effet qui l'écoute a besoin d'une fonction stable.
    },
    [avecJeton, quandAccesPerdu],
  );

  // Une demande venue du planning : on ouvre la fiche, puis on l'oublie —
  // sans quoi revenir à la liste rouvrirait la même fiche sans fin.
  useEffect(() => {
    if (ouvrirOrdre === null) return;
    ouvrir(ouvrirOrdre);
    if (quandOrdreOuvert !== undefined) quandOrdreOuvert();
  }, [ouvrirOrdre, ouvrir, quandOrdreOuvert]);

  /**
   * Recopie l'adresse du site choisi dans l'étape.
   *
   * Le nom, le code postal et la ville sont DÉJÀ dans la liste ; seule l'adresse
   * demande de lire la fiche. Si cette lecture échoue, on n'invente rien : à la
   * création l'API recopiera l'adresse elle-même, et à la modification le champ
   * reste tel qu'il était.
   */
  const adresseDuSite = useCallback(
    async (siteId: string, poserAdresse: (adresse: string) => void): Promise<void> => {
      const j = await avecJeton();
      if (j === null) return;
      const lu = await lireFiche(j, ANNUAIRE_SITES, siteId);
      if (lu.etat !== 'ok') return;
      poserAdresse(valeur(lu.valeur.fiche, 'adresse') ?? '');
    },
    [avecJeton],
  );

  /** Le site choisi, tel qu'il se recopie dans une étape. */
  const depuisSite = (siteId: string): Partial<SaisieEtape> => {
    const site = sites.find((s) => s.id === siteId);
    if (site === undefined) return { site_id: '' };
    return {
      site_id: siteId,
      site_nom: site.nom,
      site_code_postal: site.code_postal ?? '',
      site_ville: site.ville ?? '',
    };
  };

  // -------------------------------------------------------------------------
  // Les écritures
  // -------------------------------------------------------------------------

  const enregistrerOrdre = () => {
    if (editionOrdre === null || fiche.nom !== 'prete') return;
    const fautes = validerOrdre(editionOrdre.saisie, editionOrdre.initiale);
    if (Object.keys(fautes).length > 0) {
      setEditionOrdre({ ...editionOrdre, fautes, refus: null });
      return;
    }
    const change = differenceOrdre(editionOrdre.initiale, editionOrdre.saisie);
    if (Object.keys(change).length === 0) {
      setEditionOrdre(null);
      return;
    }

    const encours = editionOrdre;
    const id = fiche.id;
    setEditionOrdre({ ...encours, fautes: {}, refus: null, envoi: true });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await modifierOrdre(j, id, change);
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setEditionOrdre(null);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEditionOrdre({ ...encours, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  const enregistrerEtape = () => {
    if (editionEtape === null || fiche.nom !== 'prete') return;
    const fautes = validerEtape(editionEtape.saisie);
    if (Object.keys(fautes).length > 0) {
      setEditionEtape({ ...editionEtape, fautes, refus: null });
      return;
    }

    const encours = editionEtape;
    const otId = fiche.id;
    setEditionEtape({ ...encours, fautes: {}, refus: null, envoi: true });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat =
        encours.id === null
          ? await ajouterEtape(j, otId, corpsEtape(encours.saisie, true))
          : await modifierEtape(
              j,
              otId,
              encours.id,
              corpsEtape(encours.saisie, false, encours.initiale),
            );

      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setEditionEtape(null);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEditionEtape({ ...encours, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  /** Le refus d'une écriture d'étape, posé au même endroit pour les deux boîtes. */
  const finirLEcriture = (resultat: { etat: 'ok' | 'echec'; cause?: EchecApi }) => {
    setAction({
      confirmeAnnulation: false,
      confirmeSuppression: false,
      envoi: false,
      refus:
        resultat.etat === 'ok' || resultat.cause === undefined
          ? null
          : REFUS[resultat.cause],
    });
  };

  // -------------------------------------------------------------------------
  // LES PRESTATIONS ANNEXES — 15/09/2026
  // -------------------------------------------------------------------------

  /**
   * Ouvrir la boîte : `null` pour ajouter, une prestation pour la modifier.
   *
   * À L'AJOUT, LA PREMIÈRE NATURE ACTIVE EST PRÉ-CHOISIE et son prix avec.
   * Un menu vide obligerait à deux gestes pour la prestation la plus courante.
   */
  const ouvrirLaPrestation = (prestation: PrestationAnnexe | null) => {
    setAction((a) => ({ ...a, refus: null }));
    if (prestation !== null) {
      setSaisiePrestation({
        prestationId: prestation.id,
        natureId: prestation.nature_id,
        detail: prestation.detail ?? '',
        quantite: pourLaSaisie(prestation.quantite),
        prixUnitaire: pourLaSaisie(prestation.prix_unitaire),
      });
      return;
    }
    const premiere = naturesPrestation.find((n) => n.actif);
    setSaisiePrestation({
      prestationId: null,
      natureId: premiere?.id ?? '',
      detail: '',
      quantite: '1',
      prixUnitaire: pourLaSaisie(premiere?.prix_defaut),
    });
  };

  /**
   * CHANGER DE NATURE REMPLACE LE PRIX, mais seulement à l'ajout et seulement
   * tant qu'on ne l'a pas touché soi-même : écraser un prix négocié parce que
   * l'utilisateur corrige la nature serait une perte silencieuse.
   */
  const champDeLaPrestation = (
    cle: 'natureId' | 'detail' | 'quantite' | 'prixUnitaire',
    valeur: string,
  ) => {
    setSaisiePrestation((actuelle) => {
      if (actuelle === null) return actuelle;
      if (cle !== 'natureId') return { ...actuelle, [cle]: valeur };
      const avant = naturesPrestation.find((n) => n.id === actuelle.natureId);
      const apres = naturesPrestation.find((n) => n.id === valeur);
      const prixIntact =
        actuelle.prixUnitaire === '' ||
        actuelle.prixUnitaire === pourLaSaisie(avant?.prix_defaut);
      return {
        ...actuelle,
        natureId: valeur,
        prixUnitaire: prixIntact
          ? pourLaSaisie(apres?.prix_defaut)
          : actuelle.prixUnitaire,
      };
    });
  };

  /**
   * LA VIRGULE EST ACCEPTÉE À LA SAISIE et traduite ici : on écrit « 2,5 »
   * sur un clavier français, et l'API attend un point. Refuser la virgule
   * ferait échouer une saisie juste.
   */
  const enNombre = (saisi: string): string => saisi.trim().replace(',', '.');

  const enregistrerLaPrestation = () => {
    if (fiche.nom !== 'prete' || saisiePrestation === null) return;
    const otId = fiche.id;
    const saisie = saisiePrestation;
    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat =
        saisie.prestationId === null
          ? await ajouterUnePrestation(j, otId, {
              nature_id: saisie.natureId,
              detail: saisie.detail.trim() === '' ? null : saisie.detail.trim(),
              quantite: enNombre(saisie.quantite),
              prix_unitaire: enNombre(saisie.prixUnitaire),
            })
          : await modifierUnePrestation(j, otId, saisie.prestationId, {
              detail: saisie.detail.trim() === '' ? null : saisie.detail.trim(),
              quantite: enNombre(saisie.quantite),
              prix_unitaire: enNombre(saisie.prixUnitaire),
            });
      if (resultat.etat === 'ok') {
        setPrestations(resultat.valeur);
        setSaisiePrestation(null);
      } else if (resultat.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(resultat);
    })();
  };

  const retirerLaPrestation = (prestationId: string) => {
    if (fiche.nom !== 'prete') return;
    const otId = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await retirerUnePrestation(j, otId, prestationId);
      if (resultat.etat === 'ok') setPrestations(resultat.valeur);
      else if (resultat.cause === 'acces') quandAccesPerdu();
      finirLEcriture(resultat);
    })();
  };

  /**
   * VALIDER passe par une boîte, DÉVALIDER non.
   *
   * Valider demande QUAND — l'utilisateur l'a demandé le 04/09/2026, et poser
   * l'instant de la saisie en silence donnait une heure fausse dès qu'on valide
   * le lendemain matin les livraisons de la veille. Dévalider n'a rien à
   * demander : il n'y a plus d'heure de réalisation à connaître.
   */
  const basculerFait = (id: string) => {
    if (fiche.nom !== 'prete') return;
    const etape = fiche.fiche.etapes.find((e) => e.id === id);
    if (etape === undefined) return;

    if (!etape.fait) {
      setLitige(null);
      setEchange(null);
      setAction((a) => ({ ...a, refus: null }));
      // L'HEURE COURANTE SUR LE DÉPART, l'arrivée vide. On valide une étape
      // quand elle est finie, pas quand le camion se gare — et une arrivée
      // proposée serait recopiée sans réfléchir, ce qui fabriquerait une
      // attente fausse dans le coût de revient.
      setValidation({
        etapeId: id,
        jour: aujourdhui(),
        heureArrivee: '',
        heureDepart: maintenant(),
      });
      return;
    }

    const otId = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await modifierEtape(j, otId, id, { fait: false });
      if (resultat.etat === 'ok') poser(resultat.valeur);
      else if (resultat.cause === 'acces') quandAccesPerdu();
      finirLEcriture(resultat);
    })();
  };

  /** La boîte de validation, confirmée. */
  const confirmerValidation = () => {
    if (validation === null || fiche.nom !== 'prete') return;
    if (refusValidation(validation) !== null) return;
    const otId = fiche.id;
    const repartiLe = instantDepuis(validation.jour, validation.heureDepart);
    if (repartiLe === null) return;
    // L'arrivée ne part que si elle est saisie : `undefined` laisse la colonne
    // telle quelle, alors que `null` l'effacerait.
    const arriveLe =
      validation.heureArrivee === ''
        ? null
        : instantDepuis(validation.jour, validation.heureArrivee);

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await modifierEtape(j, otId, validation.etapeId, {
        fait: true,
        reparti_le: repartiLe,
        ...(arriveLe === null ? {} : { arrive_le: arriveLe }),
      });
      if (resultat.etat !== 'ok') {
        if (resultat.cause === 'acces') quandAccesPerdu();
        finirLEcriture(resultat);
        return;
      }

      /**
       * LE TEMPS PASSÉ, DANS LA FOULÉE — et le coût se fige derrière.
       *
       * Écrit APRÈS la validation, et non avec : ce sont deux tables et deux
       * routes. Si la seconde rate, l'étape reste validée — ce qui est le bon
       * sens : le camion est bien arrivé, seul le temps manque, et il se
       * rattrape par « Recalculer ».
       */
      const minutes = minutesSaisies(tempsPasse);
      if (minutes !== null) {
        await poserLExecution(j, otId, { temps_reel_minutes: minutes });
        setTempsPasse('');
        const lecture = await lireLeCout(j, otId);
        if (lecture.etat === 'ok') setCout(lecture.valeur);
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setValidation(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  /**
   * La boîte de litige, confirmée.
   *
   * Le motif est CRÉÉ D'ABORD s'il est neuf, dans un appel séparé : il n'existe
   * pas de route qui fasse les deux, et en inventer une pour ce seul geste
   * mêlerait un paramétrage à une écriture de transport. Si la création réussit
   * et que l'étape échoue, le motif reste — ce qui est sans dommage : il
   * resservira.
   */
  /**
   * RÉSOUDRE : dire COMMENT, obligatoirement.
   *
   * L'API le refuse sinon, et la base aussi (`litiges_resolution_complete`) —
   * mais on ne fait pas partir un appel qu'on sait perdu.
   */
  const enregistrerLaResolution = () => {
    if (resolution === null || fiche.nom !== 'prete') return;
    if (resolution.texte.trim() === '') {
      setAction((a) => ({ ...a, refus: 'Dites comment le litige a été réglé.' }));
      return;
    }
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const resoluLe = instantDepuis(resolution.jour, resolution.heure);
      const fait = await resoudreUnLitige(j, resolution.litigeId, {
        resolution: resolution.texte.trim(),
        ...(resoluLe === null ? {} : { resolu_le: resoluLe }),
      });
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        finirLEcriture(fait);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setResolution(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  const ouvrirLeLitige = () => {
    if (litige === null || fiche.nom !== 'prete') return;
    if (refusLitige(litige) !== null) return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      let motifId = litige.motifId;
      if (litige.motifNouveau !== null) {
        const cree = await creerMotifLitige(j, litige.motifNouveau.trim());
        if (cree.etat !== 'ok') {
          if (cree.cause === 'acces') quandAccesPerdu();
          finirLEcriture(cree);
          return;
        }
        setMotifs((actuels) => [...actuels, cree.valeur]);
        motifId = cree.valeur.id;
      }

      // LE LITIGE A SA PROPRE ROUTE depuis le 07/09/2026 : il ne se pose plus
      // par un PATCH de l'étape. Et il ne rend plus la fiche : on la relit,
      // pour que le bandeau et le compteur viennent de la base.
      const ouvertLe = instantDepuis(litige.jour, litige.heure);
      const pose = await ouvrirUnLitige(j, litige.etapeId, {
        motif_litige_id: motifId,
        ...(litige.note.trim() === '' ? {} : { note: litige.note.trim() }),
        ...(ouvertLe === null ? {} : { ouvert_le: ouvertLe }),
      });
      if (pose.etat !== 'ok') {
        if (pose.cause === 'acces') quandAccesPerdu();
        finirLEcriture(pose);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setLitige(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  /**
   * ENREGISTRER L'ÉCHANGE — ou l'effacer, ce qui est le même geste avec un
   * corps vide.
   *
   * L'écriture passe par le module Emballage, puis la fiche est RELUE en
   * entier. Elle ne se met pas à jour toute seule : l'échange y arrive par une
   * jointure de `SELECTION_ETAPES`, et l'API vient peut-être aussi de créer un
   * bon. Recopier le résultat à la main dans l'état laisserait la fiche dire
   * autre chose que la base.
   */
  const enregistrerLEchange = (effacer: boolean) => {
    if (echange === null || fiche.nom !== 'prete') return;
    if (!effacer && refusEchange(echange.saisie) !== null) return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const pose = await poserLEchange(
        j,
        echange.etapeId,
        effacer ? {} : corpsEchange(echange.saisie),
      );
      if (pose.etat !== 'ok') {
        if (pose.cause === 'acces') quandAccesPerdu();
        finirLEcriture(pose);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setEchange(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  /**
   * OUVRIR LE PANNEAU, et chercher qui fait cet axe.
   *
   * L'axe se déduit des colonnes que la liste porte déjà — `chargement_ville`
   * et `livraison_ville` viennent du premier chargement et de la dernière
   * livraison. On ne redemande rien à personne.
   *
   * La recherche N'ÉCHOUE PAS le panneau : si elle rate, la liste est vide et
   * le panneau le dit. Refuser d'ouvrir pour un défaut de réseau empêcherait
   * d'affréter un ordre dont on connaît déjà le transporteur.
   */
  const ouvrirLAffretement = () => {
    if (fiche.nom !== 'prete') return;
    const ordre = fiche.fiche.ordre_transport;

    setValidation(null);
    setLitige(null);
    setEchange(null);
    setAction((a) => ({ ...a, refus: null }));
    setAffretement(AFFRETEMENT_VIDE);
    setProposes([]);
    setTousTransporteurs([]);
    setNouveauTransporteur(null);
    setChercheEnCours(true);

    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setChercheEnCours(false);
        return;
      }
      // L'AXE VIENT DE L'ORDRE, calculé par l'API : le pays est traduit en
      // base, par la table `pays`. Le recalculer ici ferait une seconde
      // traduction, et deux traductions finissent par diverger.
      const [trouve, annuaire] = await Promise.all([
        chercherTransporteurs(j, {
          depart: ordre.zone_depart,
          arrivee: ordre.zone_arrivee,
          vehicule: ordre.gabarit_code,
        }),
        lireListe(j, ANNUAIRE_TRANSPORTEURS),
      ]);
      if (trouve.etat === 'ok') setProposes(trouve.valeur.transporteurs);
      if (annuaire.etat === 'ok') setTousTransporteurs(annuaire.valeur);
      setChercheEnCours(false);
    })();
  };

  /**
   * OUVRIR LE FORMULAIRE DE CRÉATION, par-dessus le panneau d'affrètement.
   *
   * Le choix du déroulant est remis à vide : « + Nouveau transporteur… » n'est
   * pas une sélection, c'est un geste. Le laisser posé enverrait `+nouveau`
   * comme identifiant si la création était abandonnée.
   */
  const ouvrirLeNouveauTransporteur = () => {
    setAffretement((a) => (a === null ? a : { ...a, transporteurId: '' }));
    setNouveauTransporteur({
      saisie: saisieVide(ANNUAIRE_TRANSPORTEURS),
      fautes: {},
      refus: null,
    });
  };

  /**
   * CRÉER, puis RELIRE L'ANNUAIRE et sélectionner le nouveau venu.
   *
   * On relit plutôt que d'ajouter la fiche rendue à la liste en mémoire : c'est
   * la même règle que partout ici — ce que l'écran affiche vient de la base, et
   * non d'une recopie qui finirait par diverger.
   */
  const enregistrerLeNouveauTransporteur = () => {
    const encours = nouveauTransporteur;
    if (encours === null) return;

    const fautes = validerFiche(encours.saisie, ANNUAIRE_TRANSPORTEURS);
    if (Object.keys(fautes).length > 0) {
      setNouveauTransporteur({ ...encours, fautes, refus: null });
      return;
    }

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const cree = await creerFiche(
        j,
        ANNUAIRE_TRANSPORTEURS,
        pourCreation(encours.saisie, ANNUAIRE_TRANSPORTEURS),
      );
      if (cree.etat !== 'ok') {
        if (cree.cause === 'acces') quandAccesPerdu();
        finirLEcriture(cree);
        return;
      }

      const neuf = cree.valeur.fiche.id;
      const annuaire = await lireListe(j, ANNUAIRE_TRANSPORTEURS);
      if (annuaire.etat === 'ok') setTousTransporteurs(annuaire.valeur);
      setAffretement((a) => (a === null ? a : { ...a, transporteurId: neuf }));
      setNouveauTransporteur(null);
      finirLEcriture({ etat: 'ok' });
    })();
  };

  /**
   * POSER LE CLIENT sur la commande en cours, qu'on la CRÉE ou qu'on la MODIFIE.
   *
   * Les deux états sont mis à jour PAR FONCTION, et les deux sont appelés sans
   * se demander lequel est ouvert : un seul est non nul à la fois, l'autre ne
   * fait rien. Lire `creation` ici serait lire une valeur figée au moment où le
   * geste a commencé — l'enregistrement passe par le réseau, et l'écran a pu
   * changer entre-temps.
   */
  const poserLeClient = (id: string) => {
    setCreation((c) =>
      c === null
        ? c
        : { ...c, ordre: { ...c.ordre, saisie: { ...c.ordre.saisie, client_id: id } } },
    );
    setEditionOrdre((e) =>
      e === null ? e : { ...e, saisie: { ...e.saisie, client_id: id } },
    );
  };

  const ouvrirLeNouveauClient = () => {
    setNouveauClient({ saisie: saisieVide(ANNUAIRE_CLIENTS), fautes: {}, refus: null });
  };

  /**
   * CRÉER LE CLIENT, puis RELIRE L'ANNUAIRE et le sélectionner sur l'ordre.
   *
   * On relit plutôt que d'ajouter la fiche rendue à la liste en mémoire : même
   * règle que pour le transporteur — ce que l'écran affiche vient de la base,
   * et non d'une recopie qui finirait par diverger.
   */
  const enregistrerLeNouveauClient = () => {
    const encours = nouveauClient;
    if (encours === null) return;

    const fautes = validerFiche(encours.saisie, ANNUAIRE_CLIENTS);
    if (Object.keys(fautes).length > 0) {
      setNouveauClient({ ...encours, fautes, refus: null });
      return;
    }

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const cree = await creerFiche(
        j,
        ANNUAIRE_CLIENTS,
        pourCreation(encours.saisie, ANNUAIRE_CLIENTS),
      );
      if (cree.etat !== 'ok') {
        if (cree.cause === 'acces') quandAccesPerdu();
        finirLEcriture(cree);
        return;
      }

      const neuf = cree.valeur.fiche.id;
      const annuaire = await lireListe(j, ANNUAIRE_CLIENTS);
      if (annuaire.etat === 'ok') setClients(annuaire.valeur);
      poserLeClient(neuf);
      setNouveauClient(null);
      finirLEcriture({ etat: 'ok' });
    })();
  };

  /**
   * AFFRÉTER, puis relire la fiche.
   *
   * L'API fige le nom, le contact, le prix et l'axe, et l'ordre sort du
   * planning dans la foulée. Recopier le résultat à la main dans l'état
   * laisserait la fiche dire autre chose que la base.
   */
  const enregistrerLAffretement = () => {
    if (affretement === null || fiche.nom !== 'prete') return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const fait = await affreter(j, otId, corpsAffretement(affretement));
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        finirLEcriture(fait);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setAffretement(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  /**
   * ANNULER L'AFFRÈTEMENT — la ligne reste, l'ordre revient au planning.
   */
  const annulerLeAffretement = () => {
    if (fiche.nom !== 'prete') return;
    const ordre = fiche.fiche.ordre_transport;
    if (ordre.affretement_id === null) return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const fait = await annulerLAffretement(j, ordre.affretement_id as string);
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        finirLEcriture(fait);
        return;
      }
      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') poser(relue.valeur);
      finirLEcriture(relue);
    })();
  };

  /**
   * Monter ou descendre une étape.
   *
   * L'écran envoie L'ORDRE ENTIER, et non le rang de l'étape déplacée : deux
   * `PATCH` de rang échoueraient au premier, l'index unique (ot_id, rang) étant
   * vérifié à chaque ligne. La route `PUT` renumérote tout dans une seule
   * transaction.
   */
  const deplacerEtape = (rang: number, pas: -1 | 1) => {
    if (fiche.nom !== 'prete') return;
    const etapes = [...fiche.fiche.etapes];
    const vers = rang + pas;
    if (vers < 0 || vers >= etapes.length) return;
    const [deplacee] = etapes.splice(rang, 1);
    if (deplacee === undefined) return;
    etapes.splice(vers, 0, deplacee);

    const otId = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await reordonnerEtapes(
        j,
        otId,
        etapes.map((e) => e.id),
      );
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setAction({
          confirmeAnnulation: false,
          confirmeSuppression: false,
          envoi: false,
          refus: null,
        });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: REFUS[resultat.cause],
      });
    })();
  };

  const supprimerLEtape = () => {
    if (editionEtape === null || editionEtape.id === null || fiche.nom !== 'prete') {
      return;
    }
    const encours = editionEtape;
    const otId = fiche.id;
    const etapeId = encours.id as string;
    setEditionEtape({ ...encours, envoi: true, refus: null });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await supprimerEtape(j, otId, etapeId);
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setEditionEtape(null);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEditionEtape({ ...encours, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  /**
   * Crée le site replié dans la carte, puis le rattache à l'étape.
   *
   * Il est ajouté à la liste des sites RENDUE PAR L'API, jamais à ce qui a été
   * saisi : la base a pu normaliser une valeur.
   */
  /**
   * LIRE UNE CONFIRMATION ET OUVRIR LA SAISIE — lot 124.
   *
   * TROIS TEMPS, ET AUCUN N'ÉCRIT EN BASE :
   *   1. on refuse tout de suite ce qui n'est pas lisible — un mauvais
   *      fichier ne doit pas coûter trente secondes d'attente ;
   *   2. on envoie le document et on attend le modèle ;
   *   3. on ouvre le formulaire de création AVEC ce qui a été lu.
   *
   * L'ÉTAPE 3 EST EXACTEMENT CE QUE FAIT `quandCreer`, à la saisie près. Le
   * formulaire, ses contrôles, son enregistrement : rien n'est dupliqué. Si
   * la création d'un ordre change un jour, elle change pour les deux.
   */
  const lireUneConfirmation = (fichier: File | null) => {
      if (fichier === null) return;
      const refus = refusDuDocument({
        nom: fichier.name,
        type: fichier.type,
        taille: fichier.size,
      });
      if (refus !== null) {
        setLecture({ quoi: 'refus', message: refus });
        return;
      }

      setLecture({ quoi: 'en_cours', nom: fichier.name });
      void (async () => {
        const j = await avecJeton();
        if (j === null) {
          setLecture({ quoi: 'repos' });
          return;
        }
        const octets = await fichier.arrayBuffer();
        const resultat = await lireConfirmation(j, {
          base64: enBase64(octets),
          type: 'application/pdf',
        });
        if (resultat.etat !== 'ok') {
          if (resultat.cause === 'acces') quandAccesPerdu();
          setLecture({ quoi: 'refus', message: REFUS[resultat.cause] });
          return;
        }

        const suite = suiteDeLaLecture(resultat.valeur, fichier.name, aujourdhui());
        setLecture(suite.etat);
        if (suite.saisie === null) return;

        setVue('creation');
        setFiche({ nom: 'aucune' });
        setEditionOrdre(null);
        setEditionEtape(null);
        setVenuDUneConfirmation(true);
        setCreation({
          ordre: editionVide(suite.saisie.ordre),
          etapes: suite.saisie.etapes.map((e) => editionEtapeVide(e, null)),
        });
      })();
  };

  /** Relit la liste des confirmations à créer. */
  const rechargerACreer = useCallback(async (): Promise<readonly ConfirmationACreer[] | null> => {
    const j = await jeton();
    if (j === null) return null;
    const r = await listerLesConfirmations(j);
    if (r.etat !== 'ok') return null;
    setACreer((a) => ({ ...a, liste: r.valeur }));
    return r.valeur;
  }, [jeton]);

  /**
   * À L'OUVERTURE : la liste, l'abonnement à la lecture en arrière-plan, et
   * la lecture elle-même si des confirmations attendent (l'application la
   * lance aussi ; deux appels ne font qu'une boucle).
   */
  useEffect(() => {
    void rechargerACreer();
    lancerLaLecture(jeton);
    return sAbonnerALaLecture(() => {
      void rechargerACreer();
    });
  }, [rechargerACreer, jeton]);

  const deposer = (fichiers: readonly File[]) => {
    const pdf = fichiers.filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
    );
    const ignores = fichiers.length - pdf.length;
    if (pdf.length === 0) {
      setACreer((a) => ({ ...a, refus: 'Seuls les PDF sont lus.' }));
      return;
    }
    setACreer((a) => ({ ...a, envois: a.envois + pdf.length, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const rates: string[] = [];
      for (const f of pdf) {
        const r = await deposerUneConfirmation(j, f);
        if (r.etat !== 'ok') rates.push(f.name);
        setACreer((a) => ({ ...a, envois: Math.max(0, a.envois - 1) }));
        await rechargerACreer();
      }
      const refus = [
        rates.length > 0 ? `Non envoyé : ${rates.join(', ')}.` : null,
        ignores > 0 ? `${ignores} fichier${ignores > 1 ? 's' : ''} non PDF ignoré${ignores > 1 ? 's' : ''}.` : null,
      ].filter((x) => x !== null).join(' ');
      setACreer((a) => ({ ...a, refus: refus === '' ? null : refus }));
      lancerLaLecture(jeton);
    })();
  };

  /**
   * VÉRIFIER : la saisie pré-remplie s'ouvre, exactement comme après une
   * lecture une par une (lot 124) — même `suiteDeLaLecture`, même formulaire.
   */
  const verifier = (c: ConfirmationACreer) => {
    const faite = lectureDe(c);
    if (faite === null) return;
    const suite = suiteDeLaLecture(faite, c.nom, aujourdhui());
    setLecture(suite.etat);
    if (suite.saisie === null) return;
    setConfirmationEnCours(c.id);
    setVue('creation');
    setFiche({ nom: 'aucune' });
    setEditionOrdre(null);
    setEditionEtape(null);
    setVenuDUneConfirmation(true);
    setCreation({
      ordre: editionVide(suite.saisie.ordre),
      etapes: suite.saisie.etapes.map((e) => editionEtapeVide(e, null)),
    });
  };

  const ecarter = (c: ConfirmationACreer) => {
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const r = await ecarterLaConfirmation(j, c.id);
      if (r.etat !== 'ok') setACreer((a) => ({ ...a, refus: REFUS[r.cause] }));
      await rechargerACreer();
    })();
  };

  const relireUne = (c: ConfirmationACreer) => {
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      setACreer((a) => ({
        ...a,
        refus: null,
        liste: (a.liste ?? []).map((x) => (x.id === c.id ? { ...x, etat: 'a_lire' as const } : x)),
      }));
      const issue = await relire(j, c);
      if (issue === 'panne') setACreer((a) => ({ ...a, refus: `« ${c.nom} » n’a pas pu être relue.` }));
      await rechargerACreer();
    })();
  };

  const creerLeSite = (
    edition: EditionEtape,
    poserEdition: (e: EditionEtape) => void,
  ) => {
    const saisieSite = edition.site;
    if (saisieSite === null) return;
    const fautesSite = validerSite(saisieSite);
    if (Object.keys(fautesSite).length > 0) {
      poserEdition({ ...edition, fautesSite });
      return;
    }

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await creerFiche(
        j,
        ANNUAIRE_SITES,
        /*
          LA PROVENANCE — lot 124. « Confirmation » n'est posé que si la
          saisie en cours vient d'un document lu. Un site créé à la main
          garde une provenance vide, et c'est ce qui donne du sens aux
          fiches qui en portent une.
        */
        pourCreationSite(
          saisieSite,
          venuDUneConfirmation ? 'Confirmation' : undefined,
          // LE CODE ENGENDRÉ — F2. Même règle que l'annuaire : quatre lettres
          // du nom et un numéro libre parmi les sites chargés. L'unicité
          // reste tenue par la base (`sites_societe_code_idx`).
          codePropose(saisieSite.nom, sites),
        ),
      );
      if (resultat.etat !== 'ok') {
        if (resultat.cause === 'acces') quandAccesPerdu();
        poserEdition({ ...edition, refus: REFUS[resultat.cause] });
        return;
      }

      const cree = resultat.valeur.fiche;
      setSites((actuels) => [...actuels, cree]);
      poserEdition({
        ...edition,
        site: null,
        fautesSite: {},
        refus: null,
        saisie: {
          ...edition.saisie,
          site_id: cree.id,
          site_nom: cree.nom,
          site_adresse: valeur(cree, 'adresse') ?? '',
          site_code_postal: cree.code_postal ?? '',
          site_ville: cree.ville ?? '',
        },
      });
    })();
  };

  /**
   * Annuler et rouvrir, les deux seules décisions humaines sur le statut.
   *
   * Elles passent par leurs propres routes et non par une modification de
   * champ : le statut se déduit des étapes, et un `PATCH { statut }` poserait
   * une valeur que le premier changement d'étape écraserait. L'API le refuse
   * d'ailleurs depuis le 04/09/2026.
   *
   * Le statut affiché après coup est CELUI QUE L'API REND, jamais celui qu'on
   * espérait : à la réouverture, c'est l'état réel des étapes.
   */
  const changerLAnnulation = (
    appel: (jeton: string, id: string) => Promise<Resultat<FicheOrdre>>,
  ) => {
    if (fiche.nom !== 'prete') return;
    const id = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await appel(j, id);
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setAction({
          confirmeAnnulation: false,
          confirmeSuppression: false,
          envoi: false,
          refus: null,
        });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: REFUS[resultat.cause],
      });
    })();
  };

  const annulerLOrdre = () => {
    changerLAnnulation(annulerOrdre);
  };

  const rouvrirLOrdre = () => {
    changerLAnnulation(rouvrirOrdre);
  };

  /**
   * LE GESTE DE FACTURATION. Les deux routes ne rendent RIEN (204) : on
   * recharge la fiche pour lire l'état réel, comme partout ailleurs.
   */
  const bougerLaFacturation = (
    appel: (jeton: string, id: string) => Promise<Resultat<null>>,
  ) => {
    if (fiche.nom !== 'prete') return;
    const id = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await appel(j, id);
      if (resultat.etat !== 'ok') {
        if (resultat.cause === 'acces') quandAccesPerdu();
        setAction((a) => ({ ...a, envoi: false, refus: REFUS[resultat.cause] }));
        return;
      }
      // `poser` REMET AUSSI LA LIGNE DE LA LISTE À JOUR : sans elle, revenir
      // en arrière montrerait l'ordre sans sa nouvelle pastille.
      const relue = await lireOrdre(j, id);
      if (relue.etat === 'ok') poser(relue.valeur);
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: null,
      });
    })();
  };

  const supprimerLOrdre = () => {
    if (fiche.nom !== 'prete') return;
    const id = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await supprimerOrdre(j, id);
      if (resultat.etat === 'ok') {
        setListe((actuelle) =>
          actuelle.nom === 'prete'
            ? { nom: 'prete', ordres: actuelle.ordres.filter((o) => o.id !== id) }
            : actuelle,
        );
        setFiche({ nom: 'aucune' });
        setVue('liste');
        setAction({
          confirmeAnnulation: false,
          confirmeSuppression: false,
          envoi: false,
          refus: null,
        });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: REFUS[resultat.cause],
      });
    })();
  };

  /**
   * La création : l'ordre D'ABORD, ses étapes ENSUITE.
   *
   * L'ordre doit exister avant qu'on puisse lui accrocher quoi que ce soit —
   * les étapes sont sous son chemin. Si une étape échoue, l'ordre reste : il
   * est ouvert sur sa fiche avec ce qui a été créé, et le message dit laquelle
   * manque. C'est mieux que d'effacer un ordre déjà numéroté, qui trouerait la
   * numérotation.
   */
  const enregistrerCreation = () => {
    if (creation === null) return;

    const fautesOrdre = validerOrdre(creation.ordre.saisie);
    if (Object.keys(fautesOrdre).length > 0) {
      setCreation({
        ...creation,
        ordre: { ...creation.ordre, fautes: fautesOrdre, refus: null },
      });
      return;
    }

    const etapesFautives = creation.etapes.map((e) => validerEtape(e.saisie));
    if (etapesFautives.some((f) => Object.keys(f).length > 0)) {
      setCreation({
        ...creation,
        ordre: {
          ...creation.ordre,
          refus: 'Chaque étape demande un site et une date.',
        },
        etapes: creation.etapes.map((e, rang) => ({
          ...e,
          fautes: etapesFautives[rang] ?? {},
        })),
      });
      return;
    }

    // LES LIGNES DU PACKAGING — F4. Une faute bloque l'enregistrement : elle
    // se voit sous sa case, et un métrage faux se facturerait.
    const lignes = lignesDe(creation, supports);
    const types = creation.etapes.map((e) => e.saisie.type);
    if (lignes.some((l) => Object.keys(validerLigneMarchandise(l, types)).length > 0)) {
      setCreation({
        ...creation,
        ordre: {
          ...creation.ordre,
          refus: 'Une ligne du Packaging est incomplète : corrigez-la avant d’enregistrer.',
        },
      });
      return;
    }

    const encours = creation;
    setCreation({
      ...encours,
      ordre: { ...encours.ordre, fautes: {}, refus: null, envoi: true },
    });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const cree = await creerOrdre(j, pourCreationOrdre(encours.ordre.saisie));
      if (cree.etat !== 'ok') {
        if (cree.cause === 'acces') quandAccesPerdu();
        setCreation({
          ...encours,
          ordre: { ...encours.ordre, envoi: false, refus: REFUS[cree.cause] },
        });
        return;
      }

      const id = cree.valeur.ordre_transport.id;
      let derniere = cree.valeur;
      /**
       * L'ORDRE EXISTE : la confirmation dont il vient est « créée » tout de
       * suite, et son PDF joint — même si une étape est refusée plus bas,
       * l'ordre est là et se corrige sur sa fiche (lot 147).
       */
      const venueDe = confirmationEnCours;
      if (venueDe !== null) {
        await marquerCreee(j, venueDe, id);
        setConfirmationEnCours(null);
      }
      // LES IDENTIFIANTS DES ÉTAPES, dans l'ordre de la saisie : les lignes
      // du Packaging y pointent par rang.
      const ids: string[] = [];
      for (const etape of encours.etapes) {
        // AVEC DES LIGNES, LA MARCHANDISE NE PART PAS SUR L'ÉTAPE : l'API la
        // recalcule depuis les lignes (`projeterLesMarchandises`).
        const corps =
          lignes.length > 0
            ? Object.fromEntries(
                Object.entries(corpsEtape(etape.saisie, true)).filter(
                  ([cle]) => cle !== 'quantite' && cle !== 'support' && cle !== 'poids',
                ),
              )
            : corpsEtape(etape.saisie, true);
        const ajout = await ajouterEtape(j, id, corps);
        if (ajout.etat !== 'ok') {
          // L'ordre EXISTE : on l'ouvre plutôt que de le laisser invisible, et
          // on dit ce qui manque.
          poser(derniere);
          setCreation(null);
          setVue('fiche');
          setOnglet('etapes');
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: false,
            envoi: false,
            refus: `L’ordre a été créé, mais une étape a été refusée : ${REFUS[ajout.cause]}`,
          });
          return;
        }
        derniere = ajout.valeur;
        // LA NOUVELLE ÉTAPE est celle du plus grand rang : l'API range les
        // étapes ajoutées à la suite.
        const neuve = [...ajout.valeur.etapes].sort((a, b) => b.rang - a.rang)[0];
        ids.push(neuve?.id ?? '');
      }

      // LES LIGNES DU PACKAGING, une fois les étapes connues — F4.
      if (lignes.length > 0) {
        const pose = await remplacerLesMarchandises(j, id, corpsMarchandises(lignes, ids));
        if (pose.etat !== 'ok') {
          poser(derniere);
          setCreation(null);
          setVue('fiche');
          setOnglet('commande');
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: false,
            envoi: false,
            refus: `L’ordre a été créé, mais le Packaging a été refusé : ${REFUS[pose.cause]}`,
          });
          return;
        }
        derniere = pose.valeur;
      }

      // LES PRESTATIONS ANNEXES, après l'ordre et ses étapes — F3. Une ligne
      // sans nature ne part pas : elle n'a pas de sens.
      for (const ligne of encours.prestations ?? []) {
        if (ligne.natureId === '') continue;
        const ajout = await ajouterUnePrestation(j, id, {
          nature_id: ligne.natureId,
          detail: ligne.detail.trim() === '' ? null : ligne.detail.trim(),
          quantite: ligne.quantite.trim().replace(',', '.'),
          prix_unitaire: ligne.prixUnitaire.trim().replace(',', '.'),
        });
        if (ajout.etat !== 'ok') {
          // L'ORDRE EXISTE : on l'ouvre et on dit ce qui manque, comme pour
          // une étape refusée.
          poser(derniere);
          setCreation(null);
          setVue('fiche');
          setOnglet('commande');
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: false,
            envoi: false,
            refus: `L’ordre a été créé, mais une prestation annexe a été refusée : ${REFUS[ajout.cause]}`,
          });
          return;
        }
      }

      // LES DOCUMENTS DÉPOSÉS AVANT L'ENREGISTREMENT — F3. Mêmes trois
      // temps que l'onglet Documents : déclarer, envoyer, confirmer.
      for (const piece of encours.documents ?? []) {
        const fichier = piece.fichier;
        if (fichier === null) continue;
        const declare = await declarerLeDocument(j, id, {
          nom: fichier.name,
          type_id: piece.typeId === '' ? null : piece.typeId,
          poids: fichier.size,
        });
        const envoye =
          declare.etat === 'ok'
            ? await envoyerLeFichier(j, declare.valeur.chemin, fichier)
            : null;
        if (declare.etat !== 'ok' || envoye === null || envoye.etat !== 'ok') {
          poser(derniere);
          setCreation(null);
          setVue('fiche');
          setOnglet('documents');
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: false,
            envoi: false,
            refus: `L’ordre a été créé, mais le document « ${fichier.name} » n’a pas pu être joint. Reprenez-le dans l’onglet Documents.`,
          });
          return;
        }
        await confirmerLeDocument(j, id, declare.valeur.id);
      }

      /**
       * LA SUIVANTE S'OUVRE — réponse de Mohamed : « à la suite, une par
       * une ». Plus rien à vérifier : retour à l'onglet « À créer ».
       */
      if (venueDe !== null && encours.affreter !== true) {
        // LA LISTE DES ORDRES REÇOIT LE NOUVEL OT, même si on enchaîne.
        poser(derniere);
        const liste = await rechargerACreer();
        const suivante = liste === null ? undefined : suivanteAVerifier(liste, venueDe);
        if (suivante !== undefined) {
          verifier(suivante);
          return;
        }
        setCreation(null);
        setLecture({ quoi: 'repos' });
        setVue('liste');
        setOngletListe('a_creer');
        return;
      }

      poser(derniere);
      setCreation(null);
      setVue('fiche');
      setOnglet('commande');
      if (encours.affreter === true) setAffreterApresCreation(true);
    })();
  };

  /**
   * « AFFRÉTER À UN TRANSPORTEUR… » CHOISI À LA SAISIE — F3. La boîte
   * d'affrètement ne s'ouvre que sur une fiche prête : on attend qu'elle le
   * soit, puis on l'ouvre une fois.
   */
  const [affreterApresCreation, setAffreterApresCreation] = useState(false);
  useEffect(() => {
    if (!affreterApresCreation || fiche.nom !== 'prete') return;
    setAffreterApresCreation(false);
    ouvrirLAffretement();
    // `ouvrirLAffretement` est recréée à chaque rendu ; seule la fiche prête
    // doit déclencher l'ouverture.
  }, [affreterApresCreation, fiche.nom]);

  /**
   * LE PACKAGING D'UN ORDRE EXISTANT, EN MODIFICATION — F4. Remis à zéro dès
   * qu'on change d'ordre : des lignes saisies sur un ordre ne doivent jamais
   * partir sur un autre.
   */
  const [editionPackaging, setEditionPackaging] = useState<
    readonly LigneMarchandise[] | null
  >(null);
  const idFiche = fiche.nom === 'aucune' ? null : fiche.id;
  useEffect(() => {
    setEditionPackaging(null);
  }, [idFiche]);

  const modifierLePackaging = () => {
    if (fiche.nom !== 'prete') return;
    const { etapes, marchandises } = fiche.fiche;
    const types = etapes.map((e) => e.type);
    // Un ordre sans ligne part de ce que portent ses chargements : c'est ce
    // qu'on voyait sur les étapes, et il n'y a rien à retaper.
    const depart =
      (marchandises ?? []).length > 0
        ? lignesDepuisLues(marchandises ?? [], etapes)
        : marchandisesDepuisEtapes(etapes.map(etapeDepuis), supports);
    setEditionPackaging(depart.length > 0 ? depart : [ligneMarchandiseVide(types)]);
  };

  const enregistrerLePackaging = () => {
    if (fiche.nom !== 'prete' || editionPackaging === null) return;
    const { etapes } = fiche.fiche;
    const types = etapes.map((e) => e.type);
    if (
      editionPackaging.some((l) => Object.keys(validerLigneMarchandise(l, types)).length > 0)
    ) {
      setAction((a) => ({
        ...a,
        refus: 'Une ligne du Packaging est incomplète : corrigez-la avant d’enregistrer.',
      }));
      return;
    }
    const otId = fiche.id;
    const lignes = editionPackaging;
    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const pose = await remplacerLesMarchandises(
        j,
        otId,
        corpsMarchandises(
          lignes,
          etapes.map((e) => e.id),
        ),
      );
      if (pose.etat !== 'ok') {
        if (pose.cause === 'acces') quandAccesPerdu();
        setAction((a) => ({ ...a, envoi: false, refus: REFUS[pose.cause] }));
        return;
      }
      poser(pose.valeur);
      setEditionPackaging(null);
      setAction((a) => ({ ...a, envoi: false, refus: null }));
    })();
  };

  // -------------------------------------------------------------------------

  const majEtapeCreation = (rang: number, changement: Partial<EditionEtape>) => {
    setCreation((actuelle) =>
      actuelle === null
        ? actuelle
        : {
            ...actuelle,
            etapes: actuelle.etapes.map((e, i) =>
              i === rang ? { ...e, ...changement } : e,
            ),
          },
    );
  };

  return (
    <VueOrdres
      jeton={jeton}
      quandAccesPerdu={quandAccesPerdu}
      vue={vue}
      liste={liste}
      fiche={fiche}
      onglet={onglet}
      fenetre={fenetre}
      horsPeriode={horsPeriode}
      quandToutesLesDates={() => setFenetre({ du: '', au: '' })}
      trouves={trouves}
      etendue={etendue}
      quandChercherPartout={() => {
        void chercherPartout();
      }}
      // Revenir à la liste ordinaire : la fenêtre n'a pas bougé, c'est donc la
      // clé de rechargement qui relance l'appel — et ce chargement remettra
      // `etendue` à `null` tout seul.
      quandRevenirALaListe={() => setRechargement((n) => n + 1)}
      filtres={filtres}
      tri={tri}
      panneauOuvert={panneauOuvert}
      recherche={recherche}
      clients={clients}
      centres={centres}
      sites={sites}
      gabarits={gabarits}
      motifs={motifs}
      supports={supports}
      vehicules={vehicules}
      chauffeurs={chauffeurs}
      remorques={remorques}
      editionOrdre={editionOrdre}
      editionEtape={editionEtape}
      creation={creation}
      actions={{
        confirmeAnnulation: action.confirmeAnnulation,
        confirmeSuppression: action.confirmeSuppression,
        envoi: action.envoi,
        refus: action.refus,
        quandAnnulerOrdre: annulerLOrdre,
        quandRouvrirOrdre: rouvrirLOrdre,
        quandEnvoyerEnFacturation: () => {
          bougerLaFacturation(envoyerEnFacturation);
        },
        quandRetirerDeLaFacturation: () => {
          bougerLaFacturation(retirerDeLaFacturation);
        },
        quandSupprimerOrdre: supprimerLOrdre,
        quandDemanderAnnulation: () =>
          setAction({
            confirmeAnnulation: true,
            confirmeSuppression: false,
            envoi: false,
            refus: null,
          }),
        quandDemanderSuppression: () =>
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: true,
            envoi: false,
            refus: null,
          }),
        quandRenoncer: () =>
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: false,
            envoi: false,
            refus: null,
          }),
      }}
      quandFenetre={setFenetre}
      quandFiltres={setFiltres}
      quandTri={(colonne) => setTri((actuel: Tri) => triSuivant(actuel, colonne))}
      quandPanneau={setPanneauOuvert}
      quandRecherche={setRecherche}
      quandOuvrir={ouvrir}
      libelleRetour={retourVers?.libelle ?? 'Les OT'}
      quandRetour={() => {
        /**
         * ON REMET LA FICHE À ZÉRO DANS LES DEUX CAS, même quand on repart
         * vers un autre écran : sans cela, revenir plus tard par le menu
         * Exploitation rouvrirait la fiche d'avant, sur un ordre qui n'a plus
         * rien à voir avec ce qu'on cherche.
         */
        setVue('liste');
        setFiche({ nom: 'aucune' });
        setEditionOrdre(null);
        setEditionEtape(null);
        setCreation(null);
        // REVENIR D'UNE VÉRIFICATION ramène à l'onglet « À créer » (lot 147).
        if (confirmationEnCours !== null) {
          setConfirmationEnCours(null);
          setLecture({ quoi: 'repos' });
          setOngletListe('a_creer');
        }
        if (retourVers !== null) retourVers.quand();
      }}
      quandOnglet={(o) => {
        setOnglet(o);
        setEditionOrdre(null);
        setEditionEtape(null);
        setRefusDocument(null);
      }}
      documents={documents}
      typesDocument={typesDocument}
      ajoutDocument={ajoutDocument}
      envoiDocument={envoiDocument}
      refusDocument={refusDocument}
      quandOuvrirAjout={() => {
        setRefusDocument(null);
        setAjoutDocument({ typeId: '', fichier: null });
      }}
      quandChampAjout={(typeId) =>
        setAjoutDocument((actuel) => (actuel === null ? null : { ...actuel, typeId }))
      }
      quandFichier={(fichier) => {
        setRefusDocument(
          fichier !== null && fichier.size > POIDS_MAXIMUM
            ? 'Ce fichier dépasse 20 Mo. Réduisez-le, ou joignez-en un autre.'
            : null,
        );
        setAjoutDocument((actuel) => (actuel === null ? null : { ...actuel, fichier }));
      }}
      quandFermerAjout={() => {
        setAjoutDocument(null);
        setRefusDocument(null);
      }}
      /**
       * JOINDRE : la ligne d'abord, le fichier ensuite, la confirmation enfin.
       *
       * Si l'envoi du fichier échoue, la ligne reste en « envoi inachevé » et
       * se voit à l'écran — c'est voulu. L'inverse, effacer la ligne en
       * silence, laisserait croire que rien n'a été tenté.
       */
      quandJoindre={() => {
        if (fiche.nom !== 'prete' || ajoutDocument === null) return;
        const fichier = ajoutDocument.fichier;
        if (fichier === null) return;
        if (fichier.size > POIDS_MAXIMUM) {
          setRefusDocument('Ce fichier dépasse 20 Mo.');
          return;
        }

        const otId = fiche.id;
        setEnvoiDocument(true);
        setRefusDocument(null);

        void (async () => {
          const j = await avecJeton();
          if (j === null) {
            setEnvoiDocument(false);
            return;
          }

          const declare = await declarerLeDocument(j, otId, {
            nom: fichier.name,
            type_id: ajoutDocument.typeId === '' ? null : ajoutDocument.typeId,
            poids: fichier.size,
          });
          if (declare.etat !== 'ok') {
            if (declare.cause === 'acces') quandAccesPerdu();
            setRefusDocument(REFUS[declare.cause]);
            setEnvoiDocument(false);
            return;
          }

          const envoye = await envoyerLeFichier(j, declare.valeur.chemin, fichier);
          if (envoye.etat !== 'ok') {
            setRefusDocument(
              envoye.cause === 'trop_gros'
                ? 'Le rangement a refusé ce fichier : il est trop gros.'
                : envoye.cause === 'refus'
                  ? 'Le rangement a refusé ce fichier. Rechargez l’écran et réessayez.'
                  : 'Le rangement ne répond pas. La pièce reste à reprendre.',
            );
            const pieces = await lireDocuments(j, otId);
            if (pieces.etat === 'ok') setDocuments(pieces.valeur);
            setEnvoiDocument(false);
            return;
          }

          await confirmerLeDocument(j, otId, declare.valeur.id);
          const pieces = await lireDocuments(j, otId);
          if (pieces.etat === 'ok') setDocuments(pieces.valeur);
          setAjoutDocument(null);
          setEnvoiDocument(false);
        })();
      }}
      quandOuvrirDocument={(document) => {
        setRefusDocument(null);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const adresse = await adresseDuFichier(j, document.chemin);
          if (adresse.etat !== 'ok') {
            setRefusDocument(
              'Ce fichier est introuvable dans le rangement. Il a pu être supprimé.',
            );
            return;
          }
          // Un onglet, et pas un aperçu dans l'écran : dit dans la maquette.
          window.open(adresse.valeur, '_blank', 'noopener');
        })();
      }}
      /**
       * SUPPRIMER : le fichier d'abord, la ligne ensuite.
       *
       * Si l'effacement du fichier échoue, rien n'a bougé et l'écran le dit.
       * Dans l'autre ordre, un fichier resterait sans ligne pour le désigner —
       * invisible, et payant.
       */
      quandSupprimerDocument={(document) => {
        if (fiche.nom !== 'prete') return;
        const otId = fiche.id;
        setEnvoiDocument(true);
        setRefusDocument(null);

        void (async () => {
          const j = await avecJeton();
          if (j === null) {
            setEnvoiDocument(false);
            return;
          }
          const efface = await effacerLeFichier(j, document.chemin);
          if (efface.etat !== 'ok') {
            setRefusDocument(
              'Le fichier n’a pas pu être effacé : rien n’a été supprimé.',
            );
            setEnvoiDocument(false);
            return;
          }
          const retire = await supprimerLeDocument(j, otId, document.id);
          if (retire.etat !== 'ok') {
            if (retire.cause === 'acces') quandAccesPerdu();
            setRefusDocument(REFUS[retire.cause]);
          }
          const pieces = await lireDocuments(j, otId);
          if (pieces.etat === 'ok') setDocuments(pieces.valeur);
          setEnvoiDocument(false);
        })();
      }}
      quandModifierOrdre={() => {
        if (fiche.nom !== 'prete') return;
        setOnglet('commande');
        setEditionOrdre(editionVide(ordreDepuis(fiche.fiche.ordre_transport)));
      }}
      quandDupliquerOrdre={() => {
        if (fiche.nom !== 'prete') return;
        // LA SOURCE SE CAPTURE AVANT, parce que `setFiche` la vide juste après
        // et que les états React ne s'appliquent pas dans l'ordre où on les
        // écrit. La lire ensuite rendrait une fiche « aucune ».
        const source = fiche.fiche;
        setVue('creation');
        setFiche({ nom: 'aucune' });
        setEditionOrdre(null);
        setEditionEtape(null);
        setCreation({
          ordre: editionVide(ordreDuplique(source.ordre_transport)),
          // Pas de `etapeVide` de secours si l'ordre n'en a aucune : on recopie
          // ce qui existe, et rien de plus. Un ordre sans étape se duplique
          // sans étape, comme il a été saisi.
          etapes: etapesDupliquees(source.etapes).map((s) => editionEtapeVide(s, null)),
        });
      }}
      quandChampOrdre={(cle, v) => {
        if (creation !== null) {
          // MISE À JOUR PAR FONCTION — lot 154 : un clic sur une icône de
          // carrosserie change DEUX champs à la suite ; avec l'état capturé,
          // le second effaçait le premier.
          setCreation((c) =>
            c === null
              ? c
              : { ...c, ordre: { ...c.ordre, saisie: { ...c.ordre.saisie, [cle]: v } } },
          );
          return;
        }
        setEditionOrdre((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, saisie: { ...actuelle.saisie, [cle]: v } },
        );
      }}
      quandEnregistrerOrdre={enregistrerOrdre}
      quandAnnulerEdition={() => setEditionOrdre(null)}
      quandModifierEtape={(id) => {
        if (fiche.nom !== 'prete') return;
        const etape = fiche.fiche.etapes.find((e) => e.id === id);
        if (etape === undefined) return;
        setEditionEtape(editionEtapeVide(etapeDepuis(etape), id));
      }}
      quandBasculerFait={basculerFait}
      affretement={affretement}
      proposes={proposes}
      tousTransporteurs={tousTransporteurs}
      chercheEnCours={chercheEnCours}
      quandAffreter={ouvrirLAffretement}
      quandNouveauTransporteur={ouvrirLeNouveauTransporteur}
      nouveauTransporteur={nouveauTransporteur}
      quandChampNouveauTransporteur={(cle, valeur) => {
        setNouveauTransporteur((courant) => {
          if (courant === null) return null;
          // La faute d'un champ disparaît dès qu'on le retouche : la garder
          // afficherait un reproche à une saisie qui n'existe plus.
          const fautes = { ...courant.fautes };
          delete fautes[cle];
          return { ...courant, saisie: { ...courant.saisie, [cle]: valeur }, fautes };
        });
      }}
      quandNomQuitteNouveauTransporteur={() => {
        setNouveauTransporteur((courant) => {
          if (courant === null) return null;
          // Le code n'est proposé QUE s'il est vide : un code tapé à la main
          // ne se fait pas écraser en quittant le champ Nom.
          if ((courant.saisie['code'] ?? '').trim() !== '') return courant;
          const propose = codePropose(courant.saisie['nom'] ?? '', tousTransporteurs);
          if (propose === '') return courant;
          return { ...courant, saisie: { ...courant.saisie, code: propose } };
        });
      }}
      quandEnregistrerNouveauTransporteur={enregistrerLeNouveauTransporteur}
      quandAnnulerNouveauTransporteur={() => {
        setNouveauTransporteur(null);
        setAction((a) => ({ ...a, refus: null }));
      }}
      quandNouveauClient={ouvrirLeNouveauClient}
      nouveauClient={nouveauClient}
      quandChampNouveauClient={(cle, valeur) => {
        setNouveauClient((courant) => {
          if (courant === null) return null;
          // La faute d'un champ disparaît dès qu'on le retouche : la garder
          // afficherait un reproche à une saisie qui n'existe plus.
          const fautes = { ...courant.fautes };
          delete fautes[cle];
          return { ...courant, saisie: { ...courant.saisie, [cle]: valeur }, fautes };
        });
      }}
      quandNomQuitteNouveauClient={() => {
        setNouveauClient((courant) => {
          if (courant === null) return null;
          // Le code n'est proposé QUE s'il est vide : un code tapé à la main ne
          // se fait pas écraser en quittant le champ Nom.
          if ((courant.saisie['code'] ?? '').trim() !== '') return courant;
          const propose = codePropose(courant.saisie['nom'] ?? '', clients);
          if (propose === '') return courant;
          return { ...courant, saisie: { ...courant.saisie, code: propose } };
        });
      }}
      quandEnregistrerNouveauClient={enregistrerLeNouveauClient}
      quandAnnulerNouveauClient={() => {
        setNouveauClient(null);
        setAction((a) => ({ ...a, refus: null }));
      }}
      quandChampAffretement={(cle, valeur) => {
        setAffretement((a) => (a === null ? null : { ...a, [cle]: valeur }));
      }}
      quandEnregistrerAffretement={enregistrerLAffretement}
      quandAnnulerAffretement={annulerLeAffretement}
      echange={echange}
      quandEchange={(id) => {
        if (fiche.nom !== 'prete') return;
        const etape = fiche.fiche.etapes.find((e) => e.id === id);
        if (etape === undefined) return;
        // Une seule boîte à la fois : ouvrir l'échange referme les deux
        // autres, comme valider referme le litige.
        setValidation(null);
        setLitige(null);
        setAction((a) => ({ ...a, refus: null }));
        setEchange({ etapeId: id, saisie: echangeDepuis(etape) });
      }}
      quandChampEchange={(cle, valeur) => {
        setEchange((a) =>
          a === null ? null : { ...a, saisie: { ...a.saisie, [cle]: valeur } },
        );
      }}
      quandEnregistrerEchange={() => {
        enregistrerLEchange(false);
      }}
      quandEffacerEchange={() => {
        enregistrerLEchange(true);
      }}
      resolution={resolution}
      quandResoudre={(litigeId) => {
        setValidation(null);
        setLitige(null);
        setAction((a) => ({ ...a, refus: null }));
        setResolution({
          litigeId,
          texte: '',
          jour: aujourdhui(),
          heure: maintenant(),
        });
      }}
      quandChampResolution={(cle, v) =>
        setResolution((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, [cle]: v },
        )
      }
      quandEnregistrerResolution={enregistrerLaResolution}
      quandLitige={(id) => {
        if (fiche.nom !== 'prete') return;
        setValidation(null);
        setAction((a) => ({ ...a, refus: null }));
        // La boîte propose le premier motif actif s'il y en a un, et le
        // formulaire de CRÉATION d'un motif s'il n'y en a aucun : sans cela,
        // le premier litige de la première société était impossible à ouvrir.
        const premier = motifs.find((m) => m.actif);
        setLitige({
          etapeId: id,
          motifId: premier?.id ?? '',
          motifNouveau: premier === undefined ? '' : null,
          note: '',
          jour: aujourdhui(),
          heure: maintenant(),
        });
      }}
      validation={validation}
      litige={litige}
      tempsPasse={tempsPasse}
      quandTemps={setTempsPasse}
      cout={cout}
      prestations={prestations}
      naturesPrestation={naturesPrestation}
      saisiePrestation={saisiePrestation}
      quandOuvrirPrestation={ouvrirLaPrestation}
      quandRetirerPrestation={retirerLaPrestation}
      quandChampPrestation={champDeLaPrestation}
      quandEnregistrerPrestation={enregistrerLaPrestation}
      quandRecalculerLeCout={() => {
        if (fiche.nom !== 'prete') return;
        const otId = fiche.id;
        setAction((a) => ({ ...a, envoi: true, refus: null }));
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await recalculerLeCout(j, otId);
          if (fait.etat !== 'ok' && fait.cause === 'acces') quandAccesPerdu();
          const relu = await lireLeCout(j, otId);
          if (relu.etat === 'ok') setCout(relu.valeur);
          finirLEcriture(fait);
        })();
      }}
      quandChampValidation={(cle, v) =>
        setValidation((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, [cle]: v },
        )
      }
      quandFermerBoite={() => {
        setSaisiePrestation(null);
        setValidation(null);
        setLitige(null);
        setResolution(null);
        setEchange(null);
        setAffretement(null);
        setAction((a) => ({ ...a, refus: null }));
      }}
      quandConfirmerValidation={confirmerValidation}
      quandChampLitige={(cle, v) =>
        setLitige((actuel) => (actuel === null ? actuel : { ...actuel, [cle]: v }))
      }
      quandNouveauMotif={(nom) =>
        setLitige((actuel) =>
          actuel === null ? actuel : { ...actuel, motifNouveau: nom },
        )
      }
      quandOuvrirLitige={ouvrirLeLitige}
      quandDeplacerEtape={deplacerEtape}
      quandAjouterEtape={(type) => {
        if (fiche.nom !== 'prete') return;
        // Le jour de la dernière étape, ou aujourd'hui : une étape ajoutée
        // tombe presque toujours le même jour que la précédente.
        const derniere = fiche.fiche.etapes[fiche.fiche.etapes.length - 1];
        setEditionEtape(
          editionEtapeVide(etapeVide(type, derniere?.jour ?? aujourdhui()), null),
        );
      }}
      quandChampEtape={(cle, v) => {
        setEditionEtape((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, saisie: { ...actuelle.saisie, [cle]: v } },
        );
      }}
      quandSiteEtape={(id) => {
        setEditionEtape((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, saisie: { ...actuelle.saisie, ...depuisSite(id) } },
        );
        if (id === '') return;
        void adresseDuSite(id, (adresse) => {
          setEditionEtape((actuelle) =>
            actuelle === null || actuelle.saisie.site_id !== id
              ? actuelle
              : { ...actuelle, saisie: { ...actuelle.saisie, site_adresse: adresse } },
          );
        });
      }}
      quandOuvrirSite={() =>
        setEditionEtape((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, site: SITE_VIDE },
        )
      }
      quandChampSite={(cle, v) =>
        setEditionEtape((actuelle) =>
          actuelle === null || actuelle.site === null
            ? actuelle
            : { ...actuelle, site: { ...actuelle.site, [cle]: v } },
        )
      }
      quandCreerSite={() => {
        if (editionEtape === null) return;
        creerLeSite(editionEtape, setEditionEtape);
      }}
      quandFermerSite={() =>
        setEditionEtape((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, site: null, fautesSite: {} },
        )
      }
      quandEnregistrerEtape={enregistrerEtape}
      quandAnnulerEtape={() => setEditionEtape(null)}
      quandDemanderSuppressionEtape={() =>
        setEditionEtape((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, confirme: true },
        )
      }
      quandSupprimerEtape={supprimerLEtape}
      quandCreer={() => {
        const jour = aujourdhui();
        setVue('creation');
        setFiche({ nom: 'aucune' });
        setEditionOrdre(null);
        setEditionEtape(null);
        // LES DEUX ÉTAPES SONT DÉJÀ POSÉES — un chargement et une livraison.
        // Ce n'est pas une supposition : 87 des 94 missions de la base v1 en
        // ont exactement deux. Celui qui n'en veut qu'une la retire.
        setCreation({
          ordre: editionVide(ORDRE_VIDE),
          etapes: [
            editionEtapeVide(etapeVide('chargement', jour), null),
            editionEtapeVide(etapeVide('livraison', jour), null),
          ],
        });
      }}
      quandChampEtapeCreation={(rang, cle, v) => {
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          // L'ÉTAPE TELLE QU'ELLE EST AVANT CETTE FRAPPE. Elle sert au report
          // plus bas ; sans elle, la livraison garderait le premier chiffre
          // tapé sur le chargement (défaut du 07/09/2026).
          const avantLaFrappe = actuelle.etapes[rang];
          if (avantLaFrappe === undefined) return actuelle;
          const etapes = actuelle.etapes.map((e, i) =>
            i === rang ? { ...e, saisie: { ...e.saisie, [cle]: v } } : e,
          );
          // Ce qu'on saisit sur un CHARGEMENT se pose sur les livraisons qui
          // n'ont rien — demandé le 04/09/2026. `reporterSurLesLivraisons`
          // n'écrase jamais une valeur déjà saisie.
          if (cle !== 'quantite' && cle !== 'support' && cle !== 'poids') {
            return { ...actuelle, etapes };
          }
          // LA VALEUR D'AVANT LA FRAPPE est passée au report, et c'est ce qui
          // corrige le défaut du 07/09/2026 : sans elle, la livraison gardait
          // le premier chiffre tapé sur le chargement. Voir
          // `suitLeChargement` dans `ot.ts` pour le détail du mécanisme.
          const reportees = reporterSurLesLivraisons(
            etapes.map((e) => e.saisie),
            rang,
            avantLaFrappe.saisie,
          );
          return {
            ...actuelle,
            etapes: etapes.map((e, i) => ({ ...e, saisie: reportees[i] ?? e.saisie })),
          };
        });
      }}
      quandSiteCreation={(rang, id) => {
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                etapes: actuelle.etapes.map((e, i) =>
                  i === rang ? { ...e, saisie: { ...e.saisie, ...depuisSite(id) } } : e,
                ),
              },
        );
        if (id === '') return;
        void adresseDuSite(id, (adresse) => {
          setCreation((actuelle) =>
            actuelle === null
              ? actuelle
              : {
                  ...actuelle,
                  etapes: actuelle.etapes.map((e, i) =>
                    i === rang && e.saisie.site_id === id
                      ? { ...e, saisie: { ...e.saisie, site_adresse: adresse } }
                      : e,
                  ),
                },
          );
        });
      }}
      quandOuvrirSiteCreation={(rang) => {
        // PRÉ-REMPLI AVEC CE QUE L'ÉTAPE PORTE DÉJÀ — F3. Après la lecture
        // d'une confirmation, le lieu lu (« LINDT & SPRUNGLI, 28140 POUPRY »)
        // est là mais inconnu de l'annuaire : le recopier évite de le retaper.
        const e = creation?.etapes[rang]?.saisie;
        majEtapeCreation(rang, {
          site:
            e === undefined
              ? SITE_VIDE
              : {
                  ...SITE_VIDE,
                  nom: e.site_nom,
                  adresse: e.site_adresse,
                  code_postal: e.site_code_postal,
                  ville: e.site_ville,
                },
        });
      }}
      quandChampSiteCreation={(rang, cle, v) => {
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                etapes: actuelle.etapes.map((e, i) =>
                  i === rang && e.site !== null
                    ? { ...e, site: { ...e.site, [cle]: v } }
                    : e,
                ),
              },
        );
      }}
      quandCreerSiteCreation={(rang) => {
        const edition = creation?.etapes[rang];
        if (edition === undefined) return;
        creerLeSite(edition, (e) => majEtapeCreation(rang, e));
      }}
      quandFermerSiteCreation={(rang) =>
        majEtapeCreation(rang, { site: null, fautesSite: {} })
      }
      quandAjouterEtapeCreation={(type) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                etapes: [
                  ...actuelle.etapes,
                  // Le jour de la dernière étape saisie : une étape
                  // ajoutée tombe presque toujours le même jour que la
                  // précédente.
                  editionEtapeVide(
                    {
                      ...etapeVide(
                        type,
                        actuelle.etapes[actuelle.etapes.length - 1]?.saisie.jour ??
                          aujourdhui(),
                      ),
                      // SANS RELAIS, la nouvelle étape prend le véhicule
                      // commun — F3 : il est porté sur toutes les étapes.
                      vehicule_id:
                        actuelle.relais === true
                          ? ''
                          : (actuelle.etapes[0]?.saisie.vehicule_id ?? ''),
                    },
                    null,
                  ),
                ],
              },
        )
      }
      quandRetirerEtapeCreation={(rang) =>
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          const etapes = actuelle.etapes.filter((_, i) => i !== rang);
          const avant = actuelle.etapes.map((_, i) => i).filter((i) => i !== rang);
          const marchandises = lignesApres(
            actuelle.marchandises,
            avant,
            etapes.map((e) => e.saisie.type),
          );
          return marchandises === undefined
            ? { ...actuelle, etapes }
            : { ...actuelle, etapes, marchandises };
        })
      }
      quandDeplacerEtapeCreation={(rang, pas) =>
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          const vers = rang + pas;
          if (vers < 0 || vers >= actuelle.etapes.length) return actuelle;
          const etapes = [...actuelle.etapes];
          const [deplacee] = etapes.splice(rang, 1);
          if (deplacee === undefined) return actuelle;
          etapes.splice(vers, 0, deplacee);
          const avant = actuelle.etapes.map((_, i) => i);
          const [r] = avant.splice(rang, 1);
          avant.splice(vers, 0, r ?? rang);
          const marchandises = lignesApres(
            actuelle.marchandises,
            avant,
            etapes.map((e) => e.saisie.type),
          );
          return marchandises === undefined
            ? { ...actuelle, etapes }
            : { ...actuelle, etapes, marchandises };
        })
      }
      quandEnregistrerCreation={enregistrerCreation}
      indexationCreation={indexationCreation}
      editionPackaging={editionPackaging}
      quandModifierPackaging={modifierLePackaging}
      quandAjouterPackaging={() =>
        setEditionPackaging((actuelles) =>
          actuelles === null || fiche.nom !== 'prete'
            ? actuelles
            : [...actuelles, ligneMarchandiseVide(fiche.fiche.etapes.map((e) => e.type))],
        )
      }
      quandChampPackaging={(rang, changement) =>
        setEditionPackaging((actuelles) =>
          actuelles === null
            ? actuelles
            : actuelles.map((l, i) => (i === rang ? { ...l, ...changement } : l)),
        )
      }
      quandSupportPackaging={(rang, nom) =>
        setEditionPackaging((actuelles) =>
          actuelles === null
            ? actuelles
            : actuelles.map((l, i) => (i === rang ? choisirLeSupport(l, nom, supports) : l)),
        )
      }
      quandRetirerPackaging={(rang) =>
        setEditionPackaging((actuelles) =>
          actuelles === null ? actuelles : actuelles.filter((_, i) => i !== rang),
        )
      }
      quandEnregistrerPackaging={enregistrerLePackaging}
      quandAnnulerPackaging={() => setEditionPackaging(null)}
      quandAjouterMarchandiseCreation={() =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                marchandises: [
                  ...lignesDe(actuelle, supports),
                  ligneMarchandiseVide(actuelle.etapes.map((e) => e.saisie.type)),
                ],
              },
        )
      }
      quandChampMarchandiseCreation={(rang, changement) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                marchandises: lignesDe(actuelle, supports).map((l, i) =>
                  i === rang ? { ...l, ...changement } : l,
                ),
              },
        )
      }
      quandSupportMarchandiseCreation={(rang, nom) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                marchandises: lignesDe(actuelle, supports).map((l, i) =>
                  i === rang ? choisirLeSupport(l, nom, supports) : l,
                ),
              },
        )
      }
      quandRetirerMarchandiseCreation={(rang) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                marchandises: lignesDe(actuelle, supports).filter((_, i) => i !== rang),
              },
        )
      }
      quandAjouterDocumentCreation={(ajout) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, documents: [...(actuelle.documents ?? []), ajout] },
        )
      }
      quandRetirerDocumentCreation={(rang) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                documents: (actuelle.documents ?? []).filter((_, i) => i !== rang),
              },
        )
      }
      quandAffreterCreation={(oui) =>
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          // AFFRÉTER RETIRE LE CAMION : le transport part chez un
          // sous-traitant, aucun véhicule du parc n'est posé.
          return {
            ...actuelle,
            affreter: oui,
            etapes: oui
              ? actuelle.etapes.map((e) => ({
                  ...e,
                  saisie: { ...e.saisie, vehicule_id: '', chauffeur_id: '', remorque_id: '' },
                }))
              : actuelle.etapes,
          };
        })
      }
      quandAjouterPrestationCreation={() => {
        const premiere = naturesPrestation.find((n) => n.actif);
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                prestations: [
                  ...(actuelle.prestations ?? []),
                  {
                    natureId: premiere?.id ?? '',
                    detail: '',
                    quantite: '1',
                    prixUnitaire: pourLaSaisie(premiere?.prix_defaut),
                  },
                ],
              },
        );
      }}
      quandChampPrestationCreation={(rang, cle, valeur) =>
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          const lignes = (actuelle.prestations ?? []).map((l, i) => {
            if (i !== rang) return l;
            if (cle !== 'natureId') return { ...l, [cle]: valeur };
            // MÊME RÈGLE QUE LA BOÎTE « Prestation annexe » : changer de
            // nature remplace le prix tant qu'on ne l'a pas touché soi-même.
            const avant = naturesPrestation.find((n) => n.id === l.natureId);
            const apres = naturesPrestation.find((n) => n.id === valeur);
            const prixIntact =
              l.prixUnitaire === '' || l.prixUnitaire === pourLaSaisie(avant?.prix_defaut);
            return {
              ...l,
              natureId: valeur,
              prixUnitaire: prixIntact ? pourLaSaisie(apres?.prix_defaut) : l.prixUnitaire,
            };
          });
          return { ...actuelle, prestations: lignes };
        })
      }
      quandRetirerPrestationCreation={(rang) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                prestations: (actuelle.prestations ?? []).filter((_, i) => i !== rang),
              },
        )
      }
      quandRelaisCreation={(actif) =>
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          if (actif) return { ...actuelle, relais: true };
          // SANS RELAIS, UN SEUL VÉHICULE : celui de la première étape est
          // reporté sur toutes, chauffeur et remorque laissés à l'attelage
          // du jour que l'API recopie.
          const premier = actuelle.etapes[0]?.saisie.vehicule_id ?? '';
          return {
            ...actuelle,
            relais: false,
            etapes: actuelle.etapes.map((e) => ({
              ...e,
              saisie: { ...e.saisie, vehicule_id: premier, chauffeur_id: '', remorque_id: '' },
            })),
          };
        })
      }
      lecture={lecture}
      quandLireConfirmation={lireUneConfirmation}
      quandOublierLecture={() => setLecture({ quoi: 'repos' })}
      ongletListe={ongletListe}
      aCreer={aCreer}
      quandOngletListe={setOngletListe}
      quandDeposer={deposer}
      quandVerifier={verifier}
      quandEcarter={ecarter}
      quandRelire={relireUne}
    />
  );
}
